import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { COACHING_INSIGHTS, extractSlots } from './config/insights';
// Simplified: removed schema/engine/recipe routing
import { extractConversationContext, detectMissingData, getSmartQuestion, getAnalysisType } from './utils/conversation';
import { extractCoachFrameworks } from './utils/frameworks';
import { generateToolResults } from './utils/tools';
import { createInsightRegistry } from './utils/insightRegistry';
// Simplified: removed missing prompts system
// Load Vapi SDK dynamically (CDN ESM) to mirror the working test page
let vapi = null;
import { api, API_BASE, setWidgetPublicKey, widgetKeyHeaders } from './config/api';
import { VERTICALS, getVerticalFromHostname, pickVerticalConfig, isDanMartell, getVerticalOverrides } from './config/verticals';
import { COACHING_TYPES, getCoachingConfig } from './config/coachingTypes';

// API V1 base for direct endpoint calls
const API_V1_BASE = `${API_BASE}/api/v1`;
import { apiService } from './services/api';
import { getPreset } from './config/presets';
import { resolveAudience } from './config/audience';
import './styles/tokens.css'; // Import tokens FIRST for CSS variable definitions
import './styles/widget.css';
import './styles/animations.css';
import './styles/feedback.css';
import './styles/calculators.css';
import './styles/compact-ux.css';
import './styles/mentormind-apple-premium.css'; // Apple-Premium Design System - Mobile-First, Glass Morphism, Voice-First
import './styles/mentormind-premium-redesign.css'; // Premium Redesign - Clean, Apple-inspired
import './styles/opsiq-premium.css'; // OpsIQ Premium Design System - Professional Blue Theme, ChatGPT-like Experience

// Import new components
import ErrorBoundary from './components/ErrorBoundary';
import SmartContextCards from './components/SmartContextCards';
import LoadingState, { LoadingOverlay, InlineLoader } from './components/LoadingState';
import EmptyState, { EmptyConversation, EmptyInsights, EmptyDocuments, EmptyHistory } from './components/EmptyState';
import MobileNavigation from './components/MobileNavigation';
import VoiceControls from './components/VoiceControls';
import LazyComponent, { LazyContent } from './components/LazyComponent';
import FeedbackRating from './components/FeedbackRating';
import HVACCalculators from './components/HVACCalculators';
import ToolsPanel from './components/ToolsPanel';
import FrameworkCompletionModal from './components/FrameworkCompletionModal';
import FrameworkProgressBadge from './components/FrameworkProgressBadge';
import FrameworkProgress from './components/FrameworkProgress';
import FrameworkResult from './components/FrameworkResult';
import FrameworkSummaryCard from './components/FrameworkSummaryCard';
import SpreadsheetPanel from './components/SpreadsheetPanel';
// New MentorMind views - Premium Redesign
import HomeViewPremium from './views/HomeViewPremium';
import SessionsViewPremium from './views/SessionsViewPremium';
import ProgressViewPremium from './views/ProgressViewPremium';
import InsightsView from './views/InsightsView';
import FrameworkPickerModal from './components/FrameworkPickerModal';
import InlineActions from './components/InlineActions';

// Import utils
import { sanitizeAIOutput, sanitizeToolData } from './utils/outputSanitizer';
import {
  BarChart3,
  Check,
  CheckSquare,
  LineChart,
  Lock,
  MessageSquare,
  Play,
  RotateCcw,
  Search,
  Scale,
  Target,
  Trophy,
  Zap,
  Wrench,
  Copy,
  Download,
  Trash2
} from 'lucide-react';

// Import hooks
import { useKeyboardNavigation } from './hooks/useKeyboardNavigation';
import { useDebounce, useThrottle } from './hooks/useLazyLoading';
import { useMobileViewport, useMobilePerformance, useMobileKeyboard } from './hooks/useMobileGestures';
import useToolsFeed from './hooks/useToolsFeed';
import { getDerivedContextKeys } from './utils/context';
import { executeTool } from './utils/toolExecutors';
import ToolResultRenderer from './components/ToolResultRenderer';
// Mobile optimization hooks and components
import { useOfflineManager } from './hooks/useOfflineManager';
import { useSwipeGestures } from './hooks/useSwipeGestures';
import NetworkStatus from './components/NetworkStatus';
import QuickActions from './components/QuickActions';
import MobileModePicker from './components/MobileModePicker';
import MobileSessionsDrawer from './components/MobileSessionsDrawer';
import FloatingVoiceButton from './components/FloatingVoiceButton';
import MenuDrawer from './components/MenuDrawer';
import SettingsMenu from './components/SettingsMenu';
import StreamingMessage from './components/StreamingMessage';
// Session management
import * as sessionManager from './utils/sessionManager';
import { listOpsSessions, saveOpsSession } from './utils/opsSessionManager';



// Smart context extraction from conversation
const SESSION_KEY = 'ov_session_id';
const getSessionId = () => {
  try {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = (crypto && crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random()}`;
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch (e) {
    return `${Date.now()}-${Math.random()}`;
  }
};

// Debounce utility for performance optimization
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Simple insights engine functions
const engineParse = (text, schema) => {
  try {
    const slots = extractSlots(text);
    return { updates: slots };
  } catch (e) {
    return { updates: {} };
  }
};

// Insight schemas for different coaching types
const INSIGHT_SCHEMAS = {
  business: ['revenue', 'team_size', 'hours_per_week', 'challenge'],
  mentormind: ['revenue', 'team_size', 'hours_per_week', 'pain_point', 'business_stage'],
  health: ['current_weight', 'goal_weight', 'fitness_goals'],
  ecommerce: ['conversion_rate', 'revenue', 'sku_count'],
  sales: ['close_rate', 'pipeline_value', 'deal_size'],
  financial: ['debt', 'budget', 'savings_rate'],
  leadership: ['team_size', 'decision_fatigue', 'delegation'],
  relationship: ['relationship_conflict', 'communication_issues'],
  life: ['life_balance', 'stress_levels', 'goals'],
  career: ['current_role', 'target_role', 'skill_gaps']
};

const engineMerge = (current, updates) => {
  return { ...current, ...updates };
};

const engineNext = (context, schema, count = 2) => {
  const missing = detectMissingData(context);
  return missing.slice(0, count);
};

const engineMatch = (text, schema) => {
  try {
    const slots = extractSlots(text);
    const triggers = [];
    
    // Business triggers
    if (slots.challenge) triggers.push('Bottleneck Analysis');
    
    // Health triggers
    if (slots.currentWeight && slots.goalWeight) triggers.push('Program Design');
    
    // Sales triggers
    if (slots.sales) triggers.push('Pipeline Review');
    
    return triggers;
  } catch (e) {
    return [];
  }
};
// replaced by COACHING_TYPES config
// moved to utils/conversation.js
// moved to ./config/insights.js

// Map Supabase coaching specialization labels to widget types
const mapCoachingSpecialization = (label = '') => {
  const raw = Array.isArray(label) ? (label[0] || '') : (label || '');
  const s = String(raw).toLowerCase();
  if (s.includes('e-commerce') || s.includes('amazon')) return 'ecommerce';
  if (s.includes('fitness') || s.includes('health') || s.includes('wellness')) return 'health';
  if (s.includes('life')) return 'life';
  if (s.includes('career')) return 'career';
  if (s.includes('relationship')) return 'relationship';
  if (s.includes('financial')) return 'financial';
  if (s.includes('leadership') || s.includes('executive')) return 'leadership';
  if (s.includes('sales')) return 'sales';
  if (s.includes('team') || s.includes('group')) return 'leadership';
  return 'business';
};

// Default assistant IDs per vertical (used if none provided via URL)
const DEFAULT_ASSISTANT_IDS = {
  mentormind: '84c22938-4441-4837-8aae-b5ba475df221', // New MentorMind trial assistant
  hvac: '9697287f-280b-4f4e-a5d5-f5c8d415dc92',
  business: '6ffb8943-f659-4d89-8dce-153385be3b95',
  opsiq: '6ffb8943-f659-4d89-8dce-153385be3b95', // OpsIQ uses same assistant as business
  pai3: '6ffb8943-f659-4d89-8dce-153385be3b95' // PAI3 uses same assistant as business/OpsIQ
};

// Map frontend company IDs to backend IDs when they differ (with strict debug)
const getBackendCompanyId = (frontendId) => {
  if (frontendId === 'coach-dan-martell-demo') {
    return 'coach-dan-martell-demo-default';
  }
  // Return empty string if no valid company ID - do not default to demo account
  const actualId = typeof frontendId === 'string' ? frontendId.trim() : '';
  return actualId;
};

// Error Boundary Component
// Error Boundary is now imported from components

// Utility Functions
const getUrlParams = () => {
  const params = new URLSearchParams(window.location.search);
  const hash = window.location.hash.replace('#', '');
  
  // URL parameters extracted
  
  // Try multiple ways to get company ID
  let companyIdSlug = params.get('company_id_slug') || params.get('company_id') || '';
  
  // Fallback: try to extract from hash or other sources
  if (!companyIdSlug) {
    // Check if it's in the hash
    const hashParams = new URLSearchParams(hash);
    companyIdSlug = hashParams.get('company_id_slug') || hashParams.get('company_id') || '';

    // No default fallback - require explicit company_id_slug for all widgets
    // This ensures proper data isolation across all accounts
  }
  
  // Extract client_id for client-specific widget links
  const clientId = params.get('client_id') || params.get('clientId') || '';
  
  // Extract email for team member tracking
  const userEmail = params.get('email') || params.get('user_email') || '';
  
  // Detect vertical from domain or company slug
  let detectedVertical = params.get('vertical') || detectVerticalFromDomain();
  
  // Override vertical based on company slug for known MentorMind companies
  if (companyIdSlug && (
    companyIdSlug.includes('mentormind') ||
    companyIdSlug === 'founder-coaching-demo' ||
    companyIdSlug === 'coach-dan-martell-demo' ||
    companyIdSlug === 'coach-dan-martell-demo-default' ||
    companyIdSlug === 'demo-mentormind-company'
  )) {
    detectedVertical = 'mentormind';
  }
  
  return {
    vertical: detectedVertical,
    companyIdSlug: companyIdSlug,
    clientId: clientId, // NEW: Client ID for client-specific context
    userEmail: userEmail, // NEW: Email for team member tracking
    assistantId: params.get('assistant_id') || '',
    hash: hash || 'voice'
  };
};

const detectVerticalFromDomain = () => {
  // Delegate to the single source of truth in config/verticals.js
  // This ensures consistent defaults across all code paths.
  if (typeof window === 'undefined') return 'business';
  return getVerticalFromHostname(window.location.hostname);
};

const getCompanyConfig = (vertical, companyIdSlug) => {
  // Dan Martell's custom MentorMind configuration
  if (companyIdSlug === 'coach-dan-martell-demo' || companyIdSlug === 'coach-dan-martell-demo-default') {
    return {
      title: "Premium Business Coach",
      subtitle: "Scale Your Business • Delegate Like a Pro",
      coachName: "with AI Coach",
      logo: "/mentormind.svg",
      theme: "mentormind",
      features: { coaching: true, metrics: true, roi: true, health: true, action: true, vapi: true },
      tools: [
        {
          id: '1-3-1-framework',
          name: 'Decision Framework 1-3-1',
          icon: '🎯',
          description: 'Make better decisions with proven framework'
        },
        {
          id: 'audit-transfer-fill',
          name: 'Audit-Transfer-Fill Loop',
          icon: '🔄',
          description: 'Implement perpetual delegation cycle'
        },
        {
          id: 'camcorder-method',
          name: 'Process Recording Method',
          icon: '📹',
          description: 'Document processes for team training'
        },
        {
          id: 'pain-line-analysis',
          name: 'Constraint Finder',
          icon: '💡',
          description: 'Identify and solve business bottlenecks'
        }
      ]
    };
  }

  // Founder coaching demo configuration
  if (companyIdSlug === 'founder-coaching-demo') {
    return {
      title: "Founder Coaching Companion",
      subtitle: "Founder Coaching Demo",
      coachName: "",
      logo: "/mentormind.svg",
      theme: "mentormind",
      features: { coaching: true, metrics: true, vapi: true, health: true },
      tools: [
        {
          id: 'founder-stress-load-index',
          name: 'Founder Stress Load Index',
          icon: '📊',
          description: 'Quickly self-assess overwhelm and stress levels.'
        },
        {
          id: 'decision-clarity-matrix',
          name: 'Decision Clarity Matrix',
          icon: '🎯',
          description: 'Evaluate what to prioritize this week using urgency and impact.'
        }
      ]
    };
  }

  // Generic MentorMind demo for all coaches
  if (companyIdSlug === 'demo-mentormind-company') {
    return {
      title: "MentorMind",
      subtitle: "Your AI Coaching Assistant",
      coachName: "",
      logo: "/mentormind.svg",
      theme: "mentormind",
      features: { coaching: true, metrics: true, vapi: true }
    };
  }

  // Vertical-specific defaults
  const configs = {
    hvac: {
      title: "HVAC Expert",
      subtitle: "Your AI HVAC Assistant",
      coachName: "",
      logo: "/opsvoice-hvac.svg",
      theme: "hvac",
      features: { emergency: true, safety: true, vapi: true }
    },
    business: {
      title: "Your Business AI Companion",
      subtitle: "Instant answers from your company knowledge",
      coachName: "",
      logo: "/logos/opsiq-logo.png",
      theme: "business",
      features: { analytics: true, reports: true, vapi: true }
    },
    mentormind: {
      title: "MentorMind",
      subtitle: "Your AI Coaching Assistant",
      coachName: "",
      logo: "/mentormind.svg",
      theme: "mentormind",
      features: { coaching: true, metrics: true, vapi: true }
    },
    opsiq: {
      title: "OpsIQ Assistant",
      subtitle: "Your company knowledge. Instant answers.",
      coachName: "",
      logo: "/logos/opsiq-logo.png",
      theme: "business",
      features: { analytics: true, vapi: true }
    },
    pai3: {
      title: "PAI3 Operations Assistant",
      subtitle: "Your AI operations assistant",
      coachName: "",
      logo: "/logos/pai3-logo.png",
      theme: "pai3",
      features: { analytics: true, vapi: true, spreadsheets: true }
    }
  };

  return configs[vertical] || configs.business;
};

// Enterprise Theme Configuration
const getEnterpriseThemeConfig = (vertical, companyIdSlug) => {
  const themes = {
    mentormind: {
      danMartell: {
        title: "Premium Business Coach",
        subtitle: "Scale Your Business • Delegate Like a Pro",
        coachName: "with AI Coach",
        logo: "/mentormind.svg",
        theme: "mentormind",
        disclaimer: "🤖 AI coaching assistant - suggestions are guidance only, verify before implementing",
        features: {
          advanced: true,
          roi: true,
          health: true,
          action: true
        }
      },
      generic: {
        title: "MentorMind",
        subtitle: "Your AI Coaching Assistant",
        coachName: "",
        logo: "/mentormind.svg",
        theme: "mentormind",
        disclaimer: "🤖 AI coaching assistant • Provides strategic guidance based on proven frameworks • Not a substitute for professional coaching",
        features: { coaching: true, assessment: true, planning: true }
      },
      founderCoaching: {
        title: "Founder Coaching Companion",
        subtitle: "Founder Coaching Demo",
        coachName: "",
        logo: "/mentormind.svg",
        theme: "mentormind",
        disclaimer: "🤖 Emotional-capacity support tool • Supports founders between coaching sessions • Not a crisis or therapy service.",
        features: { coaching: true, assessment: true, planning: true, health: true }
      }
    },
    hvac: {
      title: "HVAC Expert",
      subtitle: "Your AI HVAC Assistant",
      coachName: "",
      logo: "/opsvoice-hvac.svg",
      theme: "hvac",
      disclaimer: "⚠️ HVAC operations assistant • Always follow safety protocols • Not a substitute for certified technician training • Consult supervisor for complex procedures",
      features: { safety: true, diagnostics: true, compliance: true, emergency: true }
    },
    business: {
      title: "Your AI Assistant",
      subtitle: "Get instant answers from your documents",
      coachName: "",
      logo: "/logos/opsiq-logo.png",
      theme: "business",
      disclaimer: "🤖 AI business assistant • Provides instant answers from your documents",
      features: { operations: true, team: true, workflow: true }
    },
    opsiq: {
      title: "OpsIQ Assistant",
      subtitle: "Your company knowledge. Instant answers.",
      coachName: "",
      logo: "/logos/opsiq-logo.png",
      theme: "business",
      disclaimer: "🤖 AI business assistant • Provides instant answers from your documents • Not a substitute for expert consultation",
      features: { operations: true, team: true, workflow: true }
    }
  };

  if (vertical === 'mentormind') {
    if (companyIdSlug === 'coach-dan-martell-demo' || companyIdSlug === 'coach-dan-martell-demo-default') {
      return themes.mentormind.danMartell;
    }
    if (companyIdSlug === 'founder-coaching-demo') {
      return themes.mentormind.founderCoaching;
    }
    return themes.mentormind.generic;
  }
  
  return themes[vertical] || themes.business;
};

// Enterprise Insights by Vertical
const getVerticalInsights = (vertical, companyIdSlug) => {
  const isDanMartell = companyIdSlug === 'coach-dan-martell-demo' || companyIdSlug === 'coach-dan-martell-demo-default';
  const isFounderCoaching = companyIdSlug === 'founder-coaching-demo';

  const insights = {
    // Business Growth Frameworks (Premium version for demo)
    mentormind: isDanMartell ? {
      tools: [
        { id: '1-3-1-framework', name: 'Decision Framework 1-3-1', icon: '🎯', description: 'Make better decisions with proven framework' },
        { id: 'audit-transfer-fill', name: 'Audit-Transfer-Fill Loop', icon: '🔄', description: 'Implement perpetual delegation cycle' },
        { id: 'camcorder-method', name: 'Process Recording Method', icon: '📹', description: 'Document processes for team training' },
        { id: 'pain-line-analysis', name: 'Constraint Finder', icon: '📊', description: 'Identify and solve business bottlenecks' }
      ]
    } : isFounderCoaching ? {
      tools: [
        { id: 'reflection', name: 'Reflection Mode', icon: '🧠', description: 'Journaling support for founder emotional capacity.' },
        { id: 'regulation', name: 'Regulation Toolkit', icon: '🌿', description: 'Nervous-system-aware grounding and reset prompts.' },
        { id: 'boundaries', name: 'Values & Boundaries', icon: '🧭', description: 'Support to translate your values into clear limits.' }
      ],
      prompts: [
        "Help me process something emotionally heavy from this week.",
        "I need help preparing for a hard conversation as a founder.",
        "Help me check in with my values and boundaries around this situation."
      ]
    } : {
      tools: [
        { id: 'assessment', name: 'Business Assessment', icon: '📋', description: 'Evaluate current business stage' },
        { id: 'planning', name: 'Growth Planning', icon: '📈', description: 'Strategic growth recommendations' },
        { id: 'scaling', name: 'Scaling Tools', icon: '⚡', description: 'Scale operations efficiently' }
      ]
    },

    // Business Coaching
    business: {
      tools: [
        { id: 'growth', name: 'Growth Planning', icon: '📈', description: 'Quarterly priorities and roadmap' },
        { id: 'bottleneck', name: 'Bottleneck Analysis', icon: '🔍', description: 'Identify the biggest constraint' }
      ]
    },

    // Health & Fitness Coaching
    health: {
      tools: [
        { id: 'program', name: 'Program Design', icon: '💪', description: 'Build 12-week coaching plan' },
        { id: 'nutrition', name: 'Nutrition Planner', icon: '🥗', description: 'Client-specific guidelines' },
        { id: 'habits', name: 'Habit Builder', icon: '🔄', description: 'Habit loops and accountability' }
      ]
    },

    // E-Commerce Coaching
    ecommerce: {
      tools: [
        { id: 'sku', name: 'SKU Profitability', icon: '📊', description: 'Analyze top/worst SKUs' },
        { id: 'ppc', name: 'PPC Optimizer', icon: '🎯', description: 'Surface wasted ad spend' },
        { id: 'ops', name: 'Ops Efficiency', icon: '📦', description: 'Fulfillment/returns opportunities' }
      ]
    },

    // Life Coaching
    life: {
      tools: [
        { id: 'clarity', name: 'Clarity Map', icon: '🗺️', description: 'Values → goals → actions' },
        { id: 'keystone', name: 'Keystone Habits', icon: '🔑', description: 'Pick, stack, and track 3 habits' },
        { id: 'balance', name: 'Life Balance', icon: '⚖️', description: 'Assess balance across key areas' }
      ]
    },

    // Career Coaching
    career: {
      tools: [
        { id: 'resume', name: 'Resume Booster', icon: '📄', description: 'Impact bullets with metrics' },
        { id: 'interview', name: 'Interview Coach', icon: '🎤', description: 'Mock Q&A, STAR stories' },
        { id: 'path', name: 'Career Path', icon: '🛤️', description: 'Next role and skill gaps' }
      ]
    },

    // Relationship Coaching
    relationship: {
      tools: [
        { id: 'communication', name: 'Communication Toolkit', icon: '💬', description: 'Nonviolent communication prompts' },
        { id: 'attachment', name: 'Attachment Lens', icon: '🔗', description: 'Patterns and boundaries' },
        { id: 'rituals', name: 'Rituals', icon: '💕', description: 'Weekly connection practices' }
      ]
    },

    // Financial Coaching
    financial: {
      tools: [
        { id: 'spending', name: 'Spending Plan', icon: '💳', description: 'Monthly plan and categories' },
        { id: 'debt', name: 'Debt Strategy', icon: '📉', description: 'Snowball/avalanche plan' },
        { id: 'investing', name: 'Investing 101', icon: '📈', description: 'Index funds and risk profile' }
      ]
    },

    // Leadership Coaching
    leadership: {
      tools: [
        { id: 'coaching', name: 'Decision Framework 1-3-1', icon: '👥', description: 'Coach decision-making' },
        { id: 'cadence', name: 'Team Cadence', icon: '📅', description: 'Meetings, KPIs, scorecards' }
      ]
    },

    // Sales Coaching
    sales: {
      tools: [
        { id: 'pipeline', name: 'Pipeline Review', icon: '🔄', description: 'Stages, velocity, next actions' },
        { id: 'script', name: 'Call Script Coach', icon: '📞', description: 'Openers, discovery, closes' },
        { id: 'objections', name: 'Objection Handling', icon: '🛡️', description: 'Patterns and responses' }
      ]
    },

    // HVAC (Technical vertical)
    hvac: {
      tools: [
        { id: 'safety', name: 'Safety Checklist', icon: '⚠️', description: 'Generate safety protocols' },
        { id: 'diagnostics', name: 'Equipment Diagnostics', icon: '🔧', description: 'Troubleshoot equipment issues' },
        { id: 'compliance', name: 'Compliance Tracking', icon: '📜', description: 'Track regulatory compliance' },
        { id: 'maintenance', name: 'Maintenance Scheduler', icon: '🗓️', description: 'Schedule preventive maintenance' }
      ]
    }
  };

  return insights[vertical] || insights.business;
};

const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const result = document.execCommand('copy');
      textArea.remove();
      return result;
    }
  } catch (err) {
    console.error('Failed to copy:', err);
    return false;
  }
};

const downloadFile = (content, filename, type = 'text/plain') => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const formatExport = (messages, format, metadata = {}) => {
  const timestamp = new Date().toISOString();
  const { vertical = 'business', companyIdSlug = '', sessionDuration = 0, messageCount = 0 } = metadata;

  switch (format) {
    case 'markdown':
      let md = `# OpsVoice Conversation Export\n\n`;
      md += `**Date:** ${new Date().toLocaleDateString()}\n`;
      md += `**Vertical:** ${vertical}\n`;
      md += `**Company:** ${companyIdSlug}\n`;
      md += `**Duration:** ${Math.round(sessionDuration / 60)} minutes\n`;
      md += `**Messages:** ${messageCount}\n\n---\n\n`;
      
      messages.forEach((msg, i) => {
        const time = new Date(msg.timestamp || Date.now()).toLocaleTimeString();
        md += `## ${msg.role === 'user' ? 'You' : 'Assistant'} (${time})\n\n`;
        md += `${msg.content}\n\n`;
        if (msg.sources && msg.sources.length > 0) {
          md += `**Sources:**\n`;
          msg.sources.forEach(source => {
            md += `- [${source.title}](${source.url})\n`;
          });
          md += `\n`;
        }
      });
      return md;

    case 'txt':
      let txt = `OpsVoice Conversation Export\n`;
      txt += `Date: ${new Date().toLocaleDateString()}\n`;
      txt += `Vertical: ${vertical}\n`;
      txt += `Company: ${companyIdSlug}\n`;
      txt += `Duration: ${Math.round(sessionDuration / 60)} minutes\n`;
      txt += `Messages: ${messageCount}\n`;
      txt += `${'='.repeat(50)}\n\n`;
      
      messages.forEach((msg, i) => {
        const time = new Date(msg.timestamp || Date.now()).toLocaleTimeString();
        txt += `[${time}] ${msg.role === 'user' ? 'You' : 'Assistant'}:\n`;
        txt += `${msg.content}\n`;
        if (msg.sources && msg.sources.length > 0) {
          txt += `Sources: ${msg.sources.map(s => s.title).join(', ')}\n`;
        }
        txt += `\n`;
      });
      return txt;

    default: // text
      return formatExport(messages, 'txt', metadata);
  }
};

// Source formatting helper
const getSourceDisplay = (source) => {
  if (!source) return '';
  const name = source.title || source.name || source.filename || '';
  const page = source.page ? ` (p.${source.page})` : (source.section ? ` (${source.section})` : '');
  return name ? `${name}${page}` : (source.url || '');
};

// Build inline source attribution block (DEPRECATED - not used anymore, sources shown in cards)
const buildInlineSourceAttribution = (sources = []) => {
  // Return empty - we don't want inline source text anymore
  return '';
};

// Remove inline "Source:" text from message content
const cleanMessageContent = (content) => {
  if (!content || typeof content !== 'string') return content;
  
  // Remove lines that start with "Source:" or "Sources:"
  let cleaned = content;
  const lines = cleaned.split('\n');
  cleaned = lines.filter(line => {
    const trimmed = line.trim();
    return !trimmed.match(/^(Source|Sources):\s*/i);
  }).join('\n');
  
  // Also remove inline "Source: ..." patterns anywhere in the text
  cleaned = cleaned.replace(/\s*Source:\s*[^\n]+/gi, '');
  cleaned = cleaned.replace(/\s*Sources:\s*[^\n]+/gi, '');
  
  return cleaned.trim();
};

// Dedupe and limit sources to max 3 for compact display
const dedupeAndLimitSources = (sources = []) => {
  try {
    const groups = aggregateSources(sources || []);
    return groups.slice(0, 2);
  } catch (_) {
    return (sources || []).slice(0, 2);
  }
};

// Aggregate duplicate documents and collect pages and sections
const aggregateSources = (sources = []) => {
  const map = new Map();
  for (const s of sources) {
    // Use citation or title for grouping key - extract just the document name part
    let titleKey;
    let rawCitation;
    
    // Check citation first, then title, then fallback
    if (s.citation && typeof s.citation === 'string') {
      // Citation format: "Doc Name - Section X (Page Y)" - extract just "Doc Name"
      rawCitation = s.citation;
      const parts = s.citation.split(' - ');
      titleKey = parts[0].trim();
    } else if (s.title && typeof s.title === 'string' && s.title.includes(' - ')) {
      // Title has citation format embedded - extract just doc name
      rawCitation = s.title;
      const parts = s.title.split(' - ');
      titleKey = parts[0].trim();
    } else {
      // Use title as-is (should be just doc name from backend)
      titleKey = s.title || s.filename || s.name || 'Document';
      rawCitation = titleKey;
    }
    
    // Safety check: if titleKey still has " - " in it, force split again
    if (titleKey && titleKey.includes(' - ')) {
      const parts = titleKey.split(' - ');
      titleKey = parts[0].trim();
      if (!rawCitation || rawCitation === titleKey) {
        rawCitation = s.title || s.citation || titleKey;
      }
    }
    
    // Normalize the document name for consistent grouping
    // Remove timestamp prefix (e.g., "1768766305 Oilfield Long Doc.pdf" -> "Oilfield Long Doc.pdf")
    let normalizedKey = String(titleKey).trim();
    normalizedKey = normalizedKey.replace(/^\d{10,}\s+/i, ''); // Remove timestamp prefix (10+ digits followed by space)
    normalizedKey = normalizedKey.toLowerCase().trim();
    // Remove common file extensions
    normalizedKey = normalizedKey.replace(/\.(pdf|docx|doc|txt|md)$/i, '');
    // Normalize whitespace
    normalizedKey = normalizedKey.replace(/\s+/g, ' ').trim();
    
    const key = normalizedKey;
    const existing = map.get(key);
    const current = existing || { 
      title: titleKey, // Use the original extracted title (with .pdf if present)
      url: s.url || '', 
      sections: new Map(), // Map of section -> Set of pages
      pages: new Set(), // All pages (for fallback)
      metadata: s.metadata || null,
      visibility: s.visibility || s.metadata?.visibility || 'reference' // Default to reference (public)
    };
    
    // If document already exists, prefer the title with .pdf extension if current doesn't have it
    if (existing && !current.title.includes('.pdf') && titleKey.includes('.pdf')) {
      current.title = titleKey;
    }
    
    // Preserve URL - prefer non-empty URLs, prioritize public (reference) URLs
    if (s.url && s.url.trim() !== '' && s.url !== '#') {
      if (!current.url || current.url === '#' || current.url.trim() === '') {
        current.url = s.url;
      } else if (s.visibility === 'reference' && current.visibility !== 'reference') {
        // Prefer reference (public) URLs over training_only URLs
        current.url = s.url;
      }
    }
    
    // Preserve visibility - prefer reference (public) over training_only
    if (s.visibility) {
      if (!current.visibility || current.visibility === 'training_only') {
        current.visibility = s.visibility;
      }
    } else if (s.metadata?.visibility) {
      if (!current.visibility || current.visibility === 'training_only') {
        current.visibility = s.metadata.visibility;
      }
    }
    
    // Extract section and page from citation (use rawCitation which has full info)
    let sectionName = null;
    let pageNum = null;
    
    if (rawCitation && rawCitation.includes(' - ')) {
      // Citation format: "Doc Name - Section X (Page Y)" or "Doc Name - Section X" or "Doc Name - Page Y"
      const citationParts = rawCitation.split(' - ');
      if (citationParts.length > 1) {
        const sectionPart = citationParts[1];
        // Extract section name (remove page info)
        sectionName = sectionPart.replace(/\s*\(Page\s+\d+\)/gi, '').replace(/\s*\(p\.\s+\d+\)/gi, '').trim();
        // If it's JUST "Page XX", don't treat it as a section
        if (sectionName.match(/^Page\s+\d+$/i)) {
          sectionName = null;
        }
        // Extract page number
        const pageMatch = sectionPart.match(/\(Page\s+(\d+)\)|\(p\.\s+(\d+)\)|Page\s+(\d+)/i);
        if (pageMatch) {
          pageNum = Number(pageMatch[1] || pageMatch[2] || pageMatch[3]);
        }
      }
    }
    
    // Also check metadata for section/page
    if (!sectionName && s.metadata) {
      const metaSection = s.metadata.section || s.metadata.subsection;
      if (metaSection) {
        sectionName = String(metaSection).startsWith('Section') ? String(metaSection) : `Section ${metaSection}`;
      }
    }
    
    if (!pageNum) {
      // Try page field
      if (s.page !== undefined && s.page !== null && s.page !== '') {
        pageNum = Number(String(s.page).replace(/[^0-9]/g, ''));
      }
      // Try pages array
      if (!pageNum && Array.isArray(s.pages) && s.pages.length > 0) {
        pageNum = Number(String(s.pages[0]).replace(/[^0-9]/g, ''));
      }
    }
    
    // Store by section (or "General" if no section)
    const sectionKey = sectionName || 'General';
    if (!current.sections.has(sectionKey)) {
      current.sections.set(sectionKey, new Set());
    }
    
    // Always add the section, even if no page number (for sections without pages)
    if (pageNum && !Number.isNaN(pageNum) && pageNum > 0) {
      current.sections.get(sectionKey).add(pageNum);
      current.pages.add(pageNum); // Also add to overall pages set
    } else if (sectionKey !== 'General') {
      // If we have a section but no page, still record the section (with empty pages set)
      // This ensures "Section 2" shows up even without a page number
    }
    
    map.set(key, current);
  }
  
  // Convert to array format
  const result = Array.from(map.values()).map(g => {
    // Convert sections map to array of {section, pages}
    const sectionsArray = Array.from(g.sections.entries()).map(([section, pages]) => ({
      section: section,
      pages: Array.from(pages).sort((a, b) => a - b)
    }));
    
    return {
      title: g.title,
      url: g.url,
      sections: sectionsArray, // Array of {section, pages}
      pages: Array.from(g.pages).sort((a,b) => a-b), // All pages (fallback)
      metadata: g.metadata,
      visibility: g.visibility || 'reference' // Include visibility in output
    };
  });
  
  return result;
};

// Helper function to clean document names from full paths
const cleanDocumentName = (title) => {
  if (!title) return 'Source';
  
  let cleanTitle = String(title);
  
  // Remove Windows path prefix (e.g., "C:\Users\...\")
  if (cleanTitle.includes('\\')) {
    cleanTitle = cleanTitle.split('\\').pop();
  }
  
  // Remove Unix path prefix (e.g., "/path/to/")
  if (cleanTitle.includes('/')) {
    cleanTitle = cleanTitle.split('/').pop();
  }
  
  // Remove company slug and timestamp prefix (e.g., "pai3-demo 1768149239 ")
  // Pattern: company-slug + space + timestamp + space
  cleanTitle = cleanTitle.replace(/^[a-z0-9-]+\s+\d+\s+/i, '');
  
  // Remove file extensions
  cleanTitle = cleanTitle.replace(/\.(docx|txt|pdf|md)$/gi, '');
  
  // Trim and return, fallback to "Source" if empty
  cleanTitle = cleanTitle.trim();
  return cleanTitle || 'Source';
};

// Normalize backend citations/sources into a unified, de-duplicated list
const normalizeSources = (payload) => {
  const rawSources = payload?.sources || payload?.citations || [];
  if (!Array.isArray(rawSources)) return [];

  // Some backends return sources like: { snippet, metadata: { title, sop_file_url, page_number, ... } }
  const unwrap = (s) => {
    try {
      if (s && s.metadata && typeof s.metadata === 'object') {
        // Extract clean title from citation field (e.g., "Founder Leverage Ladder - Section 1")
        let cleanTitle = s.citation || s.metadata.title || s.title || s.filename || s.name;
        
        // Use cleanDocumentName helper to clean up paths
        cleanTitle = cleanDocumentName(cleanTitle);
        
        // Prefer metadata fields but keep top-level fallbacks
        // Preserve citation field (backend sends cleaned citation: "Doc Name - Section X (Page Y)")
        return {
          ...s,
          ...s.metadata,
          title: cleanTitle,
          citation: s.citation || s.metadata.citation, // Preserve cleaned citation from backend
          url: s.metadata.url || s.metadata.link || s.metadata.sop_file_url || s.url || s.link || s.sop_file_url,
          page: s.metadata.page || s.metadata.page_number || s.page || s.page_number,
          section: s.metadata.section || s.section,
          layer: s.layer || s.metadata.layer,
          layer_name: s.layer_name || s.metadata.layer_name,
        };
      }
    } catch (_) {}
    return s || {};
  };

  // Removed duplicate handleBackendResponse function

  const isCoachDoc = (s) => {
    const url = s?.url || s?.link || s?.sop_file_url || '';
    return Boolean(s?.sop_file_url) || /(uploads|sop-files|storage|supabase|s3|gcs|static\/sop-files)/i.test(url);
  };

  // Unwrap first, then sort coach docs first
  const unwrapped = rawSources.map(unwrap);
  const sorted = [...unwrapped].sort((a, b) => Number(isCoachDoc(b)) - Number(isCoachDoc(a)));

  // De-dupe by title and cap
  const seen = new Set();
  const result = [];
  for (const s of sorted) {
    let title = (s.title || s.filename || s.name || '').trim();
    if (!title) {
      // Try to derive from URL last segment
      const url = String(s.url || '').trim();
      if (url) {
        try { title = decodeURIComponent(url.split('/').pop() || '').trim(); } catch (_) {}
      }
    }
    if (!title) title = 'Document';
    if (!title) continue;
    const key = title.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push({
      title,
      url: s.url || s.link || s.sop_file_url || '',
      page: s.page || s.page_number || undefined,
      section: s.section || undefined,
      _coachPreferred: isCoachDoc(s)
    });
    if (result.length >= 2) break;
  }
  return result;
};

const hasCoachPreferredSource = (sources = []) => Array.isArray(sources) && sources.some(s => s && (s._coachPreferred || /sop-files|\/static\/sop-files|storage|supabase/i.test(String(s.url||''))));

// Compute conversation context with health-specific parsing for weights/activity
function computeConversationContext(messages, coachingType, vertical = 'business') {
  try {
    const base = extractConversationContext(messages, coachingType, vertical) || {};
    const joined = (messages || []).map(m => String(m?.content || '')).join(' ').trim();
    const text = joined.toLowerCase();

    // Goal weight patterns (e.g., "goal is 155", "to 160 by December")
    let goalWeight = null;
    const goalPatterns = [
      /goal\s*(?:is|=)?\s*(\d{2,3}(?:\.\d+)?)\s*(?:lbs?|pounds?|kg|kilos?)?/i,
      /to\s*(\d{2,3}(?:\.\d+)?)\s*(?:lbs?|pounds?|kg|kilos?)?\s*by\s*([a-z]+\s*\d{0,4})?/i
    ];
    for (const re of goalPatterns) {
      const m = joined.match(re);
      if (m && m[1]) { goalWeight = m[1]; break; }
    }

    // Current weight patterns (e.g., "I weigh 182 lbs", "weight 182")
    let currentWeight = null;
    const currentPatterns = [
      /\b(?:i\s*(?:am|'m)\s*)?(?:weigh|weight)\s*(\d{2,3}(?:\.\d+)?)\s*(?:lbs?|pounds?|kg|kilos?)?/i,
      /\b(\d{2,3}(?:\.\d+)?)\s*(?:lbs?|pounds?|kg|kilos?)\b(?!\s*by)/i
    ];
    for (const re of currentPatterns) {
      const m = joined.match(re);
      if (m && m[1]) { currentWeight = m[1]; break; }
    }

    // Activity level patterns
    let activity = null;
    if (/lightly\s+active/i.test(joined)) activity = 'lightly_active';
    else if (/moderately\s+active/i.test(joined)) activity = 'moderately_active';
    else if (/very\s+active/i.test(joined)) activity = 'very_active';
    else if (/\bactive\b/i.test(joined)) activity = 'active';

    const ctx = { ...base };
    // Map generic weight to current_weight if only one weight is present
    if (!currentWeight && Array.isArray(base.weight) && base.weight.length > 0) {
      const num = String(base.weight[0]).replace(/[^0-9.]/g, '');
      if (num) currentWeight = num;
    }
    if (currentWeight) ctx.current_weight = currentWeight;
    if (goalWeight) ctx.goal_weight = goalWeight;
    if (activity) ctx.activity_level = activity;

    // Enhanced Business-style extractions with better patterns
    // Revenue (ARR/revenue/income) - more aggressive patterns
    const revPatterns = [
      // "arr 400k" pattern (exact match for user's input)
      /(?:arr|annual\s+recurring\s+revenue)\s*([0-9,]+(?:\.[0-9]+)?)\s*([kKmMbB])?/i,
      // "$500k revenue" pattern
      /\$?\s*([0-9,]+(?:\.[0-9]+)?)\s*([kKmMbB])?\s*(?:arr|annual\s+revenue|revenue|income)/i,
      // "revenue is 500k" pattern
      /(?:revenue|income)\s*(?:is|of|at)?\s*\$?\s*([0-9,]+(?:\.[0-9]+)?)\s*([kKmMbB])?/i,
      // "we make 500k" pattern
      /(?:we\s+)?(?:make|earn|generate|have)\s+(?:a\s+)?(?:revenue\s+of\s+)?\$?\s*([0-9,]+(?:\.[0-9]+)?)\s*([kKmMbB])?/i,
      // "400k" standalone pattern
      /\b([0-9,]+)\s*([kKmMbB])\b/i
    ];
    
    for (const pattern of revPatterns) {
      const revMatch = joined.match(pattern);
    if (revMatch) {
        const amount = revMatch[1];
        const suffix = revMatch[2] || '';
        ctx.revenue = `${amount}${suffix}`;
        break;
      }
    }
    
    // Team size - enhanced patterns
    const teamPatterns = [
      // "team 13" pattern (exact match for user's input)
      /(?:team|staff|employees?|people)\s*([0-9,]+)/i,
      /(?:team|staff|employees?|people)\s*(?:size|of|is|are|:\s*)?([0-9,]+)/i,
      /([0-9,]+)\s*(?:team|staff|employees?|people)/i,
      /(?:we\s+have|i\s+have)\s+([0-9,]+)\s*(?:team|staff|employees?|people)/i
    ];
    
    for (const pattern of teamPatterns) {
      const teamMatch = joined.match(pattern);
    if (teamMatch) {
      ctx.team_size = teamMatch[1];
        break;
      }
    }
    
    // Hours per week - enhanced patterns
    const hourPatterns = [
      // "working 76 hours" pattern (exact match for user's input)
      /(?:work|working|spend|spending|put\s+in|doing)\s+([0-9,]+)\s*(?:hours?|hrs?|h)/i,
      // "76 hours per week" pattern
      /([0-9,]+)\s*(?:hours?|hrs?|h)\s*(?:per\s*week|weekly|\/\s*week|work|working)/i,
      // "hours per week is 76" pattern
      /(?:hours?|hrs?|h)\s*(?:per\s*week|weekly|\/\s*week)\s*(?:is|are|:\s*)?([0-9,]+)/i,
      // Simple "76 hours" pattern
      /([0-9,]+)\s*(?:hours?|hrs?|h)\b/i
    ];
    
    for (const pattern of hourPatterns) {
      const hrsMatch = joined.match(pattern);
    if (hrsMatch) {
      ctx.hours_per_week = hrsMatch[1];
        break;
    }
    }
    
    return ctx;
  } catch (_) {
    return extractConversationContext(messages, coachingType, vertical) || {};
  }
}

// (removed duplicate aggregateSources definition)

// Main Widget Component
const OpsVoiceWidget = () => {
  // Component initialization
  
  // URL Parameters and Configuration
  const urlParams = getUrlParams();
  const [vertical] = useState(urlParams.vertical || getVerticalFromHostname(window.location.hostname));
  const [companyIdSlug] = useState(urlParams.companyIdSlug);
  const [clientId] = useState(urlParams.clientId); // NEW: Extract client_id from URL
  const [userEmail] = useState(urlParams.userEmail); // NEW: Extract email from URL for team member tracking
  const audience = resolveAudience();
  const [assistantId] = useState(urlParams.assistantId);
  
  // Spreadsheets mode for OpsIQ - Check URL for initial state
  const [dataSource, setDataSource] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const urlDataSource = params.get('dataSource');
    return (urlDataSource === 'spreadsheets') ? 'spreadsheets' : 'docs';
  });
  const [spreadsheetsEnabled, setSpreadsheetsEnabled] = useState(false);
  
  // Coaching Specialization State (must be above any effects that use it)
  const [coachingType, setCoachingType] = useState('business');
  
  // Compute schema once per session
  const schema = useMemo(() => INSIGHT_SCHEMAS[coachingType] || INSIGHT_SCHEMAS.business, [coachingType]);

  // Central helper to run insights
  const runInsightsFor = useCallback((text, fullTranscript) => {
    try {
      const { updates } = engineParse(text, schema);
      setConversationContext(prev => {
        const merged = engineMerge(prev, updates);
        setNextQuestions(engineNext(merged, schema));       // "What's missing" smart follow-ups
        setSuggestedTools(engineMatch(fullTranscript || text, schema)); // tool triggers
        return merged;
      });
    } catch (e) {
      // Insights parsing failed
    }
  }, [schema]);

  const [companyConfig, setCompanyConfig] = useState(null);
  const [showAllLocked, setShowAllLocked] = useState(false);
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [sessionStartTime, setSessionStartTime] = useState(Date.now());
  
  // Voice recording state (integrated with existing VAPI system)
  const [isRecording, setIsRecording] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState('');
  
  // Voice handlers - integrate with existing VAPI system
  const handleStartRecording = async () => {
    if (!hasVapi || !vapi) {
      return;
    }
    // Use existing startVoiceCall function
    await startVoiceCall();
  };
  
  const handleStopRecording = async () => {
    if (vapi && isRecording) {
      await stopVoiceCall();
    }
  };
  
  // Mobile detection and optimization
  const viewport = useMobileViewport();
  const mobilePerformance = useMobilePerformance();
  const mobileKeyboard = useMobileKeyboard();
  
  const isMobile = viewport.isMobile;
  // OpsIQ-style UX surfaces (menus, sources sheet, voice UX) for these verticals
  const isOpsMobileVertical = (vertical === 'opsiq' || vertical === 'pai3' || vertical === 'business');
  const mobileModeStorageKey = useMemo(() => {
    const slug = companyIdSlug || 'unknown';
    return `ov_mobile_mode_${slug}`;
  }, [companyIdSlug]);

  // Mobile mode: 'talk' | 'type' | null (only applies to OpsIQ + PAI3 on mobile)
  // Default to 'type' for OpsIQ/Business so chat shows immediately
  const [mobileMode, setMobileMode] = useState(() => {
    try {
      const stored = localStorage.getItem(`ov_mobile_mode_${urlParams.companyIdSlug || 'unknown'}`);
      if (stored === 'talk' || stored === 'type') return stored;
      // Default to 'type' mode for OpsIQ/Business on mobile
      return (vertical === 'opsiq' || vertical === 'business') ? 'type' : null;
    } catch (_) {
      return (vertical === 'opsiq' || vertical === 'business') ? 'type' : null;
    }
  });

  // OpsIQ + PAI3 session drawer (mobile only)
  const [isSessionsOpen, setIsSessionsOpen] = useState(false);
  const [isMenuDrawerOpen, setIsMenuDrawerOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState(null);
  const [opsSessions, setOpsSessions] = useState(() => {
    try {
      return listOpsSessions(urlParams.companyIdSlug);
    } catch (_) {
      return [];
    }
  });

  // Keep mobileMode in sync when slug changes
  useEffect(() => {
    if (!isOpsMobileVertical) return;
    try {
      const stored = localStorage.getItem(mobileModeStorageKey);
      if (stored === 'talk' || stored === 'type') {
        setMobileMode(stored);
      } else {
        // Default to 'type' for OpsIQ/Business on mobile - don't set to null
        const defaultMode = (vertical === 'opsiq' || vertical === 'business') ? 'type' : null;
        if (defaultMode && mobileMode !== defaultMode) {
          setMobileMode(defaultMode);
        }
      }
    } catch (_) {
      // On error, still default to 'type' for OpsIQ/Business
      const defaultMode = (vertical === 'opsiq' || vertical === 'business') ? 'type' : null;
      if (defaultMode && mobileMode !== defaultMode) {
        setMobileMode(defaultMode);
      }
    }
  }, [isOpsMobileVertical, mobileModeStorageKey, vertical, mobileMode]);

  // (moved) When a voice call ends on OpsIQ+PAI3 mobile, switch to Type mode for review
  
  // Mobile fullscreen state
  const [isMobileFullscreen, setIsMobileFullscreen] = useState(false);
  
  // Track if user has scrolled up (to show "scroll to latest" button)
  const [showScrollToLatest, setShowScrollToLatest] = useState(false);
  
  // Toggle fullscreen mode on mobile
  const toggleMobileFullscreen = useCallback(() => {
    setIsMobileFullscreen(prev => !prev);
    // Haptic feedback if available
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
  }, []);
  
  // Offline manager
  const offlineManager = useOfflineManager(API_BASE);
  
  // Swipe gestures
  const swipeGestures = useSwipeGestures({
    enabled: isMobile && isMobileFullscreen,
    onSwipeDown: () => {
      if (isMobileFullscreen) {
        toggleMobileFullscreen();
      }
    },
    onSwipeUp: () => {
      if (!isMobileFullscreen) {
        toggleMobileFullscreen();
      }
    },
  });

  // Visual viewport handling for keyboard-safe heights
  useEffect(() => {
    if (!window.visualViewport) return;
    const onResize = () => {
      const vv = window.visualViewport;
      document.documentElement.style.setProperty('--vvh', `${vv.height}px`);
    };
    window.visualViewport.addEventListener('resize', onResize);
    onResize();
    return () => window.visualViewport.removeEventListener('resize', onResize);
  }, []);

  // Professional error handler
  const professionalErrorHandler = (error) => {
    console.error('Professional Error Handler:', error);
    return "I'm experiencing a brief connection issue. Your session progress is saved. Please try again in a moment.";
  };

  // Global error handler for unhandled promises
  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      console.error('[Global Error] Unhandled promise rejection:', event.reason);
      console.error('[Global Error] Error details:', {
        message: event.reason?.message,
        stack: event.reason?.stack,
        name: event.reason?.name,
        url: event.reason?.config?.url || event.reason?.url
      });
      
      // Ignore certain errors that are expected (like aborted requests, network errors, etc.)
      if (event.reason?.name === 'AbortError' || 
          event.reason?.message?.includes('abort') ||
          event.reason?.message?.includes('Failed to fetch') ||
          event.reason?.message?.includes('NetworkError') ||
          event.reason?.message?.includes('network') ||
          event.reason?.code === 'NETWORK_ERROR' ||
          // Ignore errors from non-critical endpoints (metrics, tools, etc.)
          (event.reason?.url && (
            event.reason.url.includes('/metrics') ||
            event.reason.url.includes('/coach-tools') ||
            event.reason.url.includes('/activity') ||
            event.reason.url.includes('/company-docs')
          ))) {
        // Silently ignore non-critical errors - they're handled by their respective components
        event.preventDefault();
        return;
      }
      
      // Only show error for critical failures (like query endpoint failures)
      // Prevent the default browser error handling
      event.preventDefault();
      // Set a user-friendly error message only for critical errors
      const errorMessage = event.reason?.message || '';
      if (errorMessage.includes('query') || errorMessage.includes('message') || !errorMessage) {
      setError('A connection issue occurred. Your session is safe. Please try again.');
      }
    };

    const handleError = (event) => {
      console.error('[Global Error] JavaScript error:', event.error);
      setError('An unexpected error occurred. Please refresh the page if issues persist.');
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);
  
  
  // Premium icons for Dan Martell - moved outside render to prevent re-renders
  const premiumIcons = {
    'health': '⚡',
    'bottleneck': '🎯',
    'action': '🔥',
    'process-efficiency': '⚙️',
    'safety-checklist': '⚠️'
  };
  
  
  const config = pickVerticalConfig(vertical, companyIdSlug);
  // Derived coaching config
  const coachingCfg = useMemo(() => (COACHING_TYPES[coachingType] || COACHING_TYPES.business), [coachingType]);
  const preset = getPreset(vertical);
  const verticalInsights = { tools: config.tools };

  // Core State - Default to 'home' for MentorMind, 'chat' for others
  // Validate tab against vertical to prevent blank screens
  const [activeTab, setActiveTab] = useState(() => {
    const isMentorMind = (urlParams.vertical === 'mentormind' || urlParams.vertical === 'coaching') ||
                         getVerticalFromHostname(window.location.hostname) === 'mentormind';
    const validMentorMindTabs = ['home', 'sessions', 'progress', 'insights'];
    const validOtherTabs = ['chat', 'voice', 'coach', 'documents', 'insights', 'calculators'];
    
    const requestedTab = urlParams.hash;
    
    if (isMentorMind) {
      // For MentorMind, only allow valid tabs, default to 'home'
      return validMentorMindTabs.includes(requestedTab) ? requestedTab : 'home';
    } else {
      // For other verticals, allow their tabs, default to 'chat'
      return validOtherTabs.includes(requestedTab) ? requestedTab : 'chat';
    }
  });
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState(null);
  
  // Keyboard navigation (moved after setActiveTab is defined)
  const { containerRef: keyboardRef } = useKeyboardNavigation({
    enableArrowKeys: true,
    enableTabNavigation: true,
    enableEscapeKey: true,
    onEscape: () => {
      // Close any open modals or return to main view
      setActiveTab('chat');
    }
  });
  const [insightsEngine, setInsightsEngine] = useState(null);
  const [conversationInsights, setConversationInsights] = useState([]);
  const [contextualSuggestions, setContextualSuggestions] = useState([]);
  const [activeToolResult, setActiveToolResult] = useState(null);
  const [businessProfile, setBusinessProfile] = useState({
    arr: null,
    teamSize: null,
    weeklyHours: null,
    stage: null,
    challenges: [],
    goals: [],
    lastUpdated: null
  });
  const [conversationContext, setConversationContext] = useState({});
  const [nextQuestions, setNextQuestions] = useState([]);
  const [suggestedTools, setSuggestedTools] = useState([]);
  const [showSuggestionsPopup, setShowSuggestionsPopup] = useState(false);
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const [likedMessages, setLikedMessages] = useState(new Set());
  const [savedMessages, setSavedMessages] = useState([]);
  
  // Subscription tier state for voice blocking
  const [subscriptionTier, setSubscriptionTier] = useState(null);
  const [voiceMinutesRemaining, setVoiceMinutesRemaining] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  
  // Coach Notes State - Questions, unclear items, goals for follow-up
  const [coachNotes, setCoachNotes] = useState(() => {
    try {
      const saved = localStorage.getItem(`ov_notes_${companyIdSlug}`);
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [newCoachNote, setNewCoachNote] = useState({ type: 'question', content: '' });
  const [frameworkExecutionActive, setFrameworkExecutionActive] = useState(false); // Track active framework execution
  const [frameworkExecutionData, setFrameworkExecutionData] = useState(null); // Current framework execution state
  const [showFrameworkCompletionModal, setShowFrameworkCompletionModal] = useState(false); // Completion celebration
  const [frameworkCompletionData, setFrameworkCompletionData] = useState(null); // Data for completion modal
  const [viewingCompletedFramework, setViewingCompletedFramework] = useState(null); // For viewing saved framework summaries
  const [frameworkSuggestion, setFrameworkSuggestion] = useState(null); // Suggested framework from intent detection
  
  // New MentorMind session management state
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [showFrameworkPicker, setShowFrameworkPicker] = useState(false);
  const [selectedFramework, setSelectedFramework] = useState(null);
  const isMentorMind = vertical === 'mentormind' || vertical === 'coaching';
  
  // Session management handlers for MentorMind
  const handleStartVoiceSession = async () => {
    if (!companyIdSlug) return;
    const session = sessionManager.createSession(companyIdSlug, {
      firstMessage: null,
      frameworkName: null
    });
    setActiveSessionId(session.id);
    sessionManager.setCurrentSession(companyIdSlug, session.id);
    setActiveTab('sessions');
    // Start voice call
    if (hasVapi && vapi) {
      await startVoiceCall();
    }
  };

  const handleStartTextSession = () => {
    if (!companyIdSlug) return;
    const session = sessionManager.createSession(companyIdSlug, {
      firstMessage: null,
      frameworkName: null
    });
    setActiveSessionId(session.id);
    sessionManager.setCurrentSession(companyIdSlug, session.id);
    setActiveTab('sessions');
    // Focus input
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  };

  const handleStartWithFramework = () => {
    setShowFrameworkPicker(true);
  };

  const handleFrameworkSelect = (framework) => {
    if (!companyIdSlug) return;
    setSelectedFramework(framework);
    const session = sessionManager.createSession(companyIdSlug, {
      firstMessage: null,
      framework: framework.id,
      frameworkName: framework.name
    });
    setActiveSessionId(session.id);
    sessionManager.setCurrentSession(companyIdSlug, session.id);
    setActiveTab('sessions');
    // Initialize framework execution if needed
    if (framework.tool_type === 'framework' || framework.active_session) {
      setFrameworkExecutionActive(true);
      setFrameworkExecutionData({
        frameworkId: framework.id,
        frameworkName: framework.name,
        steps: framework.steps || []
      });
    }
  };

  const handleResumeSession = (sessionId) => {
    if (!companyIdSlug) return;
    const session = sessionManager.getSession(companyIdSlug, sessionId);
    if (!session) return;
    
    setActiveSessionId(sessionId);
    sessionManager.setCurrentSession(companyIdSlug, sessionId);
    
    // Restore messages
    if (session.messages && session.messages.length > 0) {
      setMessages(session.messages);
    }
    
    // Restore context
    if (session.context) {
      setConversationContext(session.context);
    }
    
    // Restore framework if present
    if (session.framework) {
      setSelectedFramework({
        id: session.framework,
        name: session.frameworkName
      });
      setFrameworkExecutionActive(true);
    }
    
    setActiveTab('sessions');
  };

  // Validate and correct activeTab when vertical changes or invalid tab is set
  useEffect(() => {
    const validMentorMindTabs = ['home', 'sessions', 'progress', 'insights'];
    const validOtherTabs = ['chat', 'voice', 'coach', 'documents', 'insights', 'calculators'];

    if (isMentorMind && !validMentorMindTabs.includes(activeTab)) {
      // Invalid tab for MentorMind - redirect to home
      setActiveTab('home');
      window.location.hash = 'home';
    } else if (!isMentorMind && !validOtherTabs.includes(activeTab)) {
      // Invalid tab for other verticals - redirect to chat
      setActiveTab('chat');
      window.location.hash = 'chat';
    }
  }, [activeTab, isMentorMind]);

  // Auto-save messages to session
  useEffect(() => {
    if (isMentorMind && activeSessionId && companyIdSlug && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage) {
        sessionManager.addMessageToSession(companyIdSlug, activeSessionId, lastMessage);
        // Update session context
        sessionManager.updateSessionContext(companyIdSlug, activeSessionId, conversationContext);
      }
    }
  }, [messages, activeSessionId, companyIdSlug, isMentorMind, conversationContext]);
  
  const sessionId = useMemo(() => getSessionId(), []);
  
  // Save sessions for PAI3/OpsIQ after assistant messages (debounced)
  // MUST be after sessionId declaration to avoid temporal dead zone
  useEffect(() => {
    if (!isOpsMobileVertical || !companyIdSlug || messages.length === 0) return;
    
    // Only save if last message is from assistant (conversation complete)
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.role !== 'assistant') return;
    
    // Debounce: wait 2 seconds after last assistant message before saving
    const saveTimer = setTimeout(() => {
      try {
        const hasUserMessage = messages.some(m => m.role === 'user');
        const hasAssistantMessage = messages.some(m => m.role === 'assistant');
        
        if (hasUserMessage && hasAssistantMessage) {
          const session = {
            id: sessionId || `session-${Date.now()}`,
            companyIdSlug,
            vertical,
            mode: 'text',
            title: messages.find(m => m.role === 'user')?.content?.substring(0, 50) || 'New Session',
            timestamp: Date.now(),
            messagesSnapshot: messages.slice(-80), // Cap to last 80 messages
          };
          const updatedSessions = saveOpsSession(companyIdSlug, session);
          setOpsSessions(updatedSessions);
        }
      } catch (e) {
      }
    }, 2000);
    
    return () => clearTimeout(saveTimer);
  }, [messages.length, isOpsMobileVertical, companyIdSlug, sessionId, vertical]);
  
  const derivedContextKeys = useMemo(() => getDerivedContextKeys(conversationContext), [conversationContext]);
  const backendCompanyId = useMemo(() => getBackendCompanyId(companyIdSlug), [companyIdSlug]);
  
  // Debounce suggestions as user types (moved after conversationContext declaration)
  const debouncedExtract = useMemo(() => debounce((t) => {
    try {
      const { updates } = engineParse(t, schema);
      // If you render live suggestion pills, compute from updates here
      setNextQuestions(engineNext({ ...conversationContext, ...updates }, schema));
    } catch {}
  }, 180), [schema, conversationContext]);
  
  // Memoized insights data - moved after conversationContext declaration to fix temporal dead zone
  const availableCards = useMemo(() => 
    createInsightRegistry(vertical, coachingType || 'business', companyIdSlug), 
    [vertical, coachingType, companyIdSlug]
  );
  const conversationData = conversationContext || {};
  const fallbackToolData = useMemo(() => {
    return (availableCards || []).map(card => {
      const ready = typeof card.visibleWhen === 'function' ? card.visibleWhen(conversationData) : false;
      const needs = Array.isArray(card.needs) ? card.needs : [];
      const missing = needs.filter(need => !(conversationData && conversationData[need]));
      return {
        id: card.id,
        name: card.label || card.name || card.id,
        icon: premiumIcons[card.id] || card.icon || '🎯',
        description: card.description || '',
        needs,
        ready,
        missing,
        sourceDocTitle: card.source || null,
        tool_type: card.tool_type || 'rag_only', // Include tool_type for execution
        tool_config: card.tool_config || {}, // Include tool_config for execution
        compute: card.compute, // Include compute function if available
        doc_id: card.doc_id || null // Include doc_id for RAG fallback
      };
    });
  }, [availableCards, conversationData]);
  const fallbackToolsProvider = useCallback(() => {
    // Include preset tools for specific company slugs
    if (companyIdSlug === 'coach-dan-martell-demo' || companyIdSlug === 'coach-dan-martell-demo-default') {
      return fallbackToolData;
    }
    // Include preset tools for founder-coaching-demo
    if (companyIdSlug === 'founder-coaching-demo') {
      return fallbackToolData;
    }
    return [];
  }, [fallbackToolData, companyIdSlug]);
  const isTrialPlan = useMemo(() => {
    if (companyConfig?.plan_tier) {
      return companyConfig.plan_tier === 'trial';
    }
    if (typeof companyConfig?.is_trial !== 'undefined') {
      return Boolean(companyConfig.is_trial);
    }
    return false;
  }, [companyConfig]);
  const {
    readyTools,
    lockedTools,
    loading: toolsLoading,
    error: toolsError,
    planTier: toolsPlanTier,
    toolLimit: toolsLimit,
    currentCount: toolsCount,
    celebration: toolCelebration,
    refetch: refetchTools,
    isFallback: toolsFallback
  } = useToolsFeed({
    companyIdSlug: backendCompanyId,
    sessionId,
    contextKeys: derivedContextKeys,
    isTrialPlan,
    fallbackToolsProvider,
    vertical // Pass vertical to prevent 403 errors on non-coaching verticals
  });
  
  // Smart insights notification - shows when tools are ready
  const [insightNotification, setInsightNotification] = useState(null);
  const [showInsightModal, setShowInsightModal] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [toolExecutionResult, setToolExecutionResult] = useState(null);
  const [toolExecuting, setToolExecuting] = useState(false);
  const [toolsSectionExpanded, setToolsSectionExpanded] = useState(true);
  
  // Tool context for better chat integration
  const [toolContext, setToolContext] = useState(null);
  
  // RAG query function for tool fallback
  const queryRAGForTool = useCallback(async (toolName, docId) => {
    if (!docId) return null;
    try {
      // Query backend for document content
      const response = await fetch(`${API_BASE}/api/v1/documents/${encodeURIComponent(docId)}/content`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        return data.content || data.text || `Framework: ${toolName}`;
      }
    } catch (error) {
      console.error('[Tool Executor] RAG query error:', error);
    }
    return `Framework: ${toolName}. Ask questions about this framework in the chat.`;
  }, []);
  
  // Execute tool asynchronously
  const executeToolAsync = useCallback(async (tool, contextData) => {
    if (!tool) return;
    
    setToolExecuting(true);
    try {
      const result = await executeTool(tool, contextData || conversationData, queryRAGForTool);
      setToolExecutionResult(result);
    } catch (error) {
      console.error('[Tool Execution] Error:', error);
      setToolExecutionResult({
        toolType: 'rag_only',
        content: tool.description || `Framework: ${tool.name || tool.label}. Ask questions about this framework in the chat.`,
        actions: ['Ask questions about this framework', 'Use this framework in your conversation']
      });
    } finally {
      setToolExecuting(false);
    }
  }, [conversationData, queryRAGForTool]);

  const resumeFramework = useCallback((tool) => {
    if (!tool) return;
    // Pre-fill the chat input with a trigger message
    const triggerMessage = `Coach me through ${tool.name || tool.label || 'this framework'}`;
    setInputText(triggerMessage);
    setActiveTab('chat');
    // Focus the input so user can just hit Enter
    setTimeout(() => {
      const chatInput = document.querySelector('.ov-chat-input');
      if (chatInput) chatInput.focus();
    }, 200);
  }, []);

  const resetFrameworkSession = useCallback(async (tool) => {
    if (!tool || !sessionId) return;
    try {
      await fetch(`${API_BASE}/api/v1/frameworks/${encodeURIComponent(tool.id)}/reset-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId })
      });
      refetchTools();
      // Note: addToast will be available when this is called (not at init time)
      if (typeof addToast === 'function') {
        addToast(`${tool.name || 'Framework'} reset`, 'success');
      }
    } catch (error) {
      console.error('Failed to reset framework session', error);
      if (typeof addToast === 'function') {
        addToast('Unable to reset framework right now', 'error');
      }
    }
  }, [sessionId, refetchTools]);
  
  // Execute tool when selected or conversationData changes
  useEffect(() => {
    if (selectedTool && showInsightModal) {
      // Use latest conversationData when executing tool
      executeToolAsync(selectedTool, conversationData);
    } else {
      setToolExecutionResult(null);
    }
  }, [selectedTool, showInsightModal, executeToolAsync, conversationData]);
  
  // Check for ready tools and show notification
  useEffect(() => {
    if (readyTools.length > 0 && !insightNotification) {
      const primaryTool = readyTools[0];
      // Map backend tool to availableCard with compute method
      const matchingCard = availableCards.find(card => card.id === primaryTool.id);
      const toolForNotification = matchingCard || primaryTool;

      setInsightNotification({
        tool: toolForNotification,
        message: `🎯 ${primaryTool.name || primaryTool.label || 'Framework tool'} is ready!`,
        description: primaryTool.description
      });
    }
    if (readyTools.length === 0 && insightNotification) {
      setInsightNotification(null);
    }
  }, [readyTools, insightNotification, availableCards]);

  useEffect(() => {
    if (!toolCelebration) return;
    playCelebrationTone();
    launchConfetti();
    addToast(`🎉 ${toolCelebration.name || 'Framework tool'} is now ready`, 'success');
  }, [toolCelebration]);

  // Track tool usage for better chat integration
  useEffect(() => {
    if (activeToolResult && conversationData && availableCards.length > 0) {
      const tool = availableCards.find(card => card.id === activeToolResult);
      if (tool) {
        const results = tool.compute(conversationData);
        const newToolContext = {
          toolId: activeToolResult,
          toolName: tool.label,
          results: results,
          framework: 'General',
          context: conversationData,
          timestamp: Date.now()
        };
        
        // Safety check to prevent React error #31
        if (typeof newToolContext.results === 'object' && newToolContext.results !== null) {
          // Ensure results can be safely serialized
          try {
            JSON.stringify(newToolContext.results);
          } catch (e) {
            return;
          }
        }
        setToolContext(newToolContext);
        
        // Clear tool context after 5 minutes to avoid stale context
        const timeout = setTimeout(() => {
          setToolContext(null);
        }, 5 * 60 * 1000);
        
        return () => clearTimeout(timeout);
      }
    }
  }, [activeToolResult, conversationData, availableCards]);
  
  // Field label utility function
  const getFieldLabel = (key) => {
    const labels = {
      revenue: '💰 Revenue',
      time_spent: '⏱️ Hours/Week', 
      hours_per_week: '⏱️ Hours/Week',
      working_hours: '⏱️ Hours/Week',
      team_size: '👥 Team Size',
      employees: '👥 Team Size',
      arr: '💰 ARR',
      monthly_revenue: '💰 Monthly Revenue',
      pain_points: '🎯 Pain Points',
      business_stage: '📈 Business Stage',
      challenges: '🎯 Challenges',
      goals: '🎯 Goals',
      industry: '🏭 Industry',
      location: '📍 Location'
    };
    return labels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  
  // Context detection indicators for visual feedback (moved to insights tab only)
  const contextIndicators = useMemo(() => {
    const indicators = [];
    
    if (conversationData.revenue || conversationData.arr || conversationData.monthly_revenue) {
      const revenue = conversationData.revenue?.[0] || conversationData.arr?.[0] || conversationData.monthly_revenue?.[0] || '';
      indicators.push({ type: 'revenue', label: 'Revenue detected', value: revenue, icon: '💰' });
    }
    if (conversationData.time_spent || conversationData.hoursPerWeek || conversationData.working_hours || conversationData.hours_per_week) {
      const hours = conversationData.time_spent?.[0] || conversationData.hoursPerWeek?.[0] || conversationData.working_hours?.[0] || conversationData.hours_per_week?.[0] || '';
      indicators.push({ type: 'hours', label: 'Hours detected', value: hours, icon: '⏰' });
    }
    if (conversationData.team_size) {
      const team = conversationData.team_size?.[0] || '';
      indicators.push({ type: 'team', label: 'Team size detected', value: team, icon: '👥' });
    }
    if (conversationData.overworked || conversationData.pain_points) {
      indicators.push({ type: 'overwork', label: 'Overwork detected', value: 'High stress', icon: '🔥' });
    }
    if (conversationData.business_stage || conversationData.growth_stage) {
      const stage = conversationData.business_stage?.[0] || conversationData.growth_stage?.[0] || '';
      indicators.push({ type: 'stage', label: 'Growth stage detected', value: stage, icon: '🚀' });
    }
    
    return indicators;
  }, [conversationData]);
  
  // Debounced context extraction for performance (moved after conversationData is defined)
  const debouncedContextExtraction = useCallback(
    debounce((message) => {
      const content = String(message?.content || '').trim();
      if (content.length < 10) return;
      
      // Wrap async logic in IIFE
      (async () => {
        try {
          // Step 1: Extract generic context (revenue, team_size, etc.)
          const genericContext = computeConversationContext([message], coachingType, vertical) || {};
          
          // Step 2: AI-powered framework context extraction (dynamic, no hardcoding!)
          let frameworkContext = {};
          if (backendCompanyId && lockedTools.length > 0) {
            try {
              // Get all framework needs that aren't already satisfied
              const frameworkNeeds = lockedTools
                .filter(tool => tool.needs && tool.needs.length > 0)
                .map(tool => ({
                  name: tool.name,
                  needs: tool.needs,
                  missing: tool.missing || []
                }));
              
              if (frameworkNeeds.length > 0) {
                
                const response = await fetch(`${API_V1_BASE}/extract-framework-context`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'X-Company-Id-Slug': backendCompanyId
                  },
                  body: JSON.stringify({
                    message: content,
                    frameworks: frameworkNeeds
                  })
                });
                
                if (response.ok) {
                  const data = await response.json();
                  frameworkContext = data.context || {};
                }
              }
            } catch (aiError) {
            }
          }
          
          // Merge generic + AI-extracted framework context
          const allContext = { ...genericContext, ...frameworkContext };
          
          // Convert context to the expected format (arrays)
          Object.entries(allContext).forEach(([key, value]) => {
            if (value && value !== '') {
              setConversationContext(prev => ({ 
                ...(prev || {}), 
                [key]: Array.isArray(value) ? value : [value] 
              }));
            }
          });
        } catch (e) {
        }
      })();
    }, 500), // 500ms debounce
    [coachingType, backendCompanyId, lockedTools]
  );
  
  const visibleCards = useMemo(() => 
    availableCards.filter(card => card.visibleWhen(conversationData)), 
    [availableCards, conversationData]
  );
  const lockedCards = useMemo(() => 
    availableCards.filter(card => !card.visibleWhen(conversationData)), 
    [availableCards, conversationData]
  );
  const visibleLocked = useMemo(() => 
    showAllLocked ? lockedCards : lockedCards.slice(0, 4), 
    [showAllLocked, lockedCards]
  );
  
  const responseBufferRef = useRef('');
  const bufferTimerRef = useRef(null);
  // Deduplicate user finals across multiple event sources
  const lastUserFinalRef = useRef({ text: '', ts: 0 });
  // Buffer for batching voice fragments
  const voiceFragmentBufferRef = useRef({ fragments: [], timer: null, lastFragmentTime: 0 });
  const FRAGMENT_BATCH_DELAY_MS = 1500; // Wait 1.5s after last fragment before sending

  const saveUserFinalToHistoryAndQuery = (finalText) => {
    const text = String(finalText || '').trim();
    if (text.length < 3) return;
    const now = Date.now();
    const last = lastUserFinalRef.current;
    if (last && last.text === text && now - (last.ts || 0) < 2000) {
      return; // recently saved same line from another event
    }
    
    // Batch voice fragments that come close together
    const buffer = voiceFragmentBufferRef.current;
    buffer.fragments.push(text);
    buffer.lastFragmentTime = now;
    
    // Clear existing timer
    if (buffer.timer) {
      clearTimeout(buffer.timer);
    }
    
    // Set timer to process batched fragments
    buffer.timer = setTimeout(() => {
      const combinedText = buffer.fragments.join(' ').trim();
      buffer.fragments = [];
      buffer.timer = null;
      
      if (combinedText.length >= 3) {
        lastUserFinalRef.current = { text: combinedText, ts: Date.now() };
        setRecentUserTranscripts(prev => [...prev.slice(-2), combinedText]);
        processBatchedVoiceQuery(combinedText);
      }
    }, FRAGMENT_BATCH_DELAY_MS);
  };

  const processBatchedVoiceQuery = (combinedText) => {
    const now = Date.now();
    const userMessage = {
      role: 'user',
      content: combinedText,
      timestamp: now,
      type: 'voice',
      id: `voice-${now}-${Math.random()}`
    };
    setMessages(prev => [...prev, userMessage]);
    
    // Process voice input for insights
    processMessageForInsights(userMessage, 'user');
    runInsightsFor(combinedText);

    // Always send voice transcripts to backend for RAG query
    // VAPI is only used for voice transcription, not function calling
    // The widget handles all backend queries to ensure accurate document-based answers
    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    // Resolve audience for voice queries (same as chat)
    const voiceAudience = resolveAudience();

    // Use same session ID as text for framework state continuity
    const sid = getSessionId();
    
    fetch(api.query(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        company_id_slug: getBackendCompanyId(companyIdSlug),
        session_id: sid, // CRITICAL: Include session_id for framework state persistence
        query: combinedText,
        original_question: combinedText, // Send original question for framework execution
        search_query: combinedText, // For document search
        vertical,
        audience: voiceAudience, // Include audience parameter for proper fallback messaging
        context: conversationContext, // Include extracted context
        user_email: userEmail || undefined // Include email for team member tracking (if provided in URL)
      }),
      signal: controller.signal
      })
      .then(res => {
        clearTimeout(timeoutId);
        return res.json();
    })
      .then(data => {
        if (data && data.answer) {
          // Check if this is a fallback response (not from documents) - same logic as chat
          const isFallback = data.fallback_reason || data.pipeline === 'fallback' || data.pipeline === 'fallback_chat' || data.pipeline === 'fallback_rag';
          
          // Enhanced source handling with better debugging
          const norm = normalizeSources(data);
          // If fallback, ignore sources - they're not relevant
          const effectiveSources = isFallback ? [] : norm;
          const hasCoachDocs = !isFallback && hasCoachPreferredSource(effectiveSources);
          
          // Strip "From your uploaded documents:" prefix if present
          let cleanAnswer = data.answer;
          cleanAnswer = cleanAnswer.replace(/^From your uploaded documents:\s*/i, '');
          cleanAnswer = cleanAnswer.replace(/^From your coach's materials:\s*/i, '');
          // Remove the fallback message about asking coach to upload documents
          cleanAnswer = cleanAnswer.replace(/I don't have specific information about.*?from your coach's materials\.\s*/i, '');
          cleanAnswer = cleanAnswer.replace(/Try asking your coach to upload documents about this topic, or rephrase your question\.\s*/i, '');
          cleanAnswer = cleanAnswer.replace(/I don't have specific information about.*?from your uploaded documents\.\s*/i, '');
          cleanAnswer = cleanAnswer.trim();
          
          // If we have a VAPI assistant transcript, prefer that over backend fallback
          if (isFallback && recentAssistantTranscripts.length > 0) {
            const vapiTranscript = recentAssistantTranscripts[recentAssistantTranscripts.length - 1];
            if (vapiTranscript && vapiTranscript.trim().length > 10) {
              cleanAnswer = vapiTranscript.trim();
              // Don't mark as fallback if we have a good VAPI transcript
              // effectiveSources = norm; // Use sources if available
            }
          }
          
          // Calculate confidence level - USE BACKEND'S CONFIDENCE IF PROVIDED
          const getConfidenceLevel = (backendConfidence, sources, hasCoachDocs, isFallback) => {
            // PRIORITY 1: Use backend's confidence if provided (e.g., for framework execution)
            if (backendConfidence && ['low', 'medium', 'high'].includes(backendConfidence.toLowerCase())) {
              return backendConfidence.toLowerCase();
            }
            
            // PRIORITY 2: Calculate based on sources and fallback status
            if (isFallback) {
              return 'low'; // Always low confidence for fallback responses
            }
            if (hasCoachDocs && sources && sources.length > 0) {
              return 'high'; // High confidence: using uploaded coach docs
            } else if (sources && sources.length > 0) {
              return 'medium'; // Medium confidence: has sources but not coach docs
            } else {
              return 'low'; // Low confidence: no sources, general knowledge
            }
          };
          
          const confidenceLevel = getConfidenceLevel(data.confidence, effectiveSources, hasCoachDocs, isFallback);
          
          // If this is a fallback and we have a VAPI transcript, don't save the backend response
          // The VAPI transcript will be saved when the call ends
          if (isFallback && recentAssistantTranscripts.length > 0) {
            const vapiTranscript = recentAssistantTranscripts[recentAssistantTranscripts.length - 1];
            if (vapiTranscript && vapiTranscript.trim().length > 10) {
              // Skip saving backend fallback - VAPI transcript is better
              return;
            }
          }
          
          const assistantMsg = {
            role: 'assistant',
            content: cleanAnswer, // No inline source attribution - sources shown separately
            timestamp: Date.now(),
            type: 'voice_rag',
            sources: effectiveSources, // Use empty array if fallback
            usesCoachDocs: hasCoachDocs && !isFallback, // Never true for fallback
            fallback: isFallback, // Mark as fallback based on backend flag
            pipeline: data.pipeline,
            confidence: confidenceLevel, // Add confidence level
            id: `assistant-${Date.now()}`
          };
          setMessages(prev => [...prev, assistantMsg]);
          
          // Run insights for voice responses too
          runInsightsFor(cleanAnswer);
        }
      })
      .catch(err => {
        clearTimeout(timeoutId);
        console.error('[Voice Debug] Error:', err);
        if (err.name === 'AbortError') {
          setVoiceError('Request timed out. Please try again.');
        } else {
          setVoiceError(professionalErrorHandler(err));
        }
      });
  };

  const calculateSimilarity = (a, b) => {
    if (!a || !b) return 0;
    const s1 = a.toLowerCase();
    const s2 = b.toLowerCase();
    const min = Math.min(s1.length, s2.length);
    if (min === 0) return 0;
    let same = 0;
    for (let i = 0; i < min; i++) if (s1[i] === s2[i]) same++;
    return same / Math.max(s1.length, s2.length);
  };

  // Smooth voice transcript processing (dedup + finalization)
  const processVoiceTranscript = (transcript, isFinal = false) => {
    const text = String(transcript || '').trim();
    if (text.length < 5) return; // Skip very short fragments

    if (isFinal) {
      const lastFinal = (lastUserFinalRef.current && lastUserFinalRef.current.text) || '';
      const similarity = calculateSimilarity(text, lastFinal);
      if (similarity > 0.7) return; // Skip near-duplicates
      // Save to history and query backend
      saveUserFinalToHistoryAndQuery(text);
      setLiveTranscript('');
    } else {
      setLiveTranscript(text);
    }
  };

  const isFragment = (sentence) => {
    const fragmentPatterns = [
      /^(and|but|or|so|because|since|while|although)\b/i,
      /\w+\s+and\s*$/i,
      /\b(both|either|neither)\s*$/i,
    ];
    return fragmentPatterns.some((p) => p.test(sentence));
  };

  const ensureProperEndings = (text) => {
    const t = (text || '').trim();
    if (!t) return t;
    return /[.!?]$/.test(t) ? t : `${t}.`;
  };

  const removeIncompleteFragments = (text) => {
    const sentences = (text || '').split(/[.!?]+/);
    const complete = sentences
      .map((s) => s.trim())
      .filter((s) => s.length > 10 && !isFragment(s));
    return ensureProperEndings(complete.join('. '));
  };

  const cleanTextEntry = (entry) => {
    // Preserve original content for history; only trim whitespace
    const content = (entry.content || '').trim();
    return { ...entry, content };
  };

  const shouldDeduplicate = (newEntry, lastEntry) => {
    if (!lastEntry) return false;
    if (newEntry.role !== lastEntry.role) return false; // Allow alternating user/assistant turns
    const timeDiff = Date.now() - (lastEntry.timestamp || Date.now());
    if (timeDiff < 30000) {
      const a = (newEntry.content || '').trim();
      const b = (lastEntry.content || '').trim();
      if (!a || !b) return false;
      if (a === b) return true;
      if (a.includes(b) || b.includes(a)) return true;
      if (calculateSimilarity(a, b) > 0.8) return true;
    }
    return false;
  };

  const addToSessionHistory = (entry) => {
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      const cleaned = cleanTextEntry(entry);
      if (!cleaned.content || cleaned.content.trim() === '') return prev;
      if (shouldDeduplicate(cleaned, last)) return prev;
      return [...prev, cleaned];
    });
  };

  const appendMessage = (role, content, extras = {}) => {
    addToSessionHistory({ role, content, timestamp: Date.now(), ...extras });
  };
  const [inputText, setInputText] = useState('');
  const [recentAssistantTranscripts, setRecentAssistantTranscripts] = useState([]);
  const [recentUserTranscripts, setRecentUserTranscripts] = useState([]);
  const lastAssistantLiveRef = useRef('');
  // Voice auto-save (simple toggle for history persistence during voice)
  const [voiceAutoSave, setVoiceAutoSave] = useState(() => {
    try {
      const v = localStorage.getItem('ov_auto_save_voice');
      return v === null ? true : v === 'true';
    } catch (e) { return true; }
  });
  // Transcript buffering for more coherent voice saves
  const [transcriptBuffer, setTranscriptBuffer] = useState('');
  const bufferTimeoutRef = useRef(null);
  // Voice auto-save toggle (persisted)
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(() => {
    try {
      const v = localStorage.getItem('ov_auto_save_voice');
      return v === null ? true : v === 'true';
    } catch (e) { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('ov_auto_save_voice', String(voiceAutoSave)); } catch (e) {}
  }, [voiceAutoSave]);
  const [toasts, setToasts] = useState([]);
  
  // Toast notification function (defined early to avoid temporal dead zone)
  const addToast = (message, variant='info') => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
    setToasts((prev) => [...prev, { id, message, variant }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter(t => t.id !== id));
    }, 2500);
  };
  
  // State management

  // Feature State
  const [docs, setDocs] = useState([]);
  const [insights, setInsights] = useState(null);
  const [capturedInsights, setCapturedInsights] = useState([]);
  
  // Business tools for PAI3/OpsIQ/Business verticals (must be after docs, messages, conversationContext)
  // NOTE: messages and conversationContext are declared earlier in the component (lines ~1229, ~1255)
  const businessTools = useMemo(() => {
    if (vertical !== 'pai3' && vertical !== 'opsiq' && vertical !== 'business') {
      return null; // Use default tools for other verticals
    }
    
    const hasDecisionKeywords = messages.some(m => 
      /\b(should i|decide|decision|options|choice|choose|versus|vs\.|or should)\b/i.test(m.content || '')
    );
    
    const baseTools = [
      {
        id: 'action-items',
        label: 'Action Items',
        description: 'Extract tasks and todos from your conversations',
        icon: <CheckSquare size={18} strokeWidth={2} />,
        ready: messages.length > 0,
        needs: messages.length === 0 ? ['Start a conversation to extract action items'] : [],
        visibleWhen: () => true, // Always show
      },
      {
        id: 'business-insights',
        label: 'Business Insights',
        description: 'Visualize trends from your conversation data',
        icon: <LineChart size={18} strokeWidth={2} />,
        ready: !!(conversationContext.revenue || conversationContext.team_size || conversationContext.hours_per_week),
        needs: ['Mention revenue, team size, or hours in conversation'],
        visibleWhen: () => true, // Always show (but locked until ready)
      },
      {
        id: 'decision-helper',
        label: 'Decision Analysis',
        description: 'Analyze pros/cons of decisions discussed',
        icon: <Scale size={18} strokeWidth={2} />,
        ready: hasDecisionKeywords,
        needs: ['Mention a decision or choice in conversation'],
        visibleWhen: () => true, // Always show (but locked until ready)
      }
    ];
    
    // Filter out document-summary for OpsIQ/Business (users manage docs in dashboard only)
    if (vertical === 'opsiq' || vertical === 'business') {
      return baseTools.filter(tool => tool.id !== 'document-summary');
    }
    
    return baseTools;
  }, [vertical, docs, messages, conversationContext]);

  // Mobile navigation tabs - New structure for MentorMind, legacy for others
  const mobileTabs = useMemo(() => {
    // New MentorMind navigation (4 tabs - Premium Redesign)
    if (isMentorMind) {
      return [
        { id: 'home', label: 'Home' },
        { id: 'sessions', label: 'Sessions' },
        { id: 'progress', label: 'Progress' },
        { id: 'insights', label: 'Insights' }
      ];
    }
    
    // Legacy navigation for other verticals
    const baseTabs = [
      { id: 'chat', label: 'Chat' }, // Combined voice + chat
      { id: 'coach', label: 'Notes', badge: (coachNotes && coachNotes.length > 0) ? coachNotes.length : null },
      { id: 'documents', label: 'Knowledge', badge: (docs && docs.length > 0) ? docs.length : null },
      { id: 'insights', label: (vertical === 'business' || vertical === 'opsiq' || vertical === 'pai3') ? 'Actions' : 'Tools' }
    ];

    return baseTabs;
  }, [docs, coachNotes, vertical, isMentorMind]);
  const captureInsight = (insight) => {
    const entry = { id: `${Date.now()}-${Math.random().toString(36).slice(2,7)}`, timestamp: Date.now(), ...insight };
    setCapturedInsights((prev) => [entry, ...prev].slice(0, 200));
    // removed debug log
    setQuickSuggestions((prev) => [{ type: 'insight', title: `Captured ${entry.type}`, value: entry.value }, ...prev].slice(0, 5));
    // Toast feedback
    addToast(`Added to insights: ${entry.type}`, 'success');
  };
  const categorizeMessage = (text) => {
    const t = (text || '').toLowerCase();
    // Client-focused categories
    if (/\b(i want to|i'd like to|my goal is|i aim to|i plan to|i would like to)\b/i.test(t)) return 'Client Goals';
    if (/\b(i'm struggling with|i struggle with|my challenge is|problem is|blocked by)\b/i.test(t)) return 'Challenges';
    if (/\b(i achieved|i completed|i finished|i accomplished|i did)\b/i.test(t)) return 'Progress';
    if (/\b(i realized|i discovered|i learned|i noticed)\b/i.test(t)) return 'Self-Awareness';
    if (/\bmy fear is|i'm afraid|i fear|imposter|impostor|perfectionism\b/i.test(t)) return 'Mindset';
    if (/\b(i'm good at|my strength is|i'm strong at)\b/i.test(t)) return 'Strengths';
    // Universal categories
    if (/(revenue|arr|profit|cost|margin|cash|financial)/i.test(t)) return 'financial';
    if (/(team|hire|hiring|employee|manager|org|headcount|people)/i.test(t)) return 'team';
    if (/(client|customer|churn|retention|nps|support|lead)/i.test(t)) return 'clients';
    if (/(goal|objective|okr|kpi|target|milestone)/i.test(t)) return 'goals';
    if (/(process|operation|ops|workflow|system|sop|procedure)/i.test(t)) return 'operational';
    if (/(market|competitor|competition|positioning|segment|industry)/i.test(t)) return 'market';
    if (/(challenge|blocker|issue|problem|risk|bottleneck)/i.test(t)) return 'challenges';
    if (/(progress|update|done|completed|achieved)/i.test(t)) return 'progress';
    if (/(strategy|plan|roadmap|approach)/i.test(t)) return 'strategy';
    if (/(growth|scale|scaling|expand|expansion)/i.test(t)) return 'growth';
    if (/(mindset|motivation|habits|discipline|stress|balance|life)/i.test(t)) return 'mindset';
    return 'general';
  };
  // Vertical-specific insight parsing helpers
  const parseCoachingMetrics = (message) => {
    const insights = [];
    const text = String(message || '').toLowerCase();
    const revenueMatch = text.match(/\$?\s?\d+(?:\.\d+)?\s*[mk]?\s*(revenue|arr|sales)/i);
    if (revenueMatch) {
      insights.push({ type: 'financial', value: revenueMatch[0] });
    }
    const teamMatch = text.match(/\b\d+\s*(people|employees|team)\b/i);
    if (teamMatch) {
      insights.push({ type: 'team', value: teamMatch[0] });
    }
    if (/(struggling|challenge|problem|stuck|difficult)/i.test(text)) {
      insights.push({ type: 'challenge', value: String(message || '').trim() });
    }
    return insights;
  };

  const parseBusinessOperations = (message) => {
    const insights = [];
    const text = String(message || '').toLowerCase();
    if (/(process|procedure|workflow|efficiency)/i.test(text)) {
      insights.push({ type: 'process', value: 'Process optimization opportunity' });
    }
    if (/(customer|client|service|support)/i.test(text)) {
      insights.push({ type: 'customer', value: 'Customer experience focus' });
    }
    return insights;
  };

  const parseHVACMetrics = (message) => {
    const insights = [];
    const text = String(message || '').toLowerCase();
    if (/(safety|danger|hazard|emergency|lockout)/i.test(text)) {
      insights.push({ type: 'safety', value: 'Safety protocol required', priority: 'high' });
    }
    if (/(maintenance|repair|service|broken|fault)/i.test(text)) {
      insights.push({ type: 'maintenance', value: 'Equipment attention needed' });
    }
    if (/(permit|inspection|code|regulation)/i.test(text)) {
      insights.push({ type: 'compliance', value: 'Regulatory requirement' });
    }
    return insights;
  };

  const processVerticalInsights = (message, v) => {
    switch (v) {
      case 'mentormind':
        return parseCoachingMetrics(message);
      case 'hvac':
        return parseHVACMetrics(message);
      case 'business':
      default:
        return parseBusinessOperations(message);
    }
  };

  const updateQuickSuggestions = (insightsList, v) => {
    const suggestionsMap = {
      mentormind: [
        'Prioritize this week’s top outcomes',
        'Outline a 3-step action plan',
        'Draft a simple accountability cadence',
        'Identify and clear the biggest bottleneck'
      ],
      business: [
        'Optimize this process',
        'Create standard procedure',
        'Analyze efficiency metrics',
        'Document best practices'
      ],
      hvac: [
        'Review safety protocols',
        'Check maintenance schedule',
        'Verify compliance requirements',
        'Update safety documentation'
      ]
    };
    setQuickSuggestions(suggestionsMap[v] || suggestionsMap.business);
  };
  const routeToInsights = (text, source) => {
    const content = (text || '').trim();
    if (!content) return;
    // Universal categorization
    const category = categorizeMessage(content);
    captureInsight({ type: category, value: content, source });
    // Vertical-specific extractions
    const verticalFindings = processVerticalInsights(content, vertical);
    verticalFindings.forEach((ins) => captureInsight({ ...ins, source, timestamp: Date.now() }));
    if (verticalFindings.length > 0) updateQuickSuggestions(verticalFindings, vertical);
  };


  // Explicit insights hook: user message → structured context → UI suggestions
  const processMessageForInsights = useCallback((message, role) => {
    if (role !== 'user') return;
    debouncedContextExtraction(message);
  }, [debouncedContextExtraction]);

  // Core state used by effects below (hoisted to avoid TDZ)
  const [quickSuggestions, setQuickSuggestions] = useState([]);
  const [missingDataItems, setMissingDataItems] = useState([]);
  const [coachFrameworks, setCoachFrameworks] = useState([]);
  const [showCoachDocsHint, setShowCoachDocsHint] = useState(true);
  // Contextual suggestions derived from recent conversation
  // (moved to main state section above)

  // Keep conversation context up to date for Smart Context panel
  // Moved below coachingType initialization to avoid TDZ errors

  // Extract coach frameworks when docs change
  useEffect(() => {
    try {
      if (docs && docs.length > 0) {
        const fw = extractCoachFrameworks(docs) || [];
        setCoachFrameworks(Array.isArray(fw) ? fw : (fw.frameworks || []));
        // removed debug log
      } else {
        setCoachFrameworks(null);
      }
    } catch (_) {}
  }, [docs]);

  // Removed: Framework-based quick suggestions from chat
  // Frameworks are now only accessible via the Tools tab for cleaner chat UX
  // Framework extraction is still used for prompt enhancement (AI style matching)

  // Safe placement after coachingType is declared
  useEffect(() => {
    try {
      const schema = INSIGHT_SCHEMAS[coachingType] || INSIGHT_SCHEMAS.business;
      const latest = messages[messages.length - 1]?.content || '';
      const { updates } = engineParse(latest, schema);
      const merged = engineMerge(conversationContext, updates);
      setConversationContext(merged);
      setMissingDataItems(engineNext(merged, schema, 2));
      const triggers = engineMatch(latest, schema);
      if (Array.isArray(triggers) && triggers.length > 0) {
        // Subtle highlight or suggestion: set quick suggestions toward tool usage
        setQuickSuggestions(prev => {
          const add = triggers.map(t => `Run ${t} analysis`);
          return Array.from(new Set([...(prev || []), ...add])).slice(0, 5);
        });
      }
    } catch (_) {}
  }, [messages, coachingType]);

  // Show upload hint once per session when there are no docs and chat has started
  useEffect(() => {
    if (!companyIdSlug) return;
    const key = `ov__docs_hint_shown__${getBackendCompanyId(companyIdSlug)}`;
    const shown = sessionStorage.getItem(key);
    if (audience === 'coach') {
      if (!shown && showCoachDocsHint && Array.isArray(docs) && docs.length === 0 && messages.length > 0) {
        sessionStorage.setItem(key, '1');
        setShowCoachDocsHint(false);
      }
    }
  }, [docs, messages.length, showCoachDocsHint]);

  // Continuation helper
  const requestContinuation = async (lastAssistantText) => {
    try {
      setIsLoading(true);
      const contextSnippet = messages.slice(-6).map(m => `${m.role}: ${m.content}`).join('\n');
      // Add timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      const response = await fetch(api.query(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_id_slug: getBackendCompanyId(companyIdSlug),
          query: `Continue your previous response. Build on this assistant message: "${lastAssistantText}". Keep the same context and provide next concrete steps.\nContext:\n${contextSnippet}`,
          vertical,
          context: conversationContext, // Include extracted context
          user_email: userEmail || undefined // Include email for team member tracking (if provided in URL)
        }),
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      const data = await response.json();
      const assistantMessage = {
        role: 'assistant',
        content: data.answer || 'Continuing…',
        timestamp: Date.now(),
        sources: data.sources || []
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (e) {
      clearTimeout(timeoutId);
      console.error('Continuation failed:', e);
      if (e.name === 'AbortError') {
        setError('Request timed out. Please try again.');
      } else {
      setError('Failed to continue. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };
  // Continuation detection helpers (universal across verticals)
  const isAffirmativeContinuation = (text) => {
    const t = String(text || '').trim();
    const phrases = [
      'yes','y','yeah','yep','yup','sure','please','okay','ok','continue','go on','keep going',
      'tell me more','more details','continue please','yes please','continue with tips','more tips','what else','any other tips'
    ];
    return phrases.some(p => t.toLowerCase().startsWith(p));
  };
  const invitesContinuation = (text) => {
    const t = String(text || '');
    return /(would you like (me )?to continue|should i continue|want me to go on|more details\??|continue\??|shall i proceed)/i.test(t);
  };
  const getLastAssistantMessage = () => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]?.role === 'assistant' && (messages[i]?.content || '').trim()) {
        return messages[i].content;
      }
    }
    return null;
  };
  const getCurrentInsightCount = () => {
    const availableCards = createInsightRegistry(vertical, coachingType || 'business', companyIdSlug);
    const visibleCards = availableCards.filter(card => card.visibleWhen(conversationContext || {}));
    return visibleCards.length;
  };
  const getRecentInsights = (n=5) => capturedInsights.slice(0, n);
  const [searchQuery, setSearchQuery] = useState('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [interactionCount, setInteractionCount] = useState(0);
  const [copyFeedback, setCopyFeedback] = useState('');

  // Smart keyword-based suggestions (chat + voice)
  const [chatSuggestions, setChatSuggestions] = useState([]);
  const [voiceSuggestions, setVoiceSuggestions] = useState([]);
  const [expandedToolIds, setExpandedToolIds] = useState(new Set());
  const [isListening, setIsListening] = useState(false);
  const [sessionMetrics, setSessionMetrics] = useState({
    startTime: Date.now(),
    interactions: 0,
    timeSaved: 0,
    efficiency: 85
  });
  
  // Voice and transcript state variables
  const [liveTranscript, setLiveTranscript] = useState('');
  const [isAssistantSpeaking, setIsAssistantSpeaking] = useState(false);
  const [isStartingVoice, setIsStartingVoice] = useState(false);
  const [hasVapi, setHasVapi] = useState(false);
  const [voiceError, setVoiceError] = useState(null);
  const [volume, setVolume] = useState(50);

  // When a voice call ends on OpsIQ+PAI3 mobile, switch to Type mode for review
  useEffect(() => {
    if (!isOpsMobileVertical) return;
    if (!isMobile) return;
    if (mobileMode !== 'talk') return;
    if (isRecording) return;
    if (isAssistantSpeaking) return;
    // If we have any transcripts collected, assume a voice turn just completed
    if ((recentUserTranscripts && recentUserTranscripts.length > 0) || (recentAssistantTranscripts && recentAssistantTranscripts.length > 0)) {
      try { localStorage.setItem(mobileModeStorageKey, 'type'); } catch (_) {}
      setMobileMode('type');
    }
  }, [
    isOpsMobileVertical,
    isMobile,
    mobileMode,
    isRecording,
    isAssistantSpeaking,
    recentUserTranscripts,
    recentAssistantTranscripts,
    mobileModeStorageKey,
  ]);

  const buildKeywordSuggestions = (text, type, v) => {
    const t = String(text || '').toLowerCase();
    const push = (arr, cond, obj) => { if (cond && obj && obj.toolId && obj.label) arr.push(obj); };
    const tools = (COACHING_INSIGHTS[type]?.tools || []);
    const byId = (id) => tools.find(t => t.id === id) || { id, name: id, icon: '🔧' };

    const out = [];
    // HEALTH
    if (type === 'health') {
      push(out, /(tired|exhausted|no energy|fatigue|drained|sluggish|sleepy|low energy|motivation|burnout)/i.test(t), { label: '💤 Build energy routine', toolId: 'program', reason: 'energy' });
      push(out, /(not losing|plateau|stuck|same weight|scale won\'t budge|diet not working)/i.test(t), { label: '📊 Recalculate plan', toolId: 'nutrition', reason: 'plateau' });
      push(out, /(no time|busy|can\'t workout|cant workout|schedule|too busy|work too much)/i.test(t), { label: '⚡ Quick workouts', toolId: 'program', reason: 'time' });
      push(out, /(pain|injury|hurt|sore|can\'t move|cant move)/i.test(t), { label: '🩹 Injury-safe program', toolId: 'program', reason: 'injury' });
    }
    // BUSINESS
    if (type === 'business') {
      push(out, /(hiring|need help|can\'t do everything|cant do everything|drowning|too much work)/i.test(t), { label: '🪜 Plan next hire', toolId: 'growth', reason: 'hiring' });
      push(out, /(not growing|stuck|plateau|same revenue|flat|stagnant)/i.test(t), { label: '🔍 Find bottleneck', toolId: 'bottleneck', reason: 'growth' });
      push(out, /(systems|process|chaos|disorganized|manual work)/i.test(t), { label: '⚙️ Build systems', toolId: 'workflow', reason: 'systems' });
    }
    // ECOMMERCE
    if (type === 'ecommerce') {
      push(out, /(ads not working|losing money|roas|facebook ads|google ads|marketing spend)/i.test(t), { label: '🎯 Fix ad spend', toolId: 'ppc', reason: 'ads' });
      push(out, /(low conversion|visitors not buying|traffic but no sales|bounce rate)/i.test(t), { label: '📊 Boost conversions', toolId: 'sku', reason: 'conversion' });
      push(out, /(inventory|fulfillment|operations|shipping|returns|supply chain)/i.test(t), { label: '📦 Streamline ops', toolId: 'ops', reason: 'ops' });
      push(out, /(amazon|fba|marketplace|competition|pricing)/i.test(t), { label: '🏆 Competitive strategy', toolId: 'ops', reason: 'competitive' });
    }
    // SALES
    if (type === 'sales') {
      push(out, /(lost deal|objection|pushback|price)/i.test(t), { label: '🛡️ Handle objections', toolId: 'objections', reason: 'objection' });
      push(out, /(pipeline|forecast|deals stuck|quota|numbers|funnel)/i.test(t), { label: '📈 Review pipeline', toolId: 'pipeline', reason: 'pipeline' });
      push(out, /(cold calls|prospecting|leads|outreach|appointments)/i.test(t), { label: '📞 Script builder', toolId: 'script', reason: 'prospecting' });
      push(out, /(closing|follow up|negotiation|final stages)/i.test(t), { label: '🤝 Close deals', toolId: 'pipeline', reason: 'closing' });
    }
    // LIFE
    if (type === 'life') {
      push(out, /(don\'t know what|confused|unclear|purpose|direction|lost|stuck)/i.test(t), { label: '🎯 Clarify goals', toolId: 'clarity', reason: 'clarity' });
      push(out, /(bad habits|routine|procrastination|discipline|consistency)/i.test(t), { label: '🔄 Build habits', toolId: 'keystone', reason: 'habits' });
      push(out, /(big decision|should i|what if|choice|crossroads|uncertain)/i.test(t), { label: '⚖️ Decision framework', toolId: 'balance', reason: 'decision' });
      push(out, /(balance|stress|overwhelmed|priorities|time management)/i.test(t), { label: '⚖️ Life balance', toolId: 'balance', reason: 'balance' });
    }
    // CAREER
    if (type === 'career') {
      push(out, /(resume|job search|applications|linkedin|portfolio)/i.test(t), { label: '📄 Boost resume', toolId: 'resume', reason: 'resume' });
      push(out, /(interview|nervous|preparation|questions|salary negotiation)/i.test(t), { label: '🎤 Interview prep', toolId: 'interview', reason: 'interview' });
      push(out, /(career change|next step|promotion|advancement|growth)/i.test(t), { label: '🛤️ Plan path', toolId: 'path', reason: 'path' });
      push(out, /(skills|learning|development|certification|training)/i.test(t), { label: '📚 Skill gaps', toolId: 'path', reason: 'skills' });
    }
    // RELATIONSHIP
    if (type === 'relationship') {
      push(out, /(communication|argue|fight|misunderstand|conflict|tension)/i.test(t), { label: '💬 Communication tools', toolId: 'communication', reason: 'communication' });
      push(out, /(boundaries|toxic|drama|manipulation|saying no)/i.test(t), { label: '🔗 Set boundaries', toolId: 'attachment', reason: 'boundaries' });
      push(out, /(connection|distant|routine|spark|intimacy|quality time)/i.test(t), { label: '💕 Build rituals', toolId: 'rituals', reason: 'rituals' });
      push(out, /(dating|single|relationships|love|partner)/i.test(t), { label: '❤️ Dating strategy', toolId: 'communication', reason: 'dating' });
    }
    // FINANCIAL
    if (type === 'financial') {
      push(out, /(debt|credit cards|paying off|loans|owe money|financial stress)/i.test(t), { label: '📉 Debt strategy', toolId: 'debt', reason: 'debt' });
      push(out, /(budget|spending|money management|expenses|cash flow)/i.test(t), { label: '💳 Spending plan', toolId: 'spending', reason: 'budget' });
      push(out, /(investing|retirement|savings|wealth|financial freedom)/i.test(t), { label: '📈 Investment guide', toolId: 'investing', reason: 'investing' });
      push(out, /(emergency fund|insurance|protection|risk|financial security)/i.test(t), { label: '🛡️ Financial safety', toolId: 'spending', reason: 'safety' });
    }
    // LEADERSHIP
    if (type === 'leadership') {
      push(out, /(decisions|choices|what should|leadership dilemma)/i.test(t), { label: '👥 Decision coaching', toolId: 'coaching', reason: 'decision' });
      push(out, /(meetings|productivity|team alignment|culture|morale)/i.test(t), { label: '📅 Team systems', toolId: 'cadence', reason: 'meetings' });
      push(out, /(performance|feedback|difficult conversations|firing)/i.test(t), { label: '💬 Leadership talks', toolId: 'coaching', reason: 'performance' });
    }
    // Vertical specific overrides
    if (v === 'hvac') {
      push(out, /(safety|danger|accident|lockout)/i.test(t), { label: '⚠️ Safety checklist', toolId: 'safety', reason: 'safety' });
      push(out, /(broken|not working|fault|repair)/i.test(t), { label: '🔧 Diagnostic help', toolId: 'diagnostics', reason: 'diagnostics' });
      push(out, /(inspection|code|permit|compliance)/i.test(t), { label: '📜 Compliance check', toolId: 'compliance', reason: 'compliance' });
      push(out, /(maintenance|service|schedule)/i.test(t), { label: '🗓️ PM planner', toolId: 'maintenance', reason: 'maintenance' });
    }
    if (v === 'business') {
      push(out, /(process|inefficient|waste|bottleneck)/i.test(t), { label: '⚙️ Process optimizer', toolId: 'process', reason: 'process' });
      push(out, /(team|productivity|alignment)/i.test(t), { label: '👥 Team booster', toolId: 'team', reason: 'team' });
      push(out, /(customers|service|complaints|retention)/i.test(t), { label: '🤝 Customer experience', toolId: 'cs', reason: 'cx' });
      push(out, /(workflow|automation|systems)/i.test(t), { label: '🔄 Workflow optimizer', toolId: 'workflow', reason: 'workflow' });
    }

    // De-dup by toolId and cap to 2
    const seen = new Set();
    const capped = [];
    for (const s of out) {
      if (!seen.has(s.toolId)) { seen.add(s.toolId); capped.push(s); }
      if (capped.length >= 2) break;
    }
    return capped;
  };

  // Recompute chat suggestions on last user message
  useEffect(() => {
    const lastUser = [...messages].reverse().find(m => m.role === 'user');
    if (!lastUser) { setChatSuggestions([]); return; }
    const sug = buildKeywordSuggestions(lastUser.content, coachingType, vertical);
    setChatSuggestions(sug);
  }, [messages, coachingType, vertical]);

  // Build voice suggestions when call stops
  useEffect(() => {
    if (!isListening) {
      const lastUtterances = (recentUserTranscripts || []).slice(-3).join(' ') || (messages[messages.length-1]?.content || '');
      const sug = buildKeywordSuggestions(lastUtterances, coachingType, vertical);
      setVoiceSuggestions(sug);
    }
  }, [isListening]);

  // Enterprise Insights State
  const [activeToolConversation, setActiveToolConversation] = useState(null);
  // removed duplicate declarations moved above
  const [pendingContinuation, setPendingContinuation] = useState(null);
  const [pendingVoiceContinuation, setPendingVoiceContinuation] = useState(null);

  // Enhanced MentorMind features state
  const [conversationHistory, setConversationHistory] = useState([]);
  const [businessStage, setBusinessStage] = useState('growth');
  const [stageResults, setStageResults] = useState(null);
  const [roiInputs, setRoiInputs] = useState({
    currentRevenue: '',
    timeSpent: '',
    hourlyRate: ''
  });
  const [roiResults, setRoiResults] = useState(null);
  const [efficiencyInputs, setEfficiencyInputs] = useState({
    teamSize: '',
    revenue: ''
  });
  const [efficiencyResults, setEfficiencyResults] = useState(null);

  // Trial State Management
  const [trialState, setTrialState] = useState({
    isTrialUser: false,
    docsUploaded: 0,
    docsRemaining: 3,
    trialExpiry: null,
    hasUploadedDocs: false,
    isLoading: false
  });


  // Business Metrics State
  const [businessMetrics, setBusinessMetrics] = useState({
    arr: '',
    team: '',
    stage: 'growth'
  });

  // Dan Martell Features State
  const [roiCalculator, setRoiCalculator] = useState({
    currentRevenue: '',
    timeSpentOnLowValue: '',
    hourlyRate: '',
    calculatedROI: null
  });
  const [businessHealth, setBusinessHealth] = useState({
    score: 0,
    categories: {
      timeManagement: { score: 0, status: 'needs-work' },
      teamStructure: { score: 0, status: 'needs-work' },
      systemsProcesses: { score: 0, status: 'needs-work' },
      revenueGrowth: { score: 0, status: 'needs-work' },
      leadership: { score: 0, status: 'needs-work' }
    },
    recommendations: []
  });
  const [actionPlans, setActionPlans] = useState([]);
  const [followUpReminders, setFollowUpReminders] = useState([]);
  const [showBusinessHealth, setShowBusinessHealth] = useState(false);

  // Dan Martell targeting
  const isDanMartellWidget = (companyIdSlug === 'coach-dan-martell-demo' || companyIdSlug === 'coach-dan-martell-demo-default');

  // Toast helpers (addToast moved earlier to avoid temporal dead zone errors)

  // Conversation Intelligence state (Dan Martell only)
  // (moved to main state section above)

  // --- Conversation Intelligence helpers (scoped to component) ---
  const parseRevenue = (raw) => {
    if (!raw) return null;
    const str = String(raw).toLowerCase();
    const num = parseFloat(str.replace(/[^0-9.]/g, ''));
    if (Number.isNaN(num)) return null;
    if (str.includes('m') || str.includes('million')) return Math.round(num * 1_000_000);
    if (str.includes('k') || str.includes('thousand')) return Math.round(num * 1_000);
    return Math.round(num);
  };

  const parseBusinessMetrics = (message) => {
    const metrics = { painPoints: [] };
    const text = (message || '').toLowerCase();
    
    // Revenue extraction - improved pattern matching
    const arrMatch = text.match(/(\d+(?:\.\d+)?)\s*(?:million|m|k|thousand)?\s*(?:arr|annual\s*recurring|revenue|per\s*year)/i);
    if (arrMatch) {
      const value = arrMatch[1];
      const multiplier = text.includes('million') || text.includes('m') ? 1000000 : 
                       text.includes('k') || text.includes('thousand') ? 1000 : 1;
      metrics.arr = parseFloat(value) * multiplier;
    }
    
    // Also look for "50000 per year" pattern
    const perYearMatch = text.match(/(\d+(?:\.\d+)?)\s*(?:million|m|k|thousand)?\s*per\s*year/i);
    if (perYearMatch && !metrics.arr) {
      const value = perYearMatch[1];
      const multiplier = text.includes('million') || text.includes('m') ? 1000000 : 
                       text.includes('k') || text.includes('thousand') ? 1000 : 1;
      metrics.arr = parseFloat(value) * multiplier;
    }
    
    // Team size extraction
    const teamMatch = text.match(/(\d+)\s*(?:people|employees|team\s*members|person|team)/i);
    if (teamMatch) metrics.teamSize = parseInt(teamMatch[1], 10);
    
    // Hours extraction
    const hoursMatch = text.match(/(\d+)\s*hours?\s*(?:per\s*)?week/i);
    if (hoursMatch) metrics.weeklyHours = parseInt(hoursMatch[1], 10);
    
    // Pain point detection
    if (text.includes('overwhelmed') || text.includes('too busy') || text.includes('burning out') || text.includes('stressed')) {
      metrics.pain_point = 'overwhelmed';
    } else if (text.includes('bottleneck') || text.includes('stuck') || text.includes('can\'t scale') || text.includes('depends on me')) {
      metrics.pain_point = 'bottleneck';
    } else if (text.includes('meeting') || text.includes('interrupt') || text.includes('decision')) {
      metrics.pain_point = 'decision_fatigue';
    } else if (text.includes('hire') || text.includes('hiring') || text.includes('delegate')) {
      metrics.pain_point = 'hiring_need';
    }
    
    // Additional context
    if (text.includes('scale') || text.includes('grow')) metrics.painPoints.push('scaling');
    if (text.includes('process') || text.includes('system')) metrics.painPoints.push('process_improvement');
    
    return metrics;
  };

  const updateBusinessProfile = (metrics) => {
    if (!metrics || Object.keys(metrics).length === 0) return;
    setBusinessProfile((prev) => ({
            ...prev,
      ...metrics,
      lastUpdated: Date.now()
    }));
    // auto-populate tool inputs
    if (typeof metrics.arr === 'number') {
      setBusinessMetrics((prev) => ({ ...prev, arr: `$${metrics.arr.toLocaleString()}` }));
      setRoiInputs((prev) => ({ ...prev, currentRevenue: `$${metrics.arr.toLocaleString()}` }));
    }
    if (typeof metrics.teamSize === 'number') {
      setBusinessMetrics((prev) => ({ ...prev, team: String(metrics.teamSize) }));
    }
    if (typeof metrics.weeklyHours === 'number') {
      setRoiInputs((prev) => ({ ...prev, timeSpent: String(metrics.weeklyHours) }));
    }
  };

  const suggestTools = (message, metrics) => {
    const suggestions = [];
    const text = (message || '').toLowerCase();
    
    if (text.includes('decision') || text.includes('choose') || text.includes('meeting') || text.includes('interrupt')) {
      suggestions.push({ 
        tool: '1-3-1-framework', 
        icon: '🎯', 
        title: 'Decision Framework 1-3-1', 
        reason: 'Decision-making fatigue detected - build team capacity' 
      });
    }
    
    if (text.includes('hiring') || text.includes('delegate') || text.includes('next hire')) {
      suggestions.push({ 
        tool: 'replacement-ladder', 
        icon: '🪜', 
        title: 'Delegation Ladder', 
        reason: 'Hiring need identified - map your evolution path' 
      });
    }
    
    if (text.includes('overwhelmed') || text.includes('too busy') || text.includes('burning out')) {
      suggestions.push({ 
        tool: 'pain-line-analysis', 
        icon: '📊', 
        title: 'Constraint Finder', 
        reason: 'Stress indicators detected - assess business health' 
      });
    }
    
    if (text.includes('training') || text.includes('teach') || text.includes('show')) {
      suggestions.push({ 
        tool: 'camcorder-method', 
        icon: '📹', 
        title: 'Process Recording Method', 
        reason: 'Training need detected - scale without your time' 
      });
    }
    
    if (text.includes('bottleneck') || text.includes('stuck') || text.includes('can\'t scale') || text.includes('depends on me')) {
      suggestions.push({ 
        tool: 'audit-transfer-fill', 
        icon: '🔄', 
        title: 'Audit-Transfer-Fill Loop', 
        reason: 'Growth constraint detected - implement perpetual delegation cycle' 
      });
    }
    
    // Process improvement suggestions
    if (text.includes('process') || text.includes('system') || text.includes('workflow')) {
      suggestions.push({ 
        tool: 'audit-transfer-fill', 
        icon: '⚙️', 
        title: 'Process Optimization', 
        reason: 'System improvement opportunity detected' 
      });
    }
    
    return suggestions;
  };

  const handleSelectTool = (toolId) => {
    // Stay in the insights tab and show tool results
    setActiveTab('insights');
    
    // Map tool IDs to the correct format
    let mappedToolId = toolId;
    if (toolId === '1-3-1-framework' || toolId === '1-3-1') {
      mappedToolId = '1-3-1-framework';
    } else if (toolId === 'pain-line-analysis') {
      mappedToolId = 'pain-line-analysis';
    } else if (toolId === 'camcorder-method') {
      mappedToolId = 'camcorder-method';
    } else if (toolId === 'audit-transfer-fill') {
      mappedToolId = 'audit-transfer-fill';
    }
    
    // Set the active tool result to show the popup
    setActiveToolResult(mappedToolId);
    
    // Add toast notification
    const toolName = mappedToolId.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
    addToast(`✅ ${toolName} tool activated!`, 'success');
  };

  // Memoized message row for performance
  const MessageRow = React.memo(function MessageRow({ msg, docs = [] }) {
    // Ensure content is always a string to prevent React error #31
    let safeContent = typeof msg.content === 'string' ? msg.content : 
                       typeof msg.content === 'object' ? JSON.stringify(msg.content) : 
                       String(msg.content || '');
    
    // Remove inline "Source:" text from content
    safeContent = cleanMessageContent(safeContent);
    
    // Ensure all message properties are safe for rendering
    const safeMsg = {
      id: String(msg.id || msg.timestamp || ''),
      role: String(msg.role || 'user'),
      timestamp: Number(msg.timestamp || Date.now()),
      type: String(msg.type || 'text'),
      content: safeContent,
      sources: Array.isArray(msg.sources) ? msg.sources : [],
      fallback: Boolean(msg.fallback),
      usesCoachDocs: Boolean(msg.usesCoachDocs),
      pipeline: String(msg.pipeline || '') // Include pipeline to show framework badge
    };
    
    return (
      <div key={safeMsg.id} className={`ov-enterprise-msg ov-msg-${safeMsg.role}`}>
        {safeMsg.role === 'assistant' ? (
          <div className="ov-msg-avatar">AI</div>
        ) : (
          <div className="ov-msg-avatar">You</div>
        )}
        <div className="ov-msg-content">
        <div className="ov-msg-timestamp">
          {new Date(safeMsg.timestamp).toLocaleTimeString()}
          {safeMsg.role === 'assistant' && safeMsg.usesCoachDocs && (
            <span className="ov-source-badge" style={{marginLeft:8}}>
              {vertical === 'hvac' ? 'Using Company SOPs' : 'Using Coach Docs'}
            </span>
          )}
          {/* Show framework badge when using framework (pipeline = framework_v2) */}
          {safeMsg.role === 'assistant' && safeMsg.pipeline === 'framework_v2' && frameworkExecutionActive && (
            <span className="ov-source-badge" style={{marginLeft:8, background:'#8b5cf6', color:'white'}}>
              🎯 Using Framework
            </span>
          )}
        </div>
        <div className={`ov-enterprise-bubble ov-bubble-${safeMsg.role}`}>
            {safeMsg.role === 'assistant' && safeMsg.sources.length === 0 && vertical === 'mentormind' && (
            <div className="ov-general-knowledge-banner">
                Using general coaching knowledge
            </div>
          )}
            <div className="ov-msg-text">{safeMsg.content}</div>
          </div>
          {/* Enhanced source attribution - same as chat */}
          {safeMsg.sources && safeMsg.sources.length > 0 && (
            <div className="ov-msg-sources">
              {dedupeAndLimitSources(safeMsg.sources).map((g, idx) => {
                // g is already aggregated from dedupeAndLimitSources
                const title = g.title || 'Source';
                const url = g.url || '';
                const pages = g.pages || [];
                // Show all pages if multiple pages from same document
                const sortedPages = Array.from(pages).sort((a, b) => a - b);
                const pageDisplay = sortedPages.length > 0 
                  ? (sortedPages.length > 1 ? ` (p. ${sortedPages.join(', ')})` : ` (p. ${sortedPages[0]})`)
                  : '';
                const isUrlSource = url && (url.startsWith('http://') || url.startsWith('https://'));
                const isDocLink = url && url.startsWith('#doc-');
                const icon = isUrlSource ? '🔗' : '📄';

                // If URL source, make it clickable
                if (isUrlSource) {
                  return (
                    <a
                      key={idx}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ov-source-chip"
                      title={`View source: ${url}`}
                    >
                      {icon} {title}{pageDisplay}
                    </a>
                  );
                }

                // If document link (#doc-...), make it clickable to open document
                if (isDocLink) {
                  return (
                    <a
                      key={idx}
                      href={url}
                      onClick={(e) => {
                        e.preventDefault();
                        const hash = url.replace('#', '');
                        const docFilename = hash.replace('doc-', '');
                        
                        // Try to find the document in the docs list and open it directly
                        const doc = docs.find(d => {
                          const dFilename = (d.filename || d.title || '').split('/').pop() || '';
                          return dFilename === docFilename || dFilename.includes(docFilename) || docFilename.includes(dFilename);
                        });
                        
                        if (doc && (doc.view_url || doc.sop_file_url)) {
                          // Open document directly in new tab
                          window.open(doc.view_url || doc.sop_file_url, '_blank', 'noopener,noreferrer');
                        } else {
                          // Fallback: navigate to documents tab and scroll to document
                          window.location.hash = hash;
                          window.dispatchEvent(new HashChangeEvent('hashchange'));
                        }
                      }}
                      className="ov-source-chip"
                      title={`View document: ${title}`}
                      style={{ cursor: 'pointer' }}
                    >
                      {icon} {title}{pageDisplay}
                    </a>
                  );
                }

                // Otherwise, just display with page numbers
                return (
                  <span key={idx} className="ov-source-chip">
                    {icon} {title}{pageDisplay}
                  </span>
                );
              })}
              {safeMsg.sources.length > 2 && (
                <span className="ov-source-chip">+{safeMsg.sources.length - 2} more</span>
              )}
            </div>
          )}
          {/* Inline Actions for MentorMind */}
          {isMentorMind && safeMsg.role === 'assistant' && (
            <InlineActions
              message={safeMsg}
              onSummarize={(msg) => {
                // TODO: Implement summarize functionality
                addToast('Summarize feature coming soon', 'info');
              }}
              onActionPlan={(msg) => {
                // TODO: Implement action plan functionality
                addToast('Action plan feature coming soon', 'info');
              }}
              onSaveToProgress={(msg) => {
                if (activeSessionId && companyIdSlug) {
                  sessionManager.addInsightToSession(companyIdSlug, activeSessionId, {
                    type: 'note',
                    value: msg.content.substring(0, 200),
                    timestamp: Date.now()
                  });
                  addToast('Saved to Progress', 'success');
                }
              }}
              onViewSources={(sources) => {
                // Show sources modal or expand view
                addToast(`Viewing ${sources.length} source${sources.length > 1 ? 's' : ''}`, 'info');
              }}
            />
          )}
          {/* "Explain this question" helper - only show for AI questions during framework execution */}
          {safeMsg.role === 'assistant' && frameworkExecutionActive && safeMsg.content.includes('?') && (
            <button
              onClick={() => {
                // Send a clarification request
                handleSendMessage("What do you mean by that question? Can you explain?");
              }}
              className="ov-explain-question-btn"
              style={{
                marginTop: '8px',
                padding: '6px 12px',
                fontSize: '13px',
                color: '#6366f1',
                background: 'transparent',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#f3f4f6';
                e.target.style.borderColor = '#6366f1';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.borderColor = '#e5e7eb';
              }}
            >
              <span>💡</span>
              <span>Explain this question</span>
            </button>
          )}
          {/* Show fallback indicators */}
          {safeMsg.role === 'assistant' && (
            <div className="ov-msg-meta">
              {safeMsg.fallback && (
                <span className="ov-fallback-badge">
                  ⚠️ General knowledge (no specific sources)
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  });

  // Lightweight components for Dan Martell experience
  const SmartSuggestions = ({ suggestions, coachingType }) => {
    if (!suggestions || suggestions.length === 0) return null;
    const items = suggestions.map(s => (typeof s === 'string' ? { icon: '💡', title: s, reason: '' } : s));
    return (
        <div className="ov-smart-suggestions dan-martell">
          <h4 className="ov-section-subtitle">💡 Suggested Tools Based on Our Conversation</h4>
        {items.map((s, idx) => (
          <div key={idx} className="ov-suggestion-card" onClick={() => {
            if (s.tool) {
              handleSelectTool(s.tool);
            } else {
              handleQuickSuggestion(s.title);
            }
          }}>
            <div className="ov-suggestion-icon">{s.icon || '💡'}</div>
              <div className="ov-suggestion-content">
                <div className="ov-suggestion-title">{s.title}</div>
              {s.reason && <div className="ov-suggestion-reason">{s.reason}</div>}
              </div>
              <div className="ov-suggestion-action">→</div>
            </div>
          ))}
        </div>
  );
  };

  const ConversationIntelligence = ({ extracted }) => {
    if (!extracted || Object.keys(extracted).length === 0) return null;
    
    // Calculate business health score
    const calculateBusinessHealth = () => {
      let score = 100;
      let areas = [];
      
      if (extracted.weeklyHours && extracted.weeklyHours > 50) {
        score -= 25;
        areas.push('Time Management');
      }
      
      if (extracted.arr && extracted.teamSize) {
        const revenuePerEmployee = extracted.arr / extracted.teamSize;
        if (revenuePerEmployee < 100000) {
          score -= 20;
          areas.push('Team Efficiency');
        }
      }
      
      if (extracted.pain_point === 'overwhelmed') {
        score -= 20;
        areas.push('Leadership Capacity');
      }
      
      if (extracted.pain_point === 'bottleneck') {
        score -= 15;
        areas.push('Systems & Processes');
      }
      
      return { score: Math.max(score, 0), areas };
    };
    
    const health = calculateBusinessHealth();
    
    return (
      <div className="ov-conversation-intel dan-martell">
        <div className="ov-intel-header">
          <h3 className="ov-intel-title">💡 Premium Business Intelligence</h3>
          <div className="ov-intel-subtitle">Key insights from your conversation</div>
        </div>
        
        <div className="ov-intel-content">
          {typeof extracted.arr === 'number' && (
            <div className="ov-intel-card">
              <div className="ov-intel-card-header">
                <span className="ov-intel-icon">📊</span>
                <span className="ov-intel-label">Annual Revenue</span>
              </div>
              <div className="ov-intel-value">${extracted.arr.toLocaleString()}</div>
              <button className="ov-intel-action" onClick={() => handleSelectTool('replacement-ladder')}>
                Plan Next Hire →
              </button>
            </div>
          )}
          
          {typeof extracted.weeklyHours === 'number' && extracted.weeklyHours > 60 && (
            <div className="ov-intel-card">
              <div className="ov-intel-card-header">
                <span className="ov-intel-icon">⏰</span>
                <span className="ov-intel-label">Workload</span>
              </div>
              <div className="ov-intel-value">{extracted.weeklyHours} hrs/week</div>
              <button className="ov-intel-action" onClick={() => handleSelectTool('buyback-roi')}>
                Calculate Buyback →
              </button>
            </div>
          )}
          
          {extracted.teamSize && (
            <div className="ov-intel-card">
              <div className="ov-intel-card-header">
                <span className="ov-intel-icon">👥</span>
                <span className="ov-intel-label">Team Size</span>
              </div>
              <div className="ov-intel-value">{extracted.teamSize} people</div>
              <button className="ov-intel-action" onClick={() => handleSelectTool('replacement-ladder')}>
                Optimize Structure →
              </button>
            </div>
          )}
          
          {extracted.pain_point && (
            <div className="ov-intel-card">
              <div className="ov-intel-card-header">
                <span className="ov-intel-icon">🎯</span>
                <span className="ov-intel-label">Pain Point</span>
              </div>
              <div className="ov-intel-value">{extracted.pain_point.replace('_', ' ')}</div>
              <button className="ov-intel-action" onClick={() => handleSelectTool('pain-line-analysis')}>
                Analyze Issue →
              </button>
            </div>
          )}
          
          {extracted.goals && extracted.goals.length > 0 && (
            <div className="ov-intel-card">
              <div className="ov-intel-card-header">
                <span className="ov-intel-icon">🎯</span>
                <span className="ov-intel-label">Goals</span>
              </div>
              <div className="ov-intel-value">{extracted.goals[0]}</div>
              <button className="ov-intel-action" onClick={() => handleSelectTool('1-3-1-framework')}>
                Create Plan →
              </button>
            </div>
          )}
          
          {extracted.challenges && extracted.challenges.length > 0 && (
            <div className="ov-intel-card">
              <div className="ov-intel-card-header">
                <span className="ov-intel-icon">🚨</span>
                <span className="ov-intel-label">Challenges</span>
              </div>
              <div className="ov-intel-value">{extracted.challenges[0]}</div>
              <button className="ov-intel-action" onClick={() => handleSelectTool('audit-transfer-fill')}>
                Solve Challenge →
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Enterprise keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // CRITICAL: Allow arrow keys to work normally in text inputs and textareas
      // This allows users to move the cursor within the text field
      const isTextInput = e.target && (
        e.target.tagName === 'INPUT' || 
        e.target.tagName === 'TEXTAREA' ||
        e.target.isContentEditable ||
        e.target.contentEditable === 'true'
      );
      
      // If user is typing in a text field and presses arrow keys, let browser handle it
      if (isTextInput && (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        return; // Don't prevent default - let the browser handle cursor movement
      }
      
      // Enter to send when focus is on chat input
      const active = document.activeElement;
      const isChatField = active && active.classList && active.classList.contains('ov-enterprise-input');
      if (isChatField && e.key === 'Enter') {
        e.preventDefault();
        if (inputText.trim()) handleSendMessage();
      }
      // Ctrl/Cmd + Enter also sends
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && inputText.trim()) {
        e.preventDefault();
        handleSendMessage();
      }
      // Forward slash to focus search
      if (e.key === '/' && !e.target.matches('input, textarea')) {
        e.preventDefault();
        const searchInput = document.querySelector('.ov-search-input, .ov-enterprise-search-input');
        if (searchInput) searchInput.focus();
      }
      // Escape to clear chat input completely (only if not in text input)
      if (e.key === 'Escape' && !isTextInput) {
        setInputText('');
        setSearchQuery('');
        setError(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [inputText]);

  // Update session metrics
  useEffect(() => {
    const updateMetrics = () => {
      const duration = (Date.now() - sessionMetrics.startTime) / 1000 / 60;
      const timeSaved = Math.round(interactionCount * 2.5); // 2.5 min per interaction
      const efficiency = interactionCount > 0 ? Math.round((timeSaved / duration) * 100) : 0;
      
      setSessionMetrics(prev => ({
            ...prev,
        interactions: interactionCount,
        timeSaved,
        efficiency: Math.min(efficiency, 300), // Cap at 300%
        documentsAccessed: docs.length,
        insightsGenerated: 0
      }));
    };

    const interval = setInterval(updateMetrics, 30000); // Update every 30s
    updateMetrics(); // Initial update
    
    return () => clearInterval(interval);
  }, [interactionCount, docs.length, sessionMetrics.startTime]);

  // Debug: track messages updates and print last 3
  // removed debug logs for clean demos
  useEffect(() => {}, [messages]);

  // Live transcript debug tracking
  useEffect(() => {}, [liveTranscript]);
  useEffect(() => {}, [recentUserTranscripts]);

  // Listen for auto-test messages from parent (Framework Test Modal)
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'AUTO_TEST_QUERY') {
        // Auto-submit the test query
        if (event.data.query) {
          setInputText(event.data.query);
          // Trigger send after a short delay to ensure input is set
          setTimeout(() => {
            // Click the send button programmatically
            const sendButton = document.querySelector('button[aria-label="Send message"]') || 
                              document.querySelector('.ov-send-button');
            if (sendButton) {
              sendButton.click();
            }
          }, 500);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []); // Empty dependencies - listener only needs to be set up once

  // Coaching-type aware quick starts via unified config (MentorMind only)
  useEffect(() => {
    if (vertical === 'mentormind') {
      const ct = COACHING_TYPES[coachingType] || COACHING_TYPES.business;
      setQuickSuggestions(ct.quickStart || []);
    }
  }, [coachingType, vertical]);

  // Dynamic Favicon
  useEffect(() => {
    const favicon = document.querySelector('link[rel="icon"]') || document.createElement('link');
    favicon.rel = 'icon';
    
    // Set favicon based on vertical or Dan Martell custom
    if (companyIdSlug.includes('dan-martell')) {
      favicon.href = '/mentormind.svg';
    } else if (vertical === 'hvac') {
      favicon.href = '/opsvoice-hvac.svg';
    } else if (vertical === 'business') {
      favicon.href = '/opsvoice-business.svg';
        } else {
      favicon.href = '/mentormind.svg';
    }
    
    if (!document.querySelector('link[rel="icon"]')) {
      document.head.appendChild(favicon);
    }
  }, []);

  // Hash Navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '') || 'chat';
      
      // Handle document links (#doc-filename)
      if (hash.startsWith('doc-')) {
        const docFilename = decodeURIComponent(hash.replace('doc-', ''));
        // Switch to docs tab and scroll to document
        setActiveTab('documents');
        // Wait for docs to load, then scroll to document
        const findAndHighlightDoc = () => {
          // Try exact match first
          let docElement = document.querySelector(`[data-doc-filename="${docFilename}"]`);
          
          // If not found, try matching just the filename (without path)
          if (!docElement) {
            const filenameOnly = docFilename.split('/').pop() || docFilename;
            docElement = document.querySelector(`[data-doc-filename="${filenameOnly}"]`);
          }
          
          // If still not found, try partial match
          if (!docElement) {
            const allDocElements = document.querySelectorAll('[data-doc-filename]');
            for (const elem of allDocElements) {
              const elemFilename = elem.getAttribute('data-doc-filename') || '';
              if (elemFilename.includes(docFilename) || docFilename.includes(elemFilename)) {
                docElement = elem;
                break;
              }
            }
          }
          
          if (docElement) {
            docElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            docElement.style.backgroundColor = 'rgba(99, 102, 241, 0.1)';
            docElement.style.transition = 'background-color 0.3s ease';
            setTimeout(() => {
              docElement.style.backgroundColor = '';
            }, 2000);
          }
        };
        
        // Try immediately, then retry after a delay if docs are still loading
        findAndHighlightDoc();
        setTimeout(findAndHighlightDoc, 500);
        setTimeout(findAndHighlightDoc, 1000);
        return;
      }
      
      // Handle regular tab navigation
      const validTabs = ['chat', 'coach', 'documents', 'insights', 'calculators'];
      if (validTabs.includes(hash)) {
      setActiveTab(hash);
      } else {
        setActiveTab('chat');
      }
    };
    
    // Check initial hash
    handleHashChange();
    
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Set theme on document root
  useEffect(() => {
    document.documentElement.setAttribute('data-tenant', config.theme);
    document.documentElement.setAttribute('data-vertical', vertical);
    document.documentElement.setAttribute('data-company-id', companyIdSlug);
  }, [config.theme, vertical, companyIdSlug]);

  // Online/Offline Detection
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Smart auto-scroll: only scroll if user is near bottom (prevents jumping during voice transcription)
  useEffect(() => {
    // Only scroll if the last message is from the assistant
    if (messages.length > 0 && messages[messages.length - 1]?.role === 'assistant') {
      const messagesContainer = messagesEndRef.current?.parentElement;
      if (!messagesContainer) {
        // Fallback: just scroll if we can't get container
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        return;
      }
      
      const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const isNearBottom = distanceFromBottom < 100;
      
      // Update scroll-to-latest button visibility
      setShowScrollToLatest(!isNearBottom && messages.length > 3);
      
      // Only auto-scroll if user is near the bottom (hasn't scrolled up to read)
      if (isNearBottom) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      } else {
      }
    }
  }, [messages]);
  
  // Listen for scroll events to update "scroll to latest" button
  useEffect(() => {
    const messagesContainer = messagesEndRef.current?.parentElement;
    if (!messagesContainer) return;
    
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const isNearBottom = distanceFromBottom < 100;
      
      // Show button only if scrolled up and have multiple messages
      setShowScrollToLatest(!isNearBottom && messages.length > 3);
    };
    
    messagesContainer.addEventListener('scroll', handleScroll);
    return () => messagesContainer.removeEventListener('scroll', handleScroll);
  }, [messages.length]);
  
  // Function to force scroll to bottom
  const forceScrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setShowScrollToLatest(false);
  }, []);

  // Tab Change Handler with Error Handling
  const handleTabChange = (tab) => {
    try {
      
      // Auto-switch back to Docs when navigating to Conversation, Notes, or Tools
      // (These tabs need the docs/chat interface, not spreadsheets)
      if (spreadsheetsEnabled && dataSource === 'spreadsheets') {
        if (tab === 'chat' || tab === 'voice' || tab === 'coach' || tab === 'insights') {
          setDataSource('docs');
        }
      }
      
      // If switching to coach tab, load notes from localStorage BEFORE changing tab
      // This ensures the state is ready when the tab renders
      if (tab === 'coach') {
        try {
          const saved = localStorage.getItem(`ov_notes_${companyIdSlug}`);
          if (saved) {
            const parsed = JSON.parse(saved);
            // Set state synchronously before tab change
            setCoachNotes(parsed);
            
            // Sync liked messages with notes that came from conversations
            const notesWithMessages = parsed.filter(note => note.messageId && note.fromConversation);
            if (notesWithMessages.length > 0) {
              const messageIds = new Set(notesWithMessages.map(note => note.messageId));
              setLikedMessages(prev => {
                const merged = new Set([...prev, ...messageIds]);
                return merged;
              });
            }
          } else {
            setCoachNotes([]);
          }
        } catch (e) {
          setCoachNotes([]);
        }
      }
      
      // Set tab after loading coach notes
    setActiveTab(tab);
    window.location.hash = tab;
    
      // Clear any existing errors when switching tabs
      if (error) {
        setError(null);
      }
      
      // Lazy load content with error handling
      if (tab === 'documents' && docs.length === 0) {
        loadDocs().catch(err => {
          console.error('[Tab Change] Failed to load docs:', err);
          setError('Failed to load documents. Please try again.');
        });
    } else if (tab === 'insights' && !insights) {
        loadInsights().catch(err => {
          console.error('[Tab Change] Failed to load insights:', err);
          setError('Failed to load insights. Please try again.');
        });
      }
    } catch (err) {
      console.error('[Tab Change] Error switching to tab:', tab, err);
      setError('Failed to switch tabs. Please refresh the page.');
    }
  };

  // Load docs and subscription on mount for Business/OpsIQ (needed for chat + documents tab)
  useEffect(() => {
    if ((vertical === 'business' || vertical === 'opsiq') && companyIdSlug) {
      if (docs.length === 0) {
        loadDocs();
      }
      // Always load subscription to check tier restrictions
      loadSubscription();
    }
  }, [vertical, companyIdSlug]);

  // Ensure initial tab content loads when landing directly on #docs or #insights
  useEffect(() => {
    if (activeTab === 'documents' && docs.length === 0) {
      loadDocs();
    }
    if (activeTab === 'insights' && !insights) {
      loadInsights();
    }
    // Also load docs for Insights tab so read-only Docs card can render
    if (activeTab === 'insights' && docs.length === 0) {
      loadDocs();
    }
  }, [activeTab]);

  // Load Subscription Info
  const loadSubscription = async () => {
    if (!companyIdSlug) {
      return;
    }
    try {
      setSubscriptionLoading(true);
      const backendId = getBackendCompanyId(companyIdSlug);
      const res = await fetch(`${API_BASE}/api/subscription/info?company_id_slug=${encodeURIComponent(backendId)}`, {
        credentials: 'include',
        headers: { ...widgetKeyHeaders() },
      });
      
      if (res.ok) {
        const data = await res.json();
        setSubscriptionTier(data.tier || null);
        
        // Calculate remaining minutes for trial users
        if (data.tier === 'trial' && data.usage) {
          const used = data.usage.voice_minutes_this_month || 0;
          const remaining = Math.max(0, 30 - used);
          setVoiceMinutesRemaining(remaining);
        } else {
          setVoiceMinutesRemaining(null); // Unlimited for paid tiers
        }
      } else {
        // Don't block voice if subscription endpoint fails - allow voice to work with fallback
        // Set subscriptionTier to null so voice can proceed with default behavior
        setSubscriptionTier(null);
      }
    } catch (err) {
      console.error('[Subscription] Failed to load subscription:', err);
      // Don't block voice if subscription endpoint fails - allow voice to work with fallback
      setSubscriptionTier(null);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Load Documents
  const loadDocs = async () => {
    if (!companyIdSlug) {
      return;
    }
    try {
      const backendId = getBackendCompanyId(companyIdSlug);
      // Load docs even if offline is detected - might be false positive
      const data = await apiService.companyDocs(backendId);
      const docsArray = Array.isArray(data.documents) ? data.documents : [];
      
      // Filter to only show documents with visibility='reference' (Knowledge Base)
      // Documents with visibility='training_only' are private and should not appear in widget
      const visibleDocs = docsArray.filter(doc => {
        const visibility = doc.visibility || doc.metadata?.visibility || 'training_only';
        const isVisible = visibility === 'reference';
        
        // Debug logging for visibility filtering
        if (!isVisible) {
          
        }
        
        return isVisible;
      });
      
      setDocs(visibleDocs);
    } catch (err) {
      console.error('[Docs] Failed to load docs:', err);
      // Don't set empty array on error - might be temporary network issue
      // Keep existing docs if any, otherwise empty
      if (docs.length === 0) {
        setDocs([]);
      }
    }
  };

  // Load Insights
  const loadInsights = async () => {
    if (!companyIdSlug) return;
    try {
      const backendId = getBackendCompanyId(companyIdSlug);
      const data = await apiService.metrics(backendId);
      // Preload demo data for demo-mentormind-company
      if (companyIdSlug === 'demo-mentormind-company') {
        const demoCaptured = [
          { type: 'Client Goals', value: 'Get promoted to Director level within 18 months' },
          { type: 'Challenges', value: 'Struggle with confidence and public speaking' },
          { type: 'Progress', value: 'Completed leadership training, started speaking up in meetings' },
          { type: 'Self-Awareness', value: "I'm great at one-on-one relationships but avoid group settings" }
        ];
        demoCaptured.forEach(ci => captureInsight(ci));
      }
      setInsights(data);
      } catch (err) {
      console.error('Failed to load insights:', err);
      setInsights({});
    }
  };


  // Handle like/save message - saves to Quick Notes
  const handleLikeMessage = (messageId) => {
    const newLiked = new Set(likedMessages);
    if (newLiked.has(messageId)) {
      // Remove from liked and from Quick Notes
      newLiked.delete(messageId);
      setCoachNotes(prev => prev.filter(note => note.messageId !== messageId));
      addToast('Removed from Quick Notes', 'info');
    } else {
      // Add to liked and save to Quick Notes
      newLiked.add(messageId);
      const message = messages.find(m => (m.id || String(m.timestamp)) === messageId);
      if (message) {
        // Determine note type based on message role
        const noteType = message.role === 'assistant' ? 'other' : 'question';
        
        // Create note with conversation indicator
        const note = {
          id: `note-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          type: noteType,
          content: cleanMessageContent(message.content || ''),
          timestamp: Date.now(),
          messageId: messageId, // Link back to original message
          fromConversation: true, // Indicator that it came from a conversation
          relatedMessage: messageId
        };
        
        // Add to Quick Notes
        setCoachNotes(prev => {
          // Remove if already exists (avoid duplicates)
          const filtered = prev.filter(n => n.messageId !== messageId);
          return [note, ...filtered];
        });
        
        addToast('Saved to Quick Notes', 'success');
      }
    }
    setLikedMessages(newLiked);
    // Persist to localStorage
    try {
      localStorage.setItem(`ov_liked_messages_${companyIdSlug}`, JSON.stringify(Array.from(newLiked)));
    } catch (e) {
    }
  };

  // Load liked messages from localStorage on mount
  useEffect(() => {
    try {
      const storedLiked = localStorage.getItem(`ov_liked_messages_${companyIdSlug}`);
      if (storedLiked) {
        setLikedMessages(new Set(JSON.parse(storedLiked)));
      }
    } catch (e) {
    }
  }, [companyIdSlug]);

  // Send Message
  const handleSendMessage = async () => {
    if (!inputText.trim() || isLoading) return;

    // Quick export command
    if (inputText.trim().toLowerCase() === '/export') {
      const sessionDuration = (Date.now() - sessionMetrics.startTime) / 1000;
      const content = formatExport(messages, 'markdown', { vertical, companyIdSlug, sessionDuration, messageCount: messages.length });
      downloadFile(content, `mentormind-analysis-${new Date().toISOString().slice(0,10)}.md`, 'text/markdown');
      setInputText('');
      return;
    }

    // Tool conversation branch
    if (activeToolConversation) {
      return handleToolConversation(activeToolConversation, inputText.trim());
    }

    // Trial guidance if no docs yet
    if (trialState?.isTrialUser && !trialState.hasUploadedDocs && docs.length === 0) {
      const helpMessage = {
        role: 'assistant',
        content: vertical === 'mentormind'
          ? "I'd be happy to help! To give you the most personalized coaching experience, I recommend uploading your coaching frameworks and methodologies first. You can upload up to 3 documents in your free trial.\n\nClick on the \"Docs\" tab above to get started!"
          : "I'd be happy to help! To give you the most personalized insights, I recommend uploading some of your business documents first. You can upload up to 3 documents in your free trial.\n\nClick on the \"Docs\" tab above to get started, or feel free to ask me general business questions.",
        timestamp: Date.now(),
        type: 'system_guidance',
        id: `guidance-${Date.now()}`
      };
      setMessages(prev => [...prev, helpMessage]);
    }

    // Auto-create session for MentorMind if none exists
    if (isMentorMind && !activeSessionId && companyIdSlug) {
      const session = sessionManager.createSession(companyIdSlug, {
        firstMessage: { content: inputText.trim(), role: 'user' },
        frameworkName: null
      });
      setActiveSessionId(session.id);
      sessionManager.setCurrentSession(companyIdSlug, session.id);
      // Switch to sessions tab if on home
      if (activeTab === 'home') {
        setActiveTab('sessions');
      }
    }

    const userMessage = {
      role: 'user',
      content: inputText.trim(),
      timestamp: Date.now(),
      type: 'text',
      id: `text-${Date.now()}`
    };

    // CRITICAL: Save user message IMMEDIATELY
    setMessages(prev => {
      const newMessages = [...prev, userMessage];
      // Structured insights hook
      if (typeof processMessageForInsights === 'function') {
      processMessageForInsights(userMessage, 'user');
      }
      // Run insights for user message (only if function exists)
      if (typeof runInsightsFor === 'function') {
      runInsightsFor(userMessage.content);
      }
      return newMessages;
    });

    setInputText('');
    
    // Auto-focus input after sending message
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
    
    // Extract context from user message to check for ready tools
    const metrics = parseBusinessMetrics(userMessage.content);
    const slots = extractSlots(userMessage.content, coachingType);
    const updatedContext = { ...conversationContext, ...metrics, ...slots };
    
    // Update conversation context immediately so tools can use it
    setConversationContext(updatedContext);
    
    // ROI auto-trigger removed
    
    // If we have enough context for ROI Calculator, check if it's ready and trigger it
    // DISABLED during framework execution to avoid interrupting the coaching flow
    if (false) { // ROI auto-trigger removed
      // Check for ROI Calculator in preset tools or ready tools
      const roiCalculatorTool = (() => {
        // Check preset tools from insightRegistry (returns array directly)
        const presetTools = createInsightRegistry(vertical, coachingType, companyIdSlug) || [];
        return presetTools.find(t => t.id === 'roi-calculator' || t.name?.toLowerCase().includes('roi'));
      })();
      
      if (roiCalculatorTool) {
        // Check if tool is ready (has all required context)
        const toolNeeds = roiCalculatorTool.needs || [];
        const hasAllNeeds = toolNeeds.every(need => {
          const needKey = need.toLowerCase().replace(/\s+/g, '_');
          return updatedContext[needKey] || updatedContext[need] || 
                 (needKey.includes('revenue') && hasRevenue) ||
                 (needKey.includes('hours') && hasHours) ||
                 (needKey.includes('team') && hasTeam);
        });
        
        if (hasAllNeeds || (!toolNeeds.length && (hasRevenue || hasHours))) {
          // Tool is ready - skip AI query and open tool directly
          setIsLoading(false);
          setSelectedTool(roiCalculatorTool);
          setShowInsightModal(true);
          return; // Exit early, don't send query to backend
        }
      }
    }

    setIsLoading(true);

    try {
      // Build contextualized prompt (framework-aware if available)
      const buildContextualPrompt = (userText, ctx, type, fw, toolCtx) => {
        const coachDocsFirst = `If coach-uploaded documents exist for this company, answer using ONLY those and cite them. If none are relevant, provide specific, actionable advice based on the business context provided, then ask one follow-up question.`;
        const ctxLine = Object.entries(ctx || {}).map(([k,v]) => `${k}: ${v}`).join(', ');
        
        // Enhanced prompt with tool context
        let enhancedPrompt = coachDocsFirst;
        
        if (fw && fw.frameworks && fw.frameworks.length > 0) {
          const name = fw.frameworks[0];
          const style = (fw.language || []).slice(0,2).join(' ');
          return `${enhancedPrompt}\nUse the coach's "${name}" framework and their voice. Style cues: ${style || 'concise, practical'}.
Context: ${ctxLine || 'n/a'}
Question: ${userText}`;
        }
        
        return `${enhancedPrompt}\nAct as a ${type} coach and keep answers to 2–3 short sentences with one follow-up question.
Context: ${ctxLine || 'n/a'}
Question: ${userText}`;
      };

      const contextual = buildContextualPrompt(userMessage.content, conversationContext, coachingType, coachFrameworks, toolContext);

      const sid = getSessionId();
      
      // For document search, use the original question directly - don't bury it in contextual prompt
      // The backend should search documents using the original question, then apply context for answer generation
      const searchQuery = userMessage.content.trim();
      
      const data = await apiService.query({
          company_id_slug: getBackendCompanyId(companyIdSlug),
        session_id: sid,
        query: contextual,
        original_question: userMessage.content, // Send original question separately for logging
        search_query: searchQuery, // Explicitly send search query for document retrieval
        vertical,
        audience,
        client_id: clientId, // NEW: Pass client_id for client-specific RAG
        user_email: userEmail || undefined, // NEW: Pass email for team member tracking (if provided in URL)
        context: conversationContext // Include extracted context
      });

      // Unified pipeline additions: extract slots + suggest tool
      const slots = extractSlots(userMessage.content, coachingType);
      if (slots && Object.keys(slots).length > 0) {
        setConversationContext(prev => ({ ...(prev || {}), ...slots }));
      }
      
      // Extract business metrics for insights across all coaching types
      const metrics = parseBusinessMetrics(userMessage.content);
      if (metrics && Object.keys(metrics).length > 0) {
        setConversationContext(prev => ({ ...prev, ...metrics }));
        updateBusinessProfile(metrics);
        
        // Suggest tools based on extracted metrics
        if (isDanMartellWidget) {
          const suggestions = suggestTools(userMessage.content, metrics);
          if (suggestions.length > 0) {
            setQuickSuggestions(suggestions);
          }
        }
      }
      
      const cfg = getCoachingConfig(coachingType, getVerticalOverrides(vertical, companyIdSlug));

      if (data && data.answer) {
        // ============================================
        // FRAMEWORK EXECUTION HANDLING (v1 + v2)
        // ============================================
        // v2 uses "framework_v2" pipeline and "framework_info" instead of "framework_execution"
        const isFrameworkV2 = data.pipeline === 'framework_v2';
        const isFrameworkV1 = data.pipeline === 'framework_execution' || data.framework_execution?.active;
        const isFrameworkExecution = isFrameworkV1 || isFrameworkV2;
        
        // Completion detection for both versions
        const isFrameworkCompletedV1 = data.framework_execution?.completed;
        const isFrameworkCompletedV2 = data.framework_info?.is_complete;
        const isFrameworkCompleted = isFrameworkCompletedV1 || isFrameworkCompletedV2;
        
        setFrameworkExecutionActive(isFrameworkExecution && !isFrameworkCompleted);
        
        if (isFrameworkExecution) {
          // Handle v2 framework execution (schema-aware with structured data)
          if (isFrameworkV2 && data.framework_info) {
            
            // Map v2 format to match v1 display format
            setFrameworkExecutionData({
              framework_name: data.framework_info.name,
              current_stage: data.framework_info.current_step,
              total_stages: data.framework_info.total_steps,
              completed: data.framework_info.is_complete,
              completion_percentage: data.framework_info.completion_percentage,
              schema_data: data.framework_info.schema_data, // Structured data (NEW!)
              active: true
            });
            
            // Handle v2 completion with summary card
            if (isFrameworkCompletedV2) {
              
              setFrameworkCompletionData({
                frameworkName: data.framework_info.name,
                summaryCard: data.summary_card, // New v2 summary card format
                schemaData: data.framework_info.schema_data, // Structured data
                isV2: true // Flag to use new rendering
              });
              
              setTimeout(() => {
                setShowFrameworkCompletionModal(true);
              }, 1500);
            }
          }
          // Handle v1 framework execution (legacy)
          else if (isFrameworkV1 && data.framework_execution) {
            // Store full framework execution data for progress display
            setFrameworkExecutionData(data.framework_execution);
            
            // Check for framework completion - trigger celebration modal!
            if (isFrameworkCompletedV1) {
              setFrameworkCompletionData({
                frameworkName: data.framework_execution.framework_name,
                completionSummary: data.framework_execution.completion_summary,
                stages: data.framework_execution.stages,
                // Pass collected context for WOW display
                collected: data.framework_execution.completion_summary?.collected || {},
                context: data.framework_execution.completion_summary?.context || {},
                isV2: false
              });
              // Small delay to let the final message render first
              setTimeout(() => {
                setShowFrameworkCompletionModal(true);
              }, 1500);
            }
          }
        } else {
          // Clear framework data when not in execution
          setFrameworkExecutionData(null);
        }
        
        // Handle framework suggestions (intent-based)
        if (data.framework_suggestion?.suggested) {
          setFrameworkSuggestion(data.framework_suggestion);
        }
        
        // Check if this is a fallback response (not from documents)
        const isFallback = data.fallback_reason || data.pipeline === 'fallback' || data.pipeline === 'fallback_chat' || data.pipeline === 'fallback_rag';
        
        // Enhanced source handling with better debugging
        const norm = normalizeSources(data);
        // If fallback, ignore sources - they're not relevant
        const effectiveSources = isFallback ? [] : norm;
        const hasCoachDocs = !isFallback && hasCoachPreferredSource(effectiveSources);
        let display = String(data.answer || '');
        
        // Strip "From your uploaded documents:" prefix if present
        display = display.replace(/^From your uploaded documents:\s*/i, '');
        display = display.replace(/^From your coach's materials:\s*/i, '');
        display = display.trim();
        
        // No inline source attribution - sources are shown separately in the UI
        // The "Using Coach Docs" badge and source chips provide all the context needed
        
        // Calculate confidence level based on sources and fallback status
        const getConfidenceLevel = (sources, hasCoachDocs, isFallback) => {
          if (isFallback) {
            return 'low'; // Always low confidence for fallback responses
          }
          if (hasCoachDocs && sources && sources.length > 0) {
            return 'high'; // High confidence: using uploaded coach docs
          } else if (sources && sources.length > 0) {
            return 'medium'; // Medium confidence: has sources but not coach docs
          } else {
            return 'low'; // Low confidence: no sources, general knowledge
          }
        };
        
        const confidenceLevel = getConfidenceLevel(effectiveSources, hasCoachDocs, isFallback);
        
        const assistantMessage = {
          role: 'assistant',
          content: display,
          timestamp: Date.now(),
          type: 'text',
          sources: effectiveSources, // Use empty array if fallback
          usesCoachDocs: hasCoachDocs && !isFallback, // Never true for fallback
          fallback: isFallback, // Mark as fallback based on backend flag
          pipeline: data.pipeline,
          confidence: confidenceLevel, // Add confidence level
          id: `assistant-${Date.now()}`
        };
        
        // Enhanced debugging for source flow
        // Debug info removed for professional appearance

        // Suggest tool based on slots/keywords using available tools
        const suggested = (() => {
          const toolIds = (cfg.tools || []).map(t => t.id);
          if (slots.hvacIssue && toolIds.includes('safety')) return cfg.tools.find(t => t.id === 'safety');
          if (slots.sales && toolIds.includes('pipeline')) return cfg.tools.find(t => t.id === 'pipeline');
          if (slots.process && toolIds.includes('bottleneck')) return cfg.tools.find(t => t.id === 'bottleneck');
          if (slots.hoursPerWeek && toolIds.includes('replacement')) return cfg.tools.find(t => t.id === 'replacement');
          if (slots.currentWeight && toolIds.includes('program')) return cfg.tools.find(t => t.id === 'program');
          if (slots.debt && toolIds.includes('debt')) return cfg.tools.find(t => t.id === 'debt');
          return null;
        })();

        // Append inline "Run Tool" chip if suggested
        let finalMessages = [assistantMessage];
        if (suggested && suggested.name) {
          finalMessages.push({
            role: 'assistant',
            content: `➡ Run ${suggested.name}`,
            timestamp: Date.now(),
            type: 'suggest_tool',
            toolId: suggested.id,
            id: `suggest-${Date.now()}`
          });
        }

      setMessages(prev => {
        const newMessages = [...prev, ...finalMessages];
        // Update contextual suggestions from recent conversation
        analyzeConversationForInsights(newMessages, coachingType);
        // Run insights for assistant message
        runInsightsFor(assistantMessage.content);
        
        // Auto-focus input after AI responds (for smooth chat flow)
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 100);
        
        return newMessages;
      });
      } else if (data && !data.answer) {
        // Backend returned data but no answer - likely an error or empty response
        console.error('[Chat Debug] Backend returned data but no answer:', data);
        const assistantMessage = {
          role: 'assistant',
          content: 'I apologize, but I encountered an issue processing your question. Please try again or rephrase your question.',
          timestamp: Date.now(),
          type: 'system_guidance',
          id: `fallback-${Date.now()}`
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (err) {
      console.error('[Chat Debug] Error:', err);
      // Professional error handling
      const assistantMessage = {
        role: 'assistant',
        content: professionalErrorHandler(err),
        timestamp: Date.now(),
        type: 'system_guidance',
        id: `fallback-${Date.now()}`
      };
      setMessages(prev => [...prev, assistantMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch company config (assistant_id, etc.) from backend/Supabase
  useEffect(() => {
    const fetchCompanyConfig = async () => {
      try {
        const res = await fetch(`${API_BASE}/widget/config?company_id_slug=${encodeURIComponent(companyIdSlug)}&vertical=${encodeURIComponent(vertical)}`);
        const data = await res.json();
        // Capture the publishable widget_public_key so protected routes can send it
        // as the X-Widget-Public-Key header (no-op when the company has no key).
        setWidgetPublicKey(data && data.widget_public_key);
        // widget/config may contain a company map; try direct field too
        const cfg = (data && (data.company || data.company_config || data)) || null;
        // Always set company config if we have any data, even if fields are missing
        if (cfg) {
          // Merge backend config with local company-specific config
          const localConfig = getCompanyConfig(vertical, companyIdSlug);
          const mergedConfig = {
            ...localConfig,  // Local config has tools, branding, etc.
            ...cfg,          // Backend config has assistant_id, ui, etc.
            // Ensure tools from local config are preserved
            tools: localConfig.tools || cfg.tools,
            // CRITICAL: Preserve ui object from backend response (contains company name from Supabase)
            ui: data.ui || cfg.ui || localConfig.ui || {}
          };
          setCompanyConfig(mergedConfig);
          
          // Check if spreadsheets enabled (OpsIQ/Business/PAI3)
          if (vertical === 'opsiq' || vertical === 'business' || vertical === 'pai3') {
            const features = cfg.features || data.features || [];
            // Handle features as either array or object
            const hasSpreadsheets = Array.isArray(features) 
              ? features.includes('spreadsheets')
              : features.spreadsheets === true;
            // For PAI3/business/opsiq, enable spreadsheets by default (always enabled for these verticals)
            if (hasSpreadsheets || vertical === 'pai3' || vertical === 'business' || vertical === 'opsiq') {
              setSpreadsheetsEnabled(true);
            }
          }
        }
        // Coaching specialization only for mentormind/coaching verticals
        // For business/opsiq, don't use coaching specialization
        if (vertical === 'mentormind' || vertical === 'coaching') {
          const specialization = (cfg?.coaching_specialization
            || (Array.isArray(cfg?.coaching_specialization) ? cfg.coaching_specialization[0] : null)
            || data?.coaching_specialization
            || (Array.isArray(data?.coaching_specialization) ? data.coaching_specialization[0] : null)
          );
          if (specialization) {
            const mapped = mapCoachingSpecialization(specialization);
            setCoachingType(mapped);
          }
        }
      } catch (e) {
      }
    };
    if (companyIdSlug) fetchCompanyConfig();
  }, [companyIdSlug]);

  // Voice Functions
  // Load Vapi SDK via CDN (same flow as test-vapi.html) and initialize once
  useEffect(() => {
    let cancelled = false;
    const loadCdnAndInit = async () => {
      try {
        if (vapi) { setHasVapi(true); return; }
        console.info('[Voice] Initializing Vapi SDK via CDN...');
        const urls = [
          'https://cdn.jsdelivr.net/npm/@vapi-ai/web@2.3.8/+esm',
          'https://unpkg.com/@vapi-ai/web@2.3.8?module',
          'https://esm.sh/@vapi-ai/web@2.3.8'
        ];
        let Ctor = null;
        for (const url of urls) {
          try {
            const mod = await import(/* webpackIgnore: true */ url);
            Ctor = mod?.default || mod?.Vapi || mod;
            if (Ctor) { console.info('[Voice] CDN ESM loaded:', url); break; }
          } catch (e) {
          }
        }
        if (!Ctor || cancelled) return;
        // Resolve key from window/process, fallback to known working public key
        const key = (typeof window !== 'undefined' && window.process && window.process.env && window.process.env.REACT_APP_VAPI_PUBLIC_KEY)
          || (typeof process !== 'undefined' && process.env && process.env.REACT_APP_VAPI_PUBLIC_KEY)
          || '31b0f51e-2e32-4a80-85b3-093a47236b2c';
        // Unwrap nested default if needed and support factory style
        if (typeof Ctor !== 'function' && Ctor && typeof Ctor.default === 'function') {
          Ctor = Ctor.default;
        } else if (typeof Ctor !== 'function' && Ctor && typeof Ctor.Vapi === 'function') {
          Ctor = Ctor.Vapi;
        }
        try {
          vapi = new Ctor(key, 'https://api.vapi.ai');
        } catch (e1) {
          try {
            vapi = Ctor(key, 'https://api.vapi.ai');
          } catch (e2) {
            console.error('[Voice] SDK init error: constructor not callable', e2);
            return;
          }
        }
        
        // Basic listeners mirroring the test page
        vapi.on('call-start', () => {
          if (cancelled) return;
          setIsListening(true);
          setIsRecording(true);
        });
        vapi.on('call-end', () => {
          if (cancelled) return;
      setIsListening(false);
          setIsRecording(false);
      setLiveTranscript('');
          setCurrentTranscript('');
      setIsAssistantSpeaking(false);
      setRecentUserTranscripts([]);
      setRecentAssistantTranscripts([]);
        });
        vapi.on('speech-start', () => { if (!cancelled) { setIsAssistantSpeaking(true); setLiveTranscript(''); setRecentUserTranscripts([]); } });
        vapi.on('speech-end', () => { if (!cancelled) { setIsAssistantSpeaking(false); setLiveTranscript(''); setRecentUserTranscripts([]); } });
        vapi.on('message', (message) => {
          if (!message || cancelled) return;
          // Restore assistant streaming display (UI only; do not save to history)
          if (message.type === 'transcript' && message.role === 'assistant') {
            const seg = (message.transcript || '').trim();
            if (!seg) return;
            setRecentAssistantTranscripts(prev => {
              if (prev.length === 0) return [seg];
              const last = prev[prev.length - 1] || '';
              if (seg === last) return prev;
              if (seg.startsWith(last) || seg.includes(last)) {
                const next = [...prev];
                next[next.length - 1] = seg;
                return next.slice(-3);
              }
              if (last.startsWith(seg)) return prev;
              return [...prev.slice(-2), seg];
            });
          }
          // Keep partials updating live transcript for user during speech
          if (message.type === 'transcript' && message.role === 'user' && message.transcriptType === 'partial') {
            const partialText = message.transcript || '';
            setLiveTranscript(partialText);
            setCurrentTranscript(partialText); // Also update for voice controls overlay
          }
          // CRITICAL: Handle user finals from message channel as this is where they often arrive
          if (message.type === 'transcript' && message.role === 'user' && message.transcriptType === 'final') {
            const finalText = (message.transcript || '').trim();
            setTimeout(() => setLiveTranscript(''), 2000);
            if (!finalText || finalText.length < 3) return;
            saveUserFinalToHistoryAndQuery(finalText);
          }
        });
        vapi.on('transcript', (evt) => {
          if (!evt || cancelled) return;
            const text = evt.transcript || '';
          if (evt.type === 'partial') {
            processVoiceTranscript(text, false);
            return;
          }
          if (evt.type === 'final') {
            processVoiceTranscript(text, true);
          }
        });
        setHasVapi(true);
      } catch (e) {
        console.error('[Voice] SDK init error:', e);
      }
    };
    loadCdnAndInit();
    return () => { cancelled = true; };
  }, []);

  const startVoiceCall = async () => {
    if (isStartingVoice || isListening) {
      return;
    }
    
    // Check tier restrictions BEFORE other checks
    if (subscriptionTier === 'starter') {
      addToast('Voice is available on Professional tier and above. Upgrade to unlock!', 'info');
      // Optionally open pricing page
      if (window.open) {
        window.open('https://www.opsvoice.ai/pricing', '_blank');
      }
      return;
    }
    
    if (subscriptionTier === 'trial' && voiceMinutesRemaining !== null && voiceMinutesRemaining <= 0) {
      addToast('Trial voice minutes used up. Upgrade to continue using voice!', 'info');
      if (window.open) {
        window.open('https://www.opsvoice.ai/pricing', '_blank');
      }
      return;
    }
    
    
    
    // Hard guards for voice functionality
    if (!isOnline) {
      console.error('[Voice] Not online');
      setVoiceError('Voice requires internet connection. Please check your network.');
      return;
    }
    
    if (!hasVapi || !vapi) {
      console.error('[Voice] VAPI not initialized', { hasVapi, vapi: !!vapi });
      setVoiceError('Voice service not available. Enable HTTPS and add this origin in Vapi.');
      return;
    }
    // Resolve assistant id from URL or defaults per vertical
    // PRIORITY: URL > Company Config > Defaults
    let resolvedAssistantId = assistantId || companyConfig?.assistant_id || companyConfig?.assistantId;
    
    // If no company config assistant ID, fall back to defaults
    if (!resolvedAssistantId) {
      console.info('[Voice Debug] Vertical:', vertical, 'Available assistants:', Object.keys(DEFAULT_ASSISTANT_IDS), 'Lookup result:', DEFAULT_ASSISTANT_IDS[vertical]);
      // For PAI3, use business assistant ID as fallback
      resolvedAssistantId = DEFAULT_ASSISTANT_IDS[vertical] || DEFAULT_ASSISTANT_IDS['business'] || DEFAULT_ASSISTANT_IDS['opsiq'] || 'fallback-id';
    }
    
    // Trial override for MentorMind: ensure trial users use the new trial assistant (except Dan Martell widget)
    try {
      if (vertical === 'mentormind' && trialState?.isTrialUser && !isDanMartellWidget) {
        resolvedAssistantId = DEFAULT_ASSISTANT_IDS.mentormind;
        console.info('[Voice] Trial override active. Using MentorMind trial assistant:', resolvedAssistantId);
      }
    } catch (_) {}
    console.info('[Voice] Using assistantId:', resolvedAssistantId);
    if (!resolvedAssistantId) {
      setVoiceError('Assistant is not configured yet. Try text chat or refresh.');
      return;
    }
    
    try {
      setIsStartingVoice(true);
      setVoiceError(null);
      setLiveTranscript('');

      // Haptic feedback
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }

      // Request microphone permission explicitly BEFORE starting VAPI
      // This prevents camera permission errors from blocking microphone access
      try {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          // Request only audio (microphone) permission
          const stream = await navigator.mediaDevices.getUserMedia({ 
            audio: true, 
            video: false  // Explicitly disable video
          });
          // Stop the stream immediately - we just wanted to get permission
          stream.getTracks().forEach(track => track.stop());
          console.info('[Voice] Microphone permission granted');
        }
      } catch (permError) {
        if (permError.name === 'NotAllowedError' || permError.name === 'PermissionDeniedError') {
          setVoiceError('Microphone permission denied. Please allow microphone access and try again.');
          return;
        }
        // Continue anyway - VAPI might handle it
      }

      // Simple: let SDK create/handle the call directly (as in test page)
      try { vapi.stop(); } catch (_) {}

      // Pass metadata with company_id_slug so VAPI assistant can use it in function calls
      // The VAPI assistant must be configured to extract this from metadata and pass it to function calls
      const metadata = {
        company_id_slug: companyIdSlug || '',
        vertical: vertical || 'business',
        widget_url: window.location.href
      };

      // For now, use the dashboard assistant which is known to work
      // TODO: Implement dynamic prompts once VAPI temporary assistant issues are resolved
      await vapi.start(resolvedAssistantId, { metadata });

      // No extra call management beyond init listeners

    } catch (error) {
      console.error('[Voice] Start failed:', error);
      setIsListening(false);
      
      // Handle specific error types
      const msg = String(error?.message || '');
      if (msg.includes('CORS')) {
        setVoiceError('Voice blocked by browser security (CORS). Ensure HTTPS and add this origin to Vapi.');
      } else if (msg.includes('400')) {
        setVoiceError('Voice request rejected (400). Verify assistant ID belongs to your Vapi project.');
      } else if (msg.includes('fetch') || msg.includes('network')) {
        setVoiceError('Network connection issue. Please check your internet and try again.');
      } else {
        setVoiceError('Voice service temporarily unavailable. Please try again or use text chat.');
      }
    } finally {
      setIsStartingVoice(false);
    }
  };

  const stopVoiceCall = async () => {
    try {
      // Append final voice turn into chat history (user + assistant) and save an Ops session snapshot.
      // This ensures the user can scroll and review after voice ends, and also reopen sessions later.

      const userText = recentUserTranscripts.length > 0
        ? String(recentUserTranscripts[recentUserTranscripts.length - 1] || '').trim()
        : '';
      const aiText = recentAssistantTranscripts.length > 0
        ? String(recentAssistantTranscripts[recentAssistantTranscripts.length - 1] || '').trim()
        : '';

      const userEntry = userText && userText.length > 2
        ? { role: 'user', content: userText, timestamp: Date.now(), type: 'voice', id: `user-voice-${Date.now()}` }
        : null;
      const assistantEntry = aiText && aiText.length > 5
        ? { role: 'assistant', content: aiText, timestamp: Date.now(), type: 'voice_rag', sources: [], id: `assistant-voice-${Date.now()}` }
        : null;

      if (userEntry || assistantEntry) {
        setMessages((prev) => {
          let updated = [...prev];

          const add = (entry) => {
            if (!entry) return;
            const last = updated[updated.length - 1];
            const cleaned = cleanTextEntry(entry);
            if (!cleaned.content || cleaned.content.trim() === '') return;
            if (shouldDeduplicate(cleaned, last)) return;
            updated = [...updated, cleaned];
          };

          add(userEntry);
          add(assistantEntry);

          // Save session snapshot for OpsIQ + PAI3 (tenant isolated)
          try {
            if ((vertical === 'opsiq' || vertical === 'pai3') && companyIdSlug) {
              const preview = (assistantEntry?.content || userEntry?.content || '').slice(0, 120);
              const session = {
                id: `ops-session-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                createdAt: Date.now(),
                vertical,
                mode: 'voice',
                title: userEntry?.content ? userEntry.content.slice(0, 42) : 'Voice Session',
                preview,
                // Cap snapshot to last 80 messages to keep localStorage reasonable
                messagesSnapshot: updated.slice(-80),
              };
              const next = saveOpsSession(companyIdSlug, session);
              setOpsSessions(next);
            }
          } catch (e) {
          }

          return updated;
        });
      }
      
      try { if (vapi) vapi.stop(); } catch (_) {}
    } catch (e) {
    }
    setIsListening(false);
    setIsRecording(false);
    setIsAssistantSpeaking(false);
    setLiveTranscript('');
    setCurrentTranscript('');
    // Clear transcripts after a delay to allow them to be saved
    setTimeout(() => {
      setRecentAssistantTranscripts([]);
      setRecentUserTranscripts([]);
    }, 1000);
    if (navigator.vibrate) {
      navigator.vibrate(100);
    }
  };

  // Enterprise Insight Tool Handlers - REMOVED - using new insights system

  // Tool conversation handler - REMOVED - using new insights system

  // moved to utils/tools.js

  // Simplified: removed missing prompts

  // Export Functions
  const handleExport = async (format) => {
    const sessionDuration = (Date.now() - sessionMetrics.startTime) / 1000;
    const content = formatExport(messages, format, {
      vertical,
      companyIdSlug,
      sessionDuration,
      messageCount: messages.length
    });

    const filename = `opsvoice-conversation-${new Date().toISOString().split('T')[0]}.${format === 'markdown' ? 'md' : 'txt'}`;

    // Try clipboard first, fallback to download
    if (format === 'text' || format === 'markdown') {
      const copied = await copyToClipboard(content);
      if (copied) {
        setCopyFeedback('Copied!');
        setTimeout(() => setCopyFeedback(''), 2000);
        return;
      }
    }

    // Fallback to download
    downloadFile(content, filename, 'text/plain');
  };

  // Quick suggestion handler
  const handleQuickSuggestion = (suggestion) => {
    // Close popup if open
    setShowSuggestionsPopup(false);
    
    // Check if suggestion is an object with toolId (for tool triggers)
    if (typeof suggestion === 'object' && suggestion !== null) {
      const toolId = suggestion.toolId || suggestion.id;
      if (toolId) {
        // Trigger tool instead of just setting text
        handleSelectTool(toolId);
        // Also switch to insights/tools tab to show the tool
        setActiveTab('insights');
        return;
      }
      // If it has a title/text, use that
      const text = suggestion.title || suggestion.value || suggestion.text || suggestion.label || '';
      if (text) {
        setInputText(text);
        if (inputRef.current) {
          inputRef.current.focus();
        }
        // Auto-send if it's a simple text suggestion
        setTimeout(() => {
          handleSendMessage();
        }, 100);
        return;
      }
    }
    // Otherwise treat as string
    const suggestionText = String(suggestion || '');
    setInputText(suggestionText);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Generate dynamic suggestions based on conversation state
  const generateDynamicSuggestions = useCallback(() => {
    const lastMessage = messages[messages.length - 1];
    const isLastMessageFromAI = lastMessage?.role === 'assistant';
    const suggestions = [];

    // If conversation is empty, use default suggestions
    if (messages.length === 0) {
      return config.quickStart && config.quickStart.length > 0 
        ? config.quickStart 
        : quickSuggestions;
    }

    // After AI response, suggest follow-ups
    if (isLastMessageFromAI && lastMessage?.content) {
      const content = lastMessage.content.toLowerCase();
      
      // Suggest using ready tools
      if (readyTools && readyTools.length > 0) {
        const tool = readyTools[0];
        suggestions.push(`Use ${tool.name || tool.label || 'this tool'}`);
      }

      // Context-aware follow-ups based on AI response
      if (content.includes('framework') || content.includes('tool')) {
        suggestions.push('Show me how to apply this');
        suggestions.push('What are the next steps?');
      } else if (content.includes('plan') || content.includes('strategy')) {
        suggestions.push('Help me implement this');
        suggestions.push('What should I do first?');
      } else if (content.includes('analysis') || content.includes('calculate')) {
        suggestions.push('Run a deeper analysis');
        suggestions.push('What does this mean for me?');
      } else {
        // Generic follow-ups
        suggestions.push('Tell me more about this');
        suggestions.push('What should I do next?');
      }

      // If missing context, suggest providing it
      if (missingDataItems && missingDataItems.length > 0) {
        const missing = missingDataItems[0];
        if (missing && missing.label) {
          suggestions.push(`Share ${missing.label.toLowerCase()}`);
        }
      }
      
      // Proactive prompts for "almost ready" frameworks (missing 1-2 context fields)
      if (lockedTools && lockedTools.length > 0) {
        const almostReadyTools = lockedTools.filter(tool => {
          if (!tool.needs || !Array.isArray(tool.needs)) return false;
          const missing = tool.needs.filter(need => {
            // Check various possible key formats
            const needKey = need.toLowerCase();
            return !conversationData[need] && 
                   !conversationData[needKey] && 
                   !conversationData[need.replace(/_/g, '')];
          });
          return missing.length > 0 && missing.length <= 2; // 1-2 missing fields
        });
        
        if (almostReadyTools.length > 0) {
          const tool = almostReadyTools[0];
          const missing = tool.needs.filter(need => {
            const needKey = need.toLowerCase();
            return !conversationData[need] && 
                   !conversationData[needKey] && 
                   !conversationData[need.replace(/_/g, '')];
          });
          
          if (missing.length > 0) {
            const fieldName = missing[0].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            const toolName = tool.name || tool.label || 'this framework';
            suggestions.unshift(`🎯 To unlock ${toolName}, share your ${fieldName}`);
          }
        }
      }
    } else {
      // After user message, suggest tools or actions
      if (readyTools && readyTools.length > 0) {
        const tool = readyTools[0];
        suggestions.push(`Try ${tool.name || tool.label || 'this tool'}`);
      }
      
      // Proactive prompts for "almost ready" frameworks (missing 1-2 context fields)
      if (lockedTools && lockedTools.length > 0) {
        const almostReadyTools = lockedTools.filter(tool => {
          if (!tool.needs || !Array.isArray(tool.needs)) return false;
          const missing = tool.needs.filter(need => {
            const needKey = need.toLowerCase();
            return !conversationData[need] && 
                   !conversationData[needKey] && 
                   !conversationData[need.replace(/_/g, '')];
          });
          return missing.length > 0 && missing.length <= 2;
        });
        
        if (almostReadyTools.length > 0) {
          const tool = almostReadyTools[0];
          const missing = tool.needs.filter(need => {
            const needKey = need.toLowerCase();
            return !conversationData[need] && 
                   !conversationData[needKey] && 
                   !conversationData[need.replace(/_/g, '')];
          });
          
          if (missing.length > 0) {
            const fieldName = missing[0].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            const toolName = tool.name || tool.label || 'this framework';
            suggestions.unshift(`🎯 Unlock ${toolName} - share your ${fieldName}`);
          }
        }
      }
      
      // Add vertical-specific suggestions
      if (quickSuggestions && quickSuggestions.length > 0) {
        suggestions.push(...quickSuggestions.slice(0, 2));
      }
    }

    // Fallback to defaults if no dynamic suggestions
    if (suggestions.length === 0) {
      return config.quickStart && config.quickStart.length > 0 
        ? config.quickStart 
        : quickSuggestions;
    }

    return suggestions.slice(0, 3);
  }, [messages, readyTools, lockedTools, missingDataItems, quickSuggestions, config.quickStart, conversationData]);

  // Simple contextual tool suggestions
  const getContextualToolSuggestions = (context, currentType) => {
    const suggestions = [];
    const tools = (COACHING_INSIGHTS[currentType]?.tools || []);
    // Business
    if (context.revenue && context.time_spent) {
      const roiTool = tools.find(t => t.id === 'roi');
      if (roiTool) suggestions.push({ ...roiTool, reason: `Revenue: ${context.revenue[0]}, Time: ${context.time_spent[0]}` });
    }
    if (context.challenges) {
      const bTool = tools.find(t => t.id === 'bottleneck');
      if (bTool) suggestions.push({ ...bTool, reason: `Challenge: ${String(context.challenges[0] || '').slice(0,30)}...` });
    }
    // Health
    if ((context.weight || context.current_weight) && currentType === 'health') {
      const pTool = tools.find(t => t.id === 'program');
      const w = (context.current_weight?.[0] || context.weight?.[0]);
      if (pTool) suggestions.push({ ...pTool, reason: `Weight data: ${w}` });
    }
    return suggestions.slice(0, 2);
  };

  // Real calculation layer for a couple of tools
  const generateRealResults = (toolId, context, currentType) => {
    if (toolId === 'roi' && currentType === 'business') {
      const revenue = parseFloat(String((context.revenue?.[0] || '0')).replace(/[^0-9.]/g, ''));
      const timeSpent = parseFloat(String((context.time_spent?.[0] || '0')).replace(/[^0-9.]/g, ''));
      if (revenue > 0 && timeSpent > 0) {
        const hourlyValue = revenue / (50 * 40);
        const annualSavings = timeSpent * 52 * Math.max(hourlyValue - 25, 0);
        const roiPct = revenue > 0 ? Math.round(((annualSavings - 15 * 52 * timeSpent) / revenue) * 100) : 0;
        return {
          title: 'Your Time Buyback Analysis',
          metrics: {
            'Your Time Value': `$${Math.round(hourlyValue)}/hour`,
            'Potential Savings': `$${Math.round(annualSavings).toLocaleString()}/year`,
            'ROI from $15/hr VA': `${roiPct}%`
          },
          actions: [
            'Hire virtual assistant for admin tasks',
            'Document your processes first',
            `Start with ${Math.max(1, Math.floor(timeSpent/2))} hours/week delegation`
          ],
          downloadable: `Time Buyback Plan\n\nCurrent Situation:\n- Revenue: $${Math.round(revenue).toLocaleString()}\n- Time on low-value: ${timeSpent}hrs/week\n- Your hourly value: $${Math.round(hourlyValue)}\n\nRecommendation:\n- Hire VA at $15-25/hour\n- Potential savings: $${Math.round(annualSavings).toLocaleString()}/year\n\nNext Steps:\n- Identify 3 repeatable tasks\n- Record them with Loom\n- Delegate for 30 days and review`
        };
      }
    }
    if (toolId === 'program' && currentType === 'health') {
      const currentWeight = parseFloat(String((context.current_weight?.[0] || context.weight?.[0] || '0')).replace(/[^0-9.]/g, ''));
      const goalWeight = parseFloat(String((context.goal_weight?.[0] || '0')).replace(/[^0-9.]/g, ''));
      if (currentWeight > 0) {
        const weeklyLoss = Math.abs(currentWeight - goalWeight) <= 20 ? 1 : 1.5;
        const timeline = goalWeight > 0 ? Math.ceil(Math.abs(currentWeight - goalWeight) / weeklyLoss) : 12;
        return {
          title: 'Your Personalized Program',
          metrics: {
            'Current Weight': `${currentWeight} lbs`,
            'Target Weight': `${goalWeight || 'Not set'} lbs`,
            'Timeline': `${timeline} weeks`
          },
          actions: [
            'Start with 3 workouts per week',
            'Focus on compound movements',
            'Track progress weekly'
          ],
          downloadable: `12-Week Program\n\n- 3x/week full-body split\n- Compound lifts (squat, hinge, push, pull)\n- Progress weekly 2-5% where possible\n- 1 conditioning session/week\n- Track sleep, steps, and protein`
        };
      }
    }
    return null;
  };

  const handleDownloadResult = (result) => {
    if (!result || !result.downloadable) return;
    const blob = new Blob([result.downloadable], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${String(result.title || 'analysis').replace(/[^a-zA-Z0-9]/g, '-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Clear conversation with confirmation
  const handleClearConversation = () => {
    if (confirm('Clear conversation history? This action cannot be undone.')) {
    setMessages([]);
      setSessionMetrics(prev => ({ ...prev, startTime: Date.now(), interactions: 0 }));
    }
  };

  // Enhanced MentorMind business tool functions
  const handleStageAssessment = () => {
    const assessments = {
      startup: {
        stage: 'Startup Phase',
        focus: ['Product-Market Fit', 'Initial Revenue', 'Core Team'],
        score: 65,
        recommendations: [
          'Focus on validating your value proposition',
          'Build repeatable sales processes',
          'Establish key performance metrics'
        ]
      },
      growth: {
        stage: 'Growth Phase', 
        focus: ['Scaling Operations', 'Team Building', 'Market Expansion'],
        score: 78,
        recommendations: [
          'Implement scalable systems and processes',
          'Focus on hiring A-players',
          'Optimize customer acquisition channels'
        ]
      },
      scale: {
        stage: 'Scale Phase',
        focus: ['Market Leadership', 'Operational Excellence', 'Strategic Vision'],
        score: 85,
        recommendations: [
          'Develop leadership team capabilities',
          'Create predictable revenue systems',
          'Explore strategic partnerships'
        ]
      }
    };
    setStageResults(assessments[businessStage]);
  };

  const calculateROI = () => {
    const revenue = parseFloat(roiInputs.currentRevenue.replace(/[$,]/g, '')) || 0;
    const hours = parseFloat(roiInputs.timeSpent) || 0;
    const rate = parseFloat(roiInputs.hourlyRate) || 0;
    
    if (revenue && hours && rate) {
      const annualSavings = hours * 52 * rate;
    const roiPercentage = Math.round((annualSavings / revenue) * 100);
      const monthlyPotential = Math.round(annualSavings / 12);
    
      setRoiResults({
        annualSavings,
        roiPercentage,
        monthlyPotential,
        breakEvenWeeks: Math.ceil(10000 / (hours * rate)) // Assuming $10k investment
      });
    }
  };

  const calculateEfficiency = () => {
    const team = parseInt(efficiencyInputs.teamSize) || 0;
    const revenue = parseFloat(efficiencyInputs.revenue.replace(/[$,]/g, '')) || 0;
    
    if (team && revenue) {
      const revenuePerEmployee = Math.round(revenue / team);
      const efficiency = revenuePerEmployee > 150000 ? 'Excellent' : 
                       revenuePerEmployee > 100000 ? 'Good' : 'Needs Improvement';
      
      setEfficiencyResults({
        revenuePerEmployee,
        efficiency,
        teamSize: team,
        totalRevenue: revenue,
        benchmark: efficiency === 'Excellent' ? 'Top 10%' : 
                  efficiency === 'Good' ? 'Top 25%' : 'Below Average'
      });
    }
  };

  // Dan Martell Business Features
  const calculateBusinessHealth = () => {
    const { arr, team, stage } = businessMetrics;
    const revenue = parseFloat(arr.replace(/[$,MK]/g, '')) || 0;
    const teamSize = parseInt(team) || 0;
    
    let scores = {
      timeManagement: 60, // Base score
      teamStructure: 50,
      systemsProcesses: 40,
      revenueGrowth: 70,
      leadership: 55
    };
    
    // Adjust based on metrics
    if (revenue > 1) scores.revenueGrowth += 20;
    if (teamSize > 10) scores.teamStructure += 25;
    if (stage === 'scale') {
      scores.systemsProcesses += 20;
      scores.leadership += 15;
    }
    
    const categories = {};
    Object.keys(scores).forEach(key => {
      const score = Math.min(100, scores[key]);
      categories[key] = {
        score,
        status: score >= 80 ? 'excellent' : score >= 60 ? 'good' : 'needs-work'
      };
    });
    
    const overallScore = Math.round(Object.values(scores).reduce((a, b) => a + b, 0) / 5);
    
    const recommendations = [];
    if (categories.timeManagement.score < 70) {
      recommendations.push('Implement Buy Back Your Time framework');
    }
    if (categories.teamStructure.score < 70) {
      recommendations.push('Use Delegation Ladder for next hire');
    }
    if (categories.systemsProcesses.score < 70) {
      recommendations.push('Document processes with 4-Week Rule');
    }
    
    setBusinessHealth({
      score: overallScore,
      categories,
      recommendations
    });
  };

  const generateActionPlan = (framework, metrics) => {
    const plans = {
      'buyback': {
        framework: 'Buy Back Your Time',
        actions: [
          { task: 'Audit current time allocation', priority: 'high', deadline: '1 week' },
          { task: 'Identify $10-25/hr tasks to delegate', priority: 'high', deadline: '1 week' },
          { task: 'Create SOPs for delegated tasks', priority: 'medium', deadline: '2 weeks' },
          { task: 'Hire virtual assistant', priority: 'high', deadline: '3 weeks' }
        ]
      },
      'replacement-ladder': {
        framework: 'Delegation Ladder',
        actions: [
          { task: 'Map current team structure', priority: 'high', deadline: '1 week' },
          { task: 'Identify next hire based on ARR/team size', priority: 'high', deadline: '1 week' },
          { task: 'Create job description and requirements', priority: 'medium', deadline: '2 weeks' },
          { task: 'Begin recruitment process', priority: 'high', deadline: '3 weeks' }
        ]
      }
    };
    
    const newPlan = { id: Date.now(), ...plans[framework] };
    setActionPlans(prev => [...prev, newPlan]);
  };

  const scheduleFollowUp = (message, daysFromNow) => {
    const scheduledDate = new Date();
    scheduledDate.setDate(scheduledDate.getDate() + daysFromNow);
    
    const reminder = {
      id: Date.now(),
      message,
      scheduledFor: scheduledDate,
      createdAt: new Date()
    };
    
    setFollowUpReminders(prev => [...prev, reminder]);
  };

  // Enterprise Session Stats Component
  const SessionStats = () => (
    <div className="ov-session-stats">
      <div className="ov-stats-grid">
        <div className="ov-stat-card">
          <div className="ov-stat-value">{Math.round((Date.now() - sessionMetrics.startTime) / 60000)}m</div>
          <div className="ov-stat-label">Session Time</div>
          </div>
        <div className="ov-stat-card">
          <div className="ov-stat-value">{sessionMetrics.interactions}</div>
          <div className="ov-stat-label">Interactions</div>
        </div>
        <div className="ov-stat-card">
          <div className="ov-stat-value">{sessionMetrics.timeSaved}m</div>
          <div className="ov-stat-label">Time Saved</div>
      </div>
        <div className="ov-stat-card">
          <div className="ov-stat-value">{sessionMetrics.efficiency}%</div>
          <div className="ov-stat-label">Efficiency</div>
        </div>
        </div>
      </div>
    );

  // Privacy components removed to clean up UI

  // Enterprise Navigation Component - Combined Voice/Chat Interface
  const EnterpriseNavigation = () => (
    <nav className="ov-enterprise-nav">
          <button
        className={`ov-nav-btn ${activeTab === 'chat' || activeTab === 'voice' ? 'active' : ''}`}
        onClick={() => handleTabChange('chat')}
        aria-label="Conversation"
          >
            <span className="ov-nav-icon">💬</span>
        <span className="ov-nav-label">Conversation</span>
          </button>
          <button
        className={`ov-nav-btn ${activeTab === 'coach' ? 'active' : ''}`}
        onClick={() => handleTabChange('coach')}
        aria-label="Notes"
          >
        <span className="ov-nav-icon">📝</span>
        <span className="ov-nav-label">Notes</span>
        {coachNotes.length > 0 && <span className="ov-nav-badge">{coachNotes.length}</span>}
          </button>
          {/* Documents tab - hidden for MentorMind vertical */}
          {vertical !== 'mentormind' && (
            <button
        className={`ov-nav-btn ${activeTab === 'documents' ? 'active' : ''}`}
        onClick={() => handleTabChange('documents')}
        aria-label="Documents"
          >
        <span className="ov-nav-icon">📚</span>
        <span className="ov-nav-label">Knowledge</span>
        {docs.length > 0 && <span className="ov-nav-badge">{docs.length}</span>}
            </button>
          )}
          {/* Actions/Tools tab - hidden for HVAC vertical */}
          {vertical !== 'hvac' && (
            <button
        className={`ov-nav-btn ${activeTab === 'insights' ? 'active' : ''}`}
        onClick={() => handleTabChange('insights')}
            aria-label={(vertical === 'business' || vertical === 'opsiq' || vertical === 'pai3') ? 'Actions' : 'Available Tools'}
          >
            <span className="ov-nav-icon">{(vertical === 'business' || vertical === 'opsiq' || vertical === 'pai3') ? '⚡' : '💡'}</span>
            <span className="ov-nav-label">{(vertical === 'business' || vertical === 'opsiq' || vertical === 'pai3') ? 'Actions' : 'Tools'}</span>
            {readyTools.length > 0 && (
              <span className="ov-nav-badge">{readyTools.length}</span>
            )}
            </button>
          )}
          {/* HVAC Calculators tab - HVAC vertical only */}
          {vertical === 'hvac' && (
            <button
              className={`ov-nav-btn ${activeTab === 'calculators' ? 'active' : ''}`}
              onClick={() => handleTabChange('calculators')}
              aria-label="HVAC Calculators"
            >
              <span className="ov-nav-icon">🧮</span>
              <span className="ov-nav-label">Calcs</span>
            </button>
          )}
    </nav>
  );

  // Enhanced conversation tab renderer
  const renderConversationTab = () => (
    <div className="ov-conversation-enhanced">
      <div className="ov-conversation-header">
        <h3 className="ov-section-title">Conversation History</h3>
        <div className="ov-conversation-controls">
          <button className="ov-control-btn" onClick={copyConversation}>
            📋 Copy All
          </button>
          <button className="ov-control-btn" onClick={() => exportConversation('markdown')}>
            📋 Copy
          </button>
          <button className="ov-control-btn" onClick={() => exportConversation('txt')}>
            💾 Export TXT
          </button>
          <button className="ov-control-btn" onClick={clearConversation}>
            🗑️ Clear
          </button>
            </div>
              </div>
      
      <div className="ov-conversation-stats">
        <div className="ov-stat-pill">
          <span>💬 Messages: {messages.length}</span>
              </div>
        <div className="ov-stat-pill">
                          <span>⏱️ Session: {Math.round((Date.now() - sessionMetrics.startTime) / 60000)}m</span>
            </div>
        <div className="ov-stat-pill">
          <span>🎯 Interactions: {interactionCount}</span>
          </div>
      </div>

      <div className="ov-messages-history">
              {messages.length === 0 ? (
                <div className="ov-empty">
            No conversation history yet. Start chatting to see your messages here!
                </div>
              ) : (
          messages.map((msg, idx) => (
            <MessageRow key={msg.id || msg.timestamp || idx} msg={msg} docs={docs} />
                ))
              )}
            </div>
    </div>
  );

  // Filter messages for search
  const filteredMessages = messages.filter(msg =>
    searchQuery === '' || msg.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate session stats
  const sessionDuration = Math.round((Date.now() - sessionMetrics.startTime) / 1000 / 60);
  const timeSaved = Math.round(interactionCount * 2.5); // Estimate 2.5 minutes saved per interaction

  // Loading message based on vertical
  const getLoadingMessage = () => {
    const msgs = [
      'Analyzing your question...',
      'Searching knowledge base...',
      'Generating insights...',
      'Almost ready...'
    ];
    return msgs[Math.floor(Math.random()*msgs.length)];
  };

  // Vertical-specific placeholders
  const placeholder = useMemo(() => {
    if (vertical === 'hvac') {
      return 'Type to start chatting...';
    } else if (vertical === 'mentormind') {
      if (coachingType === 'business') {
        return 'Type to start chatting...';
      } else if (coachingType === 'health') {
        return 'Type to start chatting...';
      } else if (coachingType === 'sales') {
        return 'Type to start chatting...';
      } else if (coachingType === 'life') {
        return 'Type to start chatting...';
      } else if (coachingType === 'career') {
        return 'Type to start chatting...';
      } else {
        return 'Type to start chatting...';
      }
    } else if (vertical === 'business' || vertical === 'opsiq') {
      return 'Type to start chatting...';
    }
    return config.placeholder || 'Type to start chatting...';
  }, [vertical, coachingType, config.placeholder]);

  const displayTitle = useMemo(() => {
    // For pai3 and other custom verticals, use backend config name directly
    if (vertical === 'pai3') {
      const title = companyConfig?.ui?.name || config.title;
      return title;
    }
    const humanizeCompanySlug = (slug) => {
      const s = String(slug || '').trim().toLowerCase();
      if (!s) return '';
      // Remove common prefixes
      const cleaned = s
        .replace(/^business-/, '')
        .replace(/^opsiq-/, '')
        .replace(/^company-/, '')
        .replace(/-default$/, '')
        .trim();
      const words = cleaned.split(/[-_]+/).filter(Boolean);
      const titled = words.map(w => (w.length ? (w[0].toUpperCase() + w.slice(1)) : w)).join(' ');
      if (!titled) return '';
      return /intelligence$/i.test(titled) ? titled : `${titled} Intelligence`;
    };

    // Personalized branding for opsIQ/business vertical
    if (vertical === 'business' || vertical === 'opsiq') {
      // Prefer backend-provided company display name (fallbacks included)
      const rawName = String(
        companyConfig?.ui?.name ||
        companyConfig?.name ||
        companyConfig?.company_name ||
        companyConfig?.company ||
        companyConfig?.display_name ||
        ''
      ).trim();
      if (rawName) {
        // Keep stable: company name only (no suffixes)
        const cleanName = rawName.replace(/\s*(Business|AI|Assistant)\s*$/i, '').trim();
        return cleanName || config.title;
      }

      // Last-resort: derive from company slug so it never shows generic "Business"
      const slugTitle = humanizeCompanySlug(companyIdSlug);
      if (slugTitle) return slugTitle;
    }
    // MentorMind coaching specialization
    if (vertical === 'mentormind') {
      return COACHING_TYPES[coachingType]?.title || config.title;
    }
    return config.title;
  }, [vertical, coachingType, config.title, companyConfig]);

  const displaySubtitle = useMemo(() => {
    // For pai3, use backend subtitle if available
    if (vertical === 'pai3') {
      const subtitle = companyConfig?.ui?.subtitle || config.subtitle;
      return subtitle;
    }
    if (vertical === 'mentormind') {
      return COACHING_TYPES[coachingType]?.subtitle || config.subtitle;
    }
    return config.subtitle;
  }, [vertical, coachingType, config.subtitle, companyConfig]);
  // Simplified: manual insight placeholder not used
  const manualPlaceholder = '';
  const getPlaceholderText = () => placeholder;

  // Shape backend replies into short, conversational coaching style with a follow-up question
  const shapeCoachReply = (raw, type, ctx) => {
    const answer = String(raw || '').trim();
    const sentences = answer.split(/(?<=[.!?])\s+/).filter(Boolean);
    const short = sentences.slice(0, 2).join(' ');
    const pick = (arr) => arr[Math.floor(Math.random()*arr.length)];
    const followUps = {
      health: [
        "What does your week look like — mornings or evenings work better?",
        "What's your current and target weight?",
        "Do you prefer gym, home, or outdoor workouts?"
      ],
      business: [
        "What's the most draining task we can delegate first?",
        "Where's the bottleneck right now — sales, delivery, or ops?",
        "How many hours/week are you in low‑value work?"
      ],
      sales: [
        "Where do deals usually stall — discovery, proposal, or pricing?",
        "What's your current close rate and target?",
        "Which objection shows up the most?"
      ],
      life: [
        "What would 'better' look like this week?",
        "Is the main challenge clarity, habits, or energy?",
        "What's one small win you want by Friday?"
      ],
      career: [
        "Are you aiming for a promotion or a switch?",
        "Which role are you targeting next?",
        "Want help tightening your resume bullets?"
      ],
      relationship: [
        "Is this more about communication or boundaries?",
        "When does this usually flare up?",
        "Want to try a quick script for the next convo?"
      ],
      financial: [
        "Is the stress more debt, budgeting, or savings?",
        "Do you want a simple spending plan to start?",
        "What's the one money win you want this month?"
      ],
      leadership: [
        "Is this a team issue or a decision clarity issue?",
        "Want help mapping a quick delegation plan?",
        "Which meeting would you fix first?"
      ],
      ecommerce: [
        "Is the pain more ads, conversion, or ops?",
        "Want to review top SKUs or ad spend first?",
        "Where do shoppers drop off most?"
      ],
      default: [
        "What's the quickest win you want this week?",
        "Want me to draft a 2‑step plan?",
        "Where should we focus first?"
      ]
    };
    const list = followUps[type] || followUps.default;
    const follow = pick(list);
    const body = short || answer || "Got it. Let's keep this practical.";
    return `${body} ${follow}`.trim();
  };

  // Render Functions
  const renderVoiceTab = () => (
    <div className="ov-enterprise-voice">
      
      {!isOnline && (
        <div className="ov-enterprise-alert ov-alert-warning">
          <span className="ov-alert-icon">📡</span>
          <span>Offline mode - Voice features require internet connection</span>
              </div>
            )}
      
      {voiceError && (
        <div className="ov-enterprise-alert ov-alert-error">
          <span className="ov-alert-icon">⚠️</span>
          <span>{voiceError}</span>
          <button className="ov-alert-action" onClick={() => setVoiceError(null)}>Dismiss</button>
          </div>
        )}

      <div className="ov-enterprise-mic-wrapper">
        <button
          className={`ov-enterprise-mic ${isListening ? 'listening' : ''} ${!hasVapi || !isOnline ? 'disabled' : ''}`}
          onClick={isListening ? stopVoiceCall : startVoiceCall}
          disabled={!hasVapi || !isOnline}
          aria-label={isListening ? 'Stop voice call' : 'Start voice call'}
        >
          <div className="ov-mic-rings">
            <div className="ov-mic-ring ov-ring-1"></div>
            <div className="ov-mic-ring ov-ring-2"></div>
            <div className="ov-mic-ring ov-ring-3"></div>
              </div>
          <div className="ov-enterprise-mic-inner">
            <svg className="ov-enterprise-mic-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="9" y="3" width="6" height="11" rx="3" ry="3" fill="currentColor" stroke="none" />
              <path d="M5 11v1a7 7 0 0 0 14 0v-1" />
              <path d="M12 19v3" />
              <path d="M8 22h8" />
            </svg>
            <span aria-hidden="true" style={{position:'absolute', opacity:0}}>🎙️</span>
                      </div>
        </button>
                    </div>

      <div className="ov-enterprise-mic-status">
        <div className="ov-status-text">
          {isListening ? 'Listening for your voice...' : 'Ready to start conversation'}
                    </div>
        <div className="ov-status-hint">
          {isListening ? 'Speak clearly into your microphone' : 'Click the microphone to begin'}
                  </div>
        {liveTranscript && (
          <div className="ov-live-preview">Live: "{liveTranscript}"</div>
        )}
              </div>

      {/* Live Transcription - show actual transcript text (user + assistant) */}
      {isListening && (
        <div className="ov-enterprise-transcript">
          <div className="ov-transcript-label">🎤 LIVE TRANSCRIPTION</div>
          <div className="ov-transcript-text">
            {liveTranscript ? (
              <div>You: {liveTranscript}</div>
            ) : (
              recentUserTranscripts.slice(-1).map((t, idx) => (
                <div key={`live-user-${idx}`}>You: {t}</div>
              ))
            )}
            {recentAssistantTranscripts.slice(-1).map((t, idx) => (
              <div key={`live-assistant-${idx}`}>Assistant: {t}</div>
            ))}
            {!liveTranscript && recentUserTranscripts.length === 0 && recentAssistantTranscripts.length === 0 && (
              <div>Listening...</div>
            )}
          </div>
        </div>
      )}

      {/* Voice post-session suggestions */}
      {!isListening && voiceSuggestions && voiceSuggestions.length > 0 && (
        <div className="ov-voice-suggestions">
          {voiceSuggestions.map((s, i) => (
            <div key={i} className="ov-voice-suggestion-card">
              <div className="ov-card-title">I noticed you mentioned {s.reason}. Want me to:</div>
                              <button className="ov-suggestion-chip" onClick={() => { setActiveTab('insights'); }}>{s.label}</button>
            </div>
          ))}
        </div>
      )}

      {/* Removed separate Assistant Speaking panel to avoid duplicates */}

      <div className="ov-enterprise-controls">
        <div className="ov-enterprise-volume">
          <span>🔊</span>
                  <input 
            type="range"
            min="0"
            max="100"
            value={volume}
            onChange={(e) => setVolume(e.target.value)}
            aria-label="Volume"
          />
          <span>{volume}%</span>
                </div>
        <label className="ov-voice-save-toggle" style={{display:'inline-flex',alignItems:'center',gap:8,marginLeft:12}}>
          <input 
            type="checkbox" 
            checked={voiceAutoSave} 
            onChange={(e) => { setVoiceAutoSave(e.target.checked); }}
          />
          <span>Auto-save to History</span>
        </label>
        <button
          className="ov-control-btn"
          onClick={stopVoiceCall}
          disabled={!isListening}
          aria-label="End voice session"
          title="End voice session"
          style={{marginLeft:12}}
        >
          ⏹️ End
        </button>
        {/* Temporary test button for voice RAG verification */}
        
      </div>

      {/* Simple waveform visualization */}
      <div className="voice-waveform">
        {[1,2,3,4,5].map(i => (
          <div key={i} className={`wave-bar ${isAssistantSpeaking ? 'active' : ''}`} style={{animationDelay: `${i * 0.1}s`}} />
        ))}
      </div>

      {/* removed duplicate live transcript sections below */}

      <SessionStats />

      {!hasVapi && (
        <div className="ov-enterprise-help">
          <span className="ov-help-icon">ℹ️</span>
          <span>Voice features require proper configuration. Contact your administrator for setup assistance.</span>
        </div>
      )}
    </div>
  );

  // Copy conversation to clipboard
  const handleCopyConversation = async () => {
    const sessionDuration = (Date.now() - sessionMetrics.startTime) / 1000;
    const content = formatExport(messages, 'txt', { vertical, companyIdSlug, sessionDuration, messageCount: messages.length });
    try {
      await navigator.clipboard.writeText(content);
      addToast('Conversation copied to clipboard', 'success');
    } catch (err) {
      console.error('Failed to copy conversation:', err);
      addToast('Failed to copy conversation', 'error');
    }
  };

  const renderChatTab = () => (
    <div className="ov-enterprise-chat">
      <div className="ov-chat-header">
        <div className="ov-quick-actions">
          <button className="ov-quick-btn" onClick={handleCopyConversation} title="Copy conversation" aria-label="Copy conversation">
            <Copy size={18} strokeWidth={2} />
          </button>
          <button className="ov-quick-btn" onClick={() => handleExport('markdown')} title="Export conversation" aria-label="Export conversation">
            <Download size={18} strokeWidth={2} />
          </button>
          <button className="ov-quick-btn" onClick={handleClearConversation} title="Clear conversation" aria-label="Clear conversation">
            <Trash2 size={18} strokeWidth={2} />
          </button>
        </div>
      </div>

      {/* Floating Voice Button - Always accessible on mobile for PAI3/OpsIQ */}
      {isOpsMobileVertical && isMobile && (
        <FloatingVoiceButton
          onClick={isRecording ? handleStopRecording : handleStartRecording}
          isRecording={isRecording}
          context={null}
          vertical={vertical}
          activeTab={activeTab}
          disabled={subscriptionTier === 'starter'}
        />
      )}

      {/* Menu Drawer for PAI3/OpsIQ */}
      {isOpsMobileVertical && (
        <MenuDrawer
          isOpen={isMenuDrawerOpen}
          onClose={() => setIsMenuDrawerOpen(false)}
          onOpenSessions={() => setIsSessionsOpen(true)}
          onOpenDocuments={() => {
            setActiveTab('documents');
            setIsMenuDrawerOpen(false);
          }}
          onOpenSettings={() => {
            addToast('Settings coming soon!', 'info');
            setIsMenuDrawerOpen(false);
          }}
          onOpenHelp={() => {
            addToast('Help & Support: Contact us at support@opsiq.com', 'info');
            setIsMenuDrawerOpen(false);
          }}
          onOpenAbout={() => {
            addToast('OpsIQ Intelligence - Your intelligent operations assistant', 'info');
            setIsMenuDrawerOpen(false);
          }}
          vertical={vertical}
        />
      )}

      {/* Settings Menu for three dots button - Mobile and Browser */}
      {isOpsMobileVertical && (
        <SettingsMenu
          isOpen={isMoreMenuOpen}
          onClose={() => {
            setIsMoreMenuOpen(false);
            setSettingsMenuAnchor(null);
          }}
          anchorElement={settingsMenuAnchor}
          vertical={vertical}
          onOpenSettings={() => {
            addToast('Settings coming soon!', 'info');
            setIsMoreMenuOpen(false);
            setSettingsMenuAnchor(null);
          }}
          onOpenHelp={() => {
            addToast('Help & Support: Contact us at support@opsiq.com', 'info');
            setIsMoreMenuOpen(false);
            setSettingsMenuAnchor(null);
          }}
          onOpenAbout={() => {
            addToast('OpsIQ Intelligence - Your intelligent operations assistant', 'info');
            setIsMoreMenuOpen(false);
            setSettingsMenuAnchor(null);
          }}
          onClearChat={() => {
            handleClearConversation();
            setIsMoreMenuOpen(false);
            setSettingsMenuAnchor(null);
          }}
          onShareSession={() => {
            if (messages.length === 0) {
              addToast('No conversation to share', 'info');
            } else {
              const conversationText = messages.map(m => `${m.role}: ${m.content}`).join('\n\n');
              if (navigator.share) {
                navigator.share({
                  title: 'OpsIQ Conversation',
                  text: conversationText
                }).catch(() => {
                  // Fallback to clipboard
                  navigator.clipboard.writeText(conversationText);
                  addToast('Conversation copied to clipboard', 'success');
                });
              } else {
                navigator.clipboard.writeText(conversationText);
                addToast('Conversation copied to clipboard', 'success');
              }
            }
            setIsMoreMenuOpen(false);
            setSettingsMenuAnchor(null);
          }}
          onExportChat={async () => {
            if (messages.length === 0) {
              addToast('No conversation to export', 'info');
            } else {
              const conversationText = messages.map(m => `${m.role}: ${m.content}`).join('\n\n');
              const filename = `opsiq-conversation-${new Date().toISOString().split('T')[0]}.txt`;
              try {
                const blob = new Blob([conversationText], { type: 'text/plain' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                a.rel = 'noopener';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                addToast('Conversation exported', 'success');
              } catch (e) {
                // Common failure in sandboxed iframes: fallback to clipboard
                try {
                  await navigator.clipboard.writeText(conversationText);
                  addToast('Export blocked in embed — conversation copied to clipboard', 'info');
                } catch (_) {
                  addToast('Export blocked in embed — please copy from chat', 'info');
                }
              }
            }
            setIsMoreMenuOpen(false);
            setSettingsMenuAnchor(null);
          }}
        />
      )}

      <MobileSessionsDrawer
        isOpen={isOpsMobileVertical && isSessionsOpen}
        onClose={() => setIsSessionsOpen(false)}
        sessions={opsSessions}
        onNewChat={() => {
          setMessages([]);
          setActiveTab('chat');
          setConversationContext({});
          setIsSessionsOpen(false);
        }}
        onOpenSession={(session) => {
          try {
            // Sessions can have either 'messages' (stored) or 'messagesSnapshot' (temporary)
            const sessionMessages = session?.messagesSnapshot || session?.messages;
            
            if (sessionMessages && Array.isArray(sessionMessages) && sessionMessages.length > 0) {
              setMessages(sessionMessages);
              setActiveTab('chat');
              setIsSessionsOpen(false);
              // Prefer Type mode for reviewing
              try { localStorage.setItem(mobileModeStorageKey, 'type'); } catch (_) {}
              setMobileMode('type');
              setTimeout(() => {
                try { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); } catch (_) {}
              }, 50);
            } else {
              
              addToast('This session has no messages to display.', 'warning');
            }
          } catch (e) {
            console.error('[OpsSessions] Failed to open session:', e, session);
            addToast('Failed to load session. Please try again.', 'error');
          }
        }}
      />

      {/* Toasts */}
      {toasts.length > 0 && (
        <div className="ov-toasts" role="status" aria-live="polite">
          {toasts.map(t => (
            <div key={t.id} className={`ov-toast ov-toast-${t.variant}`}>{t.message}</div>
          ))}
        </div>
      )}

      {/* Compact Tool Notification */}
      {insightNotification && (
        <div className="ov-compact-notification">
          <div className="ov-compact-content">
            <span className="ov-compact-icon">🎯</span>
            <span className="ov-compact-text">{insightNotification.message}</span>
            <button 
              className="ov-compact-btn"
              onClick={() => {
                setSelectedTool(insightNotification.tool);
                setShowInsightModal(true);
                setInsightNotification(null);
              }}
            >
              Run
            </button>
            <button 
              className="ov-compact-close"
              onClick={() => setInsightNotification(null)}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {searchQuery && (
        <div className="ov-enterprise-search">
                  <input 
                    type="text" 
            className="ov-enterprise-search-input"
            placeholder="Search conversation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
      )}

      {/* Split Layout: Voice + Conversation (desktop always, or mobile Type mode). Mobile Talk mode gets a centered CTA screen. */}
      {(!isMobile || (isOpsMobileVertical && mobileMode)) && (
      <div className={`ov-chat-split-layout ${isOpsMobileVertical ? `ov-ops-mobile-mode-${mobileMode}` : ''}`}>
        {/* Mobile Talk mode: centered mic CTA + transcript */}
        {isOpsMobileVertical && isMobile && mobileMode === 'talk' ? (
          <div className="ov-talk-mode-screen" role="region" aria-label="Talk mode">
            <div className="ov-talk-mode-inner">
              <button
                type="button"
                className={`ov-talk-cta-btn ${isRecording ? 'recording' : ''}`}
                onClick={isRecording ? handleStopRecording : handleStartRecording}
                aria-label={isRecording ? 'Stop recording' : 'Start voice recording'}
                title={isRecording ? 'Stop' : 'Tap to speak'}
              >
                <div className="ov-talk-cta-rings" aria-hidden="true">
                  <div className="ov-mic-pulse-ring"></div>
                  <div className="ov-mic-pulse-ring-delayed"></div>
                </div>
                <svg className="ov-mic-icon-large" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M12 2C10.9 2 10 2.9 10 4V12C10 13.1 10.9 14 12 14C13.1 14 14 13.1 14 12V4C14 2.9 13.1 2 12 2Z" fill="currentColor"/>
                  <path d="M19 10V12C19 15.9 15.9 19 12 19C8.1 19 5 15.9 5 12V10H3V12C3 16.4 6.6 20 11 20V22H13V20C17.4 20 21 16.4 21 12V10H19Z" fill="currentColor"/>
                </svg>
              </button>

              <div className="ov-talk-cta-label">
                {isRecording ? 'Listening…' : 'Tap to speak'}
              </div>
              <div className="ov-talk-cta-subtitle">
                We’ll add your voice transcript to the chat automatically after you finish.
              </div>

              <div className="ov-live-transcription-panel ov-live-transcription-panel-talk">
                {isRecording && (
                  <div className="ov-voice-waveform" aria-hidden="true">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
                      <div key={i} className="ov-waveform-bar" style={{ animationDelay: `${i * 0.08}s` }}></div>
                    ))}
                  </div>
                )}
                <div className="ov-live-transcript-text">
                  {(currentTranscript || liveTranscript) && (
                    <div className="ov-transcript-user">
                      <span className="ov-transcript-label">You:</span>
                      {currentTranscript || liveTranscript}
                    </div>
                  )}
                  {(isAssistantSpeaking || recentAssistantTranscripts.length > 0) && (
                    <div className="ov-transcript-assistant">
                      <span className="ov-transcript-label">AI:</span>
                      {recentAssistantTranscripts.length > 0
                        ? recentAssistantTranscripts[recentAssistantTranscripts.length - 1]
                        : (isAssistantSpeaking ? 'Speaking…' : '')
                      }
                    </div>
                  )}
                  {!currentTranscript && !liveTranscript && !isAssistantSpeaking && recentAssistantTranscripts.length === 0 && (
                    <div className="ov-transcript-empty">
                      {isRecording ? 'Listening…' : 'Your transcript will appear here.'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
        <>
        {/* Left Side: Voice Controls with Pulsing Mic + Live Transcription */}
        <div
          className="ov-voice-sidebar"
          style={isOpsMobileVertical && mobileMode === 'type' ? { display: 'none' } : undefined}
        >
          <div className="ov-voice-controls-panel">
            {/* Pulsing Mic Button - Always visible */}
        <button
              className={`ov-sidebar-mic-btn ${isRecording ? 'recording' : ''}`}
              onClick={isRecording ? handleStopRecording : handleStartRecording}
              aria-label={isRecording ? "Stop recording" : "Start voice recording"}
              type="button"
            >
              {isRecording ? (
                <>
                  <div className="ov-mic-pulse-ring"></div>
                  <div className="ov-mic-pulse-ring-delayed"></div>
                </>
              ) : (
                <>
                  <div className="ov-mic-pulse-ring"></div>
                  <div className="ov-mic-pulse-ring-delayed"></div>
                </>
              )}
              <svg className="ov-mic-icon-large" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C10.9 2 10 2.9 10 4V12C10 13.1 10.9 14 12 14C13.1 14 14 13.1 14 12V4C14 2.9 13.1 2 12 2Z" fill="currentColor"/>
                <path d="M19 10V12C19 15.9 15.9 19 12 19C8.1 19 5 15.9 5 12V10H3V12C3 16.4 6.6 20 11 20V22H13V20C17.4 20 21 16.4 21 12V10H19Z" fill="currentColor"/>
              </svg>
        </button>
            
            <span className="ov-mic-label-sidebar">
              {isRecording ? 'Listening...' : 'Tap to speak'}
            </span>

            {/* End Call Button - Positioned next to mic */}
            {isRecording && (
              <button
                className="ov-voice-end-btn-sidebar"
                onClick={handleStopRecording}
                aria-label="End recording"
              >
                End
              </button>
            )}

            {/* Live Transcription Area */}
            <div className="ov-live-transcription-panel">
              {isRecording && (
                <div className="ov-voice-waveform">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="ov-waveform-bar" style={{ animationDelay: `${i * 0.1}s` }}></div>
                  ))}
                </div>
              )}
              <div className="ov-live-transcript-text">
                {/* User transcription */}
                {(currentTranscript || liveTranscript) && (
                  <div className="ov-transcript-user">
                    <span className="ov-transcript-label">You:</span>
                    {currentTranscript || liveTranscript}
                  </div>
                )}
                {/* AI transcription */}
                {(isAssistantSpeaking || recentAssistantTranscripts.length > 0) && (
                  <div className="ov-transcript-assistant">
                    <span className="ov-transcript-label">AI:</span>
                    {recentAssistantTranscripts.length > 0 
                      ? recentAssistantTranscripts[recentAssistantTranscripts.length - 1]
                      : (isAssistantSpeaking ? 'Speaking...' : '')
                    }
                  </div>
                )}
                {/* Empty state */}
                {!currentTranscript && !liveTranscript && !isAssistantSpeaking && recentAssistantTranscripts.length === 0 && (
                  <div className="ov-transcript-empty">
                    {isRecording ? 'Listening...' : 'Your conversation will appear here'}
                  </div>
                )}
              </div>
                  </div>

            {/* Removed: Always-visible suggested questions - keeping only the popup version */}
          </div>
        </div>
        </>
        )}

        {/* Right Side: Conversation Messages */}
        <div className="ov-conversation-sidebar">
          {/* Empty State for OpsIQ/Business - Welcome + Examples (Mobile + Desktop) - Moved below into ov-enterprise-messages */}
          
      <div className="ov-enterprise-messages">
          {/* Empty State for OpsIQ/Business - Welcome + Examples (Mobile + Desktop) */}
          {(vertical === 'opsiq' || vertical === 'business') && messages.length === 0 && !isRecording && (
            <div className="ov-empty-state-welcome" style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: isMobile ? '2rem 1.5rem' : '3rem 2rem',
              textAlign: 'center',
              minHeight: isMobile ? 'auto' : '60vh',
            }}>
              <div style={{ fontSize: isMobile ? '3rem' : '4rem', marginBottom: '1rem', color: '#0F172A' }}>
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: isMobile ? '3rem' : '4rem', height: isMobile ? '3rem' : '4rem' }}>
                  {/* Business Intelligence Icon - Analytics Brain with Chart */}
                  {/* Brain shape (top) */}
                  <path d="M32 10 C26 10, 20 14, 20 22 C20 26, 22 30, 24 32 L24 36 C24 38, 25 39, 26 39 L38 39 C39 39, 40 38, 40 36 L40 32 C42 30, 44 26, 44 22 C44 14, 38 10, 32 10 Z" fill="#0F172A" opacity="0.1"/>
                  <path d="M32 12 C27 12, 23 15, 23 22 C23 25, 25 28, 27 30 L27 34 L37 34 L37 30 C39 28, 41 25, 41 22 C41 15, 37 12, 32 12 Z" fill="#0F172A"/>
                  {/* Chart bars (bottom - analytics data visualization) */}
                  <rect x="12" y="46" width="5" height="8" fill="#14B8A6" rx="2"/>
                  <rect x="20" y="42" width="5" height="12" fill="#14B8A6" rx="2"/>
                  <rect x="28" y="38" width="5" height="16" fill="#0F172A" rx="2"/>
                  <rect x="36" y="40" width="5" height="14" fill="#14B8A6" rx="2"/>
                  <rect x="44" y="44" width="5" height="10" fill="#14B8A6" rx="2"/>
                  {/* Connecting lines from brain to chart */}
                  <path d="M32 34 L32 38" stroke="#14B8A6" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
                  <path d="M28 34 L28 38" stroke="#14B8A6" strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
                  <path d="M36 34 L36 38" stroke="#14B8A6" strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
                </svg>
              </div>
              <h3 style={{
                fontSize: isMobile ? '1.25rem' : '2rem',
                fontWeight: '700',
                color: '#111827',
                margin: '0 0 0.75rem 0',
                lineHeight: '1.2'
              }}>
                {isMobile ? (displayTitle || 'OpsIQ') : `Welcome to ${displayTitle || config.title || 'OpsIQ Intelligence'}`}
              </h3>
              <p style={{
                fontSize: isMobile ? '0.875rem' : '1rem',
                color: '#6B7280',
                margin: '0 0 1rem 0',
                maxWidth: '600px',
                lineHeight: '1.5'
              }}>
                Get instant answers from your company knowledge.
              </p>
              
              {/* AI Disclaimer - Compact */}
              <div style={{
                fontSize: isMobile ? '0.6875rem' : '0.75rem',
                color: '#9CA3AF',
                background: '#F9FAFB',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                padding: isMobile ? '6px 10px' : '8px 12px',
                marginBottom: '1.25rem',
                maxWidth: '600px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '6px'
              }}>
                <span style={{ fontSize: '0.875rem', flexShrink: 0 }}>🤖</span>
                <span style={{ lineHeight: '1.3' }}>
                  AI assistant. Verify critical answers with sources.
                </span>
              </div>
              
              <div className="ov-welcome-examples" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                width: '100%',
                maxWidth: '600px',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '0.5rem',
                  textAlign: 'left'
                }}>
                  Try asking:
                </div>
                {docs.length > 0 ? (
                  <>
                    <button 
                      className="ov-example-chip"
                      onClick={() => {
                        const firstDoc = docs[0];
                        const docName = firstDoc.title || firstDoc.filename || 'this document';
                        setInputText(`Summarize the key points from ${docName}`);
                        setTimeout(() => inputRef.current?.focus(), 0);
                      }}
                      style={{
                        background: 'white',
                        border: '1px solid #E5E7EB',
                        borderRadius: '0.75rem',
                        padding: '0.875rem 1rem',
                        fontSize: '0.875rem',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        color: '#111827'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#F9FAFB';
                        e.currentTarget.style.borderColor = '#3B82F6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.borderColor = '#E5E7EB';
                      }}
                    >
                      💡 Summarize our {docs[0]?.title || 'SOP'}
                    </button>
                    <button 
                      className="ov-example-chip"
                      onClick={() => {
                        setInputText('What is our policy on ');
                        setTimeout(() => inputRef.current?.focus(), 0);
                      }}
                      style={{
                        background: 'white',
                        border: '1px solid #E5E7EB',
                        borderRadius: '0.75rem',
                        padding: '0.875rem 1rem',
                        fontSize: '0.875rem',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        color: '#111827'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#F9FAFB';
                        e.currentTarget.style.borderColor = '#3B82F6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.borderColor = '#E5E7EB';
                      }}
                    >
                      📋 What's our policy on...
                    </button>
                    <button 
                      className="ov-example-chip"
                      onClick={() => {
                        setInputText('Find information about ');
                        setTimeout(() => inputRef.current?.focus(), 0);
                      }}
                      style={{
                        background: 'white',
                        border: '1px solid #E5E7EB',
                        borderRadius: '0.75rem',
                        padding: '0.875rem 1rem',
                        fontSize: '0.875rem',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        color: '#111827'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#F9FAFB';
                        e.currentTarget.style.borderColor = '#3B82F6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.borderColor = '#E5E7EB';
                      }}
                    >
                      🔍 Find info about...
                    </button>
                  </>
                ) : (
                  <>
                    <button 
                      className="ov-example-chip"
                      onClick={() => {
                        setInputText('Help me analyze our revenue trends');
                        setTimeout(() => inputRef.current?.focus(), 0);
                      }}
                      style={{
                        background: 'white',
                        border: '1px solid #E5E7EB',
                        borderRadius: '0.75rem',
                        padding: '0.875rem 1rem',
                        fontSize: '0.875rem',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        color: '#111827'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#F9FAFB';
                        e.currentTarget.style.borderColor = '#3B82F6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.borderColor = '#E5E7EB';
                      }}
                    >
                      📈 Help me analyze our revenue
                    </button>
                    <button 
                      className="ov-example-chip"
                      onClick={() => {
                        setInputText('Create a hiring plan for ');
                        setTimeout(() => inputRef.current?.focus(), 0);
                      }}
                      style={{
                        background: 'white',
                        border: '1px solid #E5E7EB',
                        borderRadius: '0.75rem',
                        padding: '0.875rem 1rem',
                        fontSize: '0.875rem',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        color: '#111827'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#F9FAFB';
                        e.currentTarget.style.borderColor = '#3B82F6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.borderColor = '#E5E7EB';
                      }}
                    >
                      👥 Create a hiring plan for...
                    </button>
                    <button 
                      className="ov-example-chip"
                      onClick={() => {
                        setInputText('What are best practices for ');
                        setTimeout(() => inputRef.current?.focus(), 0);
                      }}
                      style={{
                        background: 'white',
                        border: '1px solid #E5E7EB',
                        borderRadius: '0.75rem',
                        padding: '0.875rem 1rem',
                        fontSize: '0.875rem',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        color: '#111827'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#F9FAFB';
                        e.currentTarget.style.borderColor = '#3B82F6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.borderColor = '#E5E7EB';
                      }}
                    >
                      ✨ Best practices for...
                    </button>
                  </>
                )}
              </div>
              
              <div style={{
                fontSize: '0.8125rem',
                color: '#9CA3AF',
                marginTop: '1rem'
              }}>
                Or start typing below to get instant answers
              </div>
            </div>
          )}
          
          {/* Value Proposition Banner - Show when no messages (hidden for OpsIQ, Business, and PAI3 - they have full welcome section) */}
          {messages.length === 0 && !isRecording && vertical !== 'opsiq' && vertical !== 'business' && vertical !== 'pai3' && (
            <div className="ov-value-proposition-banner">
              <div className="ov-value-proposition-content">
                <div className="ov-value-icon">✨</div>
                <div className="ov-value-text">
                  <h3 className="ov-value-title">
                    {vertical === 'mentormind' 
                      ? "Ask anything to your AI coaching companion"
                      : vertical === 'hvac'
                      ? "Your AI-powered field assistant"
                      : "Your AI business assistant"}
                  </h3>
                  <p className="ov-value-subtitle">
                    {vertical === 'mentormind'
                      ? "Smart insights captured automatically • See sources from your coach's materials"
                      : vertical === 'hvac'
                      ? "Access company SOPs instantly • Get verified answers from your documentation"
                      : spreadsheetsEnabled
                        ? "Get instant answers from your documents • For dates & deadlines, use Knowledge → Spreadsheets"
                        : "Get instant answers • Access your business knowledge"}
                  </p>
                </div>
              </div>
            </div>
          )}
        {(docs && docs.length === 0 && messages.length > 0) && (
          audience === 'coach' ? (
            showCoachDocsHint ? (
              <div className="ov-enterprise-msg ov-msg-assistant">
                <div className="ov-enterprise-bubble ov-bubble-assistant">
                  <div className="ov-enterprise-hint">
                    {vertical === 'mentormind'
                      ? 'Upload your coaching playbooks to personalize answers.'
                      : vertical === 'hvac'
                      ? 'Upload your company SOPs and equipment manuals to personalize answers.'
                      : 'Upload your company documents to personalize answers.'
                    }
                  </div>
                </div>
              </div>
            ) : null
          ) : (
            <div className="ov-enterprise-msg ov-msg-assistant">
              <div className="ov-enterprise-bubble ov-bubble-assistant">
                <div className="ov-powered-note">
                  {vertical === 'mentormind' 
                    ? "Powered by your coach's materials."
                    : 'Powered by your company documentation.'}
                </div>
              </div>
            </div>
          )
        )}
        {/* Framework Progress Badge - shows when framework is active */}
        {frameworkExecutionActive && frameworkExecutionData && (
          <div className="ov-framework-progress-wrapper" style={{padding: '0 16px', marginBottom: '8px'}}>
            <FrameworkProgressBadge
              frameworkName={frameworkExecutionData.framework_name}
              currentStage={frameworkExecutionData.current_stage}
              totalStages={frameworkExecutionData.total_stages}
              currentStageTitle={frameworkExecutionData.current_stage_title}
              stages={frameworkExecutionData.stages}
              isCompleted={frameworkExecutionData.completed}
            />
          </div>
        )}
        
        {/* Framework Suggestion Banner - shows when AI detects matching intent */}
        {frameworkSuggestion && !frameworkExecutionActive && (
          <div className="ov-framework-suggestion" style={{
            padding: '12px 16px',
            margin: '8px 16px',
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            borderRadius: '12px',
            border: '1px solid #f59e0b',
            animation: 'fadeIn 0.3s ease'
          }}>
            <div style={{display: 'flex', alignItems: 'flex-start', gap: '12px'}}>
              <span style={{fontSize: '24px'}}>💡</span>
              <div style={{flex: 1}}>
                <div style={{fontWeight: 600, color: '#92400e', marginBottom: '4px'}}>
                  Framework Available
                </div>
                <div style={{fontSize: '13px', color: '#78350f', marginBottom: '8px'}}>
                  {frameworkSuggestion.suggestion_message}
                </div>
                <button 
                  onClick={() => {
                    setInputText(frameworkSuggestion.trigger_phrase);
                    setFrameworkSuggestion(null);
                    setTimeout(() => handleSendMessage(), 100);
                  }}
                  style={{
                    background: '#f59e0b',
                    color: 'white',
                    border: 'none',
                    padding: '6px 14px',
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  ▶️ Start {frameworkSuggestion.framework_name}
                </button>
                <button 
                  onClick={() => setFrameworkSuggestion(null)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '6px 14px',
                    color: '#92400e',
                    fontSize: '12px',
                    cursor: 'pointer',
                    marginLeft: '8px'
                  }}
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Inline live transcript bubble (OpsIQ/Business) */}
        {isOpsMobileVertical && isRecording && (
          <div className="ov-enterprise-msg ov-msg-assistant ov-inline-transcript-msg">
            <div className="ov-msg-avatar">LIVE</div>
            <div className="ov-msg-content">
              <div className="ov-msg-timestamp">Live transcript</div>
              <div className="ov-enterprise-bubble ov-bubble-assistant ov-inline-transcript-bubble">
                <div className="ov-msg-text">
                  {(currentTranscript || liveTranscript) ? (
                    <div className="ov-inline-transcript-line">
                      <span className="ov-inline-transcript-label">You</span>
                      <span className="ov-inline-transcript-text">{currentTranscript || liveTranscript}</span>
                    </div>
                  ) : null}

                  {(isAssistantSpeaking || (recentAssistantTranscripts && recentAssistantTranscripts.length > 0)) ? (
                    <div className="ov-inline-transcript-line">
                      <span className="ov-inline-transcript-label">AI</span>
                      <span className="ov-inline-transcript-text">
                        {recentAssistantTranscripts && recentAssistantTranscripts.length > 0
                          ? recentAssistantTranscripts[recentAssistantTranscripts.length - 1]
                          : (isAssistantSpeaking ? 'Speaking…' : '')}
                      </span>
                    </div>
                  ) : null}

                  {!currentTranscript && !liveTranscript && !isAssistantSpeaking && (!recentAssistantTranscripts || recentAssistantTranscripts.length === 0) ? (
                    <div className="ov-inline-transcript-empty">Listening…</div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        )}

        {filteredMessages.map((msg, idx) => (
          <div key={idx} className={`ov-enterprise-msg ov-msg-${msg.role}`}>
            {msg.role === 'assistant' ? (
              <div className="ov-msg-avatar">AI</div>
            ) : (
              <div className="ov-msg-avatar">You</div>
            )}
            <div className="ov-msg-content">
            <div className="ov-msg-timestamp">
              {new Date(msg.timestamp || Date.now()).toLocaleTimeString()}
              {msg.role === 'assistant' && msg.usesCoachDocs && (
                <span className="ov-source-badge" style={{marginLeft:8}}>
                  {vertical === 'hvac' ? 'Using Company SOPs' : (vertical === 'business' || vertical === 'opsiq' || vertical === 'pai3') ? 'Using Company Docs' : 'Using Coach Docs'}
                </span>
              )}
                </div>
            <div
                className={`ov-enterprise-bubble ov-bubble-${msg.role} ${msg.sources?.length ? 'has-sources' : ''}`}
              data-testid={msg.role === 'assistant' ? 'answer-card' : undefined}
                onMouseEnter={() => msg.role === 'assistant' && msg.sources?.length > 0 && setHoveredMessageId(msg.id || `msg-${idx}`)}
                onMouseLeave={() => setHoveredMessageId(null)}
              >
              {/* Removed "ask coach to upload" banner - using fallback with citations instead */}
              <div className="ov-msg-text">
                {/* Voice indicator for voice messages */}
                {(msg.type === 'voice' || msg.type === 'voice_rag') && (
                  <span className="ov-voice-indicator" title="Voice message">
                    🎤
                  </span>
                )}
                {cleanMessageContent(msg.content) || '(no content)'}
              </div>
              
              {/* Citations - show below message, collapsible */}
              {msg.role === 'assistant' && msg.sources && msg.sources.length > 0 && (() => {
                const aggregatedSources = dedupeAndLimitSources(msg.sources);
                return (
                  <div className="ov-msg-sources-inline" key={`sources-${idx}`}>
                    <details open={aggregatedSources.length <= 2}>
                      <summary className="ov-sources-summary">
                        📄 {aggregatedSources.length} source{aggregatedSources.length > 1 ? 's' : ''}
                      </summary>
                      <div className="ov-sources-list">
                        {aggregatedSources.map((g, i) => {
                    const icon = '📄';
                    const titleDisplay = cleanDocumentName(g.title);
                    
                    // Build display from sections array
                    let locationDisplay = '';
                    if (g.sections && g.sections.length > 0) {
                      // Format: "Section 4 (p. 1, 3)" or "Section 4 (p. 1); Section 2"
                      const sectionParts = g.sections.map(sec => {
                        if (sec.section === 'General') {
                          // No section, just show pages
                          return sec.pages.length > 0 
                            ? (sec.pages.length > 1 ? `p. ${sec.pages.join(', ')}` : `p. ${sec.pages[0]}`)
                            : '';
                        } else {
                          // Has section, show section with pages (if any)
                          if (sec.pages.length > 0) {
                            return sec.pages.length > 1
                              ? `${sec.section} (p. ${sec.pages.join(', ')})`
                              : `${sec.section} (p. ${sec.pages[0]})`;
                          } else {
                            // Section without page numbers
                            return sec.section;
                          }
                        }
                      }).filter(p => p); // Remove empty parts
                      
                      locationDisplay = sectionParts.length > 0 ? ` — ${sectionParts.join('; ')}` : '';
                    } else if (g.pages && g.pages.length > 0) {
                      // Fallback: just show pages if no sections
                      locationDisplay = g.pages.length > 1 
                        ? ` (p. ${g.pages.join(', ')})` 
                        : ` (p. ${g.pages[0]})`;
                    }
                    
                    // Source cards are clickable unless marked as training_only (private)
                    const isPrivate = g.visibility === 'training_only';
                    const hasUrl = g.url && g.url.trim() !== '';
                    
                    // Make URL absolute if it's relative and starts with /api/
                    let docUrl = g.url;
                    if (hasUrl && !isPrivate && docUrl.startsWith('/api/')) {
                      docUrl = `${API_BASE}${docUrl}`;
                    }
                    
                    // Debug logging for clickability
                    
                    
                    if (!isPrivate && hasUrl) {
                      // Clickable source card for public documents with URLs
                      return (
                        <a
                          key={i}
                          href={docUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ov-source-card"
                          style={{
                            padding: '8px 12px',
                            marginBottom: '6px',
                            background: '#f8f9fa',
                            border: '1px solid #e9ecef',
                            borderRadius: '6px',
                            fontSize: '13px',
                            color: '#495057',
                            cursor: 'pointer',
                            textDecoration: 'none',
                            display: 'block',
                            transition: 'all 0.2s ease'
                          }}
                          onClick={(e) => {
                            
                            // Allow default link behavior
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#e9ecef';
                            e.currentTarget.style.borderColor = '#dee2e6';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#f8f9fa';
                            e.currentTarget.style.borderColor = '#e9ecef';
                          }}
                          title={`View source: ${titleDisplay}${locationDisplay}`}
                        >
                          {icon} <strong>{titleDisplay}</strong>{locationDisplay}
                        </a>
                      );
                    } else {
                      // Non-clickable source card for private documents or documents without URLs
                      return (
                        <div
                          key={i}
                          className="ov-source-card"
                          style={{
                            padding: '8px 12px',
                            marginBottom: '6px',
                            background: '#fff3cd',
                            border: '1px solid #ffc107',
                            borderRadius: '6px',
                            fontSize: '13px',
                            color: '#856404',
                            cursor: 'default'
                          }}
                          title={isPrivate ? `Private document (training only): ${titleDisplay}${locationDisplay}` : `Source: ${titleDisplay}${locationDisplay}`}
                        >
                          {isPrivate && '🔒 '}{icon} <strong>{titleDisplay}</strong>{locationDisplay}
                        </div>
                      );
                    }
                  })}
                      </div>
                    </details>
                  </div>
                );
              })()}

              {/* Voice follow-up chip for PAI3/OpsIQ mobile (after assistant messages) */}
              {msg.role === 'assistant' && isOpsMobileVertical && isMobile && idx === messages.length - 1 && (
                <button
                  type="button"
                  className="ov-voice-followup-chip"
                  onClick={isRecording ? handleStopRecording : handleStartRecording}
                  title="Ask a follow-up question by voice"
                >
                  🎤 Follow up?
                </button>
              )}

              {msg.role === 'assistant' && vertical === 'mentormind' && (() => {
                // Calculate confidence if not already set (for backward compatibility)
                const confidence = msg.confidence || (() => {
                  if (msg.usesCoachDocs && msg.sources && msg.sources.length > 0) return 'high';
                  if (msg.sources && msg.sources.length > 0) return 'medium';
                  return 'low';
                })();
                
                return (
                  <div className="ov-confidence-label" style={{
                    marginTop: '8px',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: confidence === 'high' ? '#10b981' : confidence === 'medium' ? '#f59e0b' : '#ef4444'
                  }}>
                    {confidence === 'high' ? '⭐ High Confidence' : 
                     confidence === 'medium' ? '⭐ Medium Confidence' : 
                     '⭐ Low Confidence'}
                  </div>
                );
              })()}
              </div>
              {/* Citations now shown on hover only - removed always-visible sources */}
              {msg.role === 'assistant' && vertical === 'hvac' && (
                <div className="response-quality hvac-verification">
                  {msg.sources && msg.sources.length > 0 ? (
                    <span className="hvac-verified">
                      ✅ Verified from company SOPs
                    </span>
                  ) : (
                    <span className="hvac-general">
                      ⚠️ General HVAC knowledge - not company-specific
                    </span>
                  )}
                </div>
              )}
              {/* Removed redundant "Based on X document(s)" - source chips already show this */}
              {msg.role === 'assistant' && (
                <div className="ov-feedback-container">
                  <FeedbackRating
                    messageId={msg.id || `msg-${idx}`}
                    questionText={idx > 0 ? (messages[idx - 1]?.content || '') : ''}
                    answerText={msg.content || ''}
                    companySlug={companyIdSlug || 'unknown'}
                    onFeedback={async (feedbackData) => {
                    }}
                  />
                  <button
                    className={`ov-like-btn ${likedMessages.has(msg.id || `msg-${idx}`) ? 'liked' : ''}`}
                    onClick={() => handleLikeMessage(msg.id || `msg-${idx}`)}
                    aria-label={likedMessages.has(msg.id || `msg-${idx}`) ? 'Remove from saved' : 'Save to memory'}
                    title={likedMessages.has(msg.id || `msg-${idx}`) ? 'Remove from saved' : 'Save to memory'}
                  >
                    <span aria-hidden="true">{likedMessages.has(msg.id || `msg-${idx}`) ? '❤️' : '🤍'}</span>
                  </button>
                </div>
              )}

              {/* Sources are displayed inline below the message - no separate button needed */}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="ov-enterprise-msg ov-msg-assistant">
            <div className="ov-enterprise-bubble ov-bubble-assistant">
              <div className="ov-enterprise-spinner"></div>
              {getLoadingMessage()}
            </div>
          </div>
        )}
        {isDanMartellWidget && (
          <ConversationIntelligence extracted={businessProfile} />
        )}
        {/* ToolsPanel removed from chat tab - frameworks/tools are only in Tools tab for cleaner UX */}
        <div ref={messagesEndRef} />
        
        {/* Scroll to Latest Button - Shows when user scrolls up */}
        {showScrollToLatest && isMobile && (
          <button
            onClick={forceScrollToBottom}
            className="ov-scroll-to-latest"
            aria-label="Scroll to latest message"
            style={{
              position: 'absolute',
              bottom: '100px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: `linear-gradient(135deg, ${
                vertical === 'pai3' ? '#00E676' :
                vertical === 'opsiq' ? '#4ECDC4' :
                '#4299E1'
              } 0%, ${
                vertical === 'pai3' ? '#00B853' :
                vertical === 'opsiq' ? '#20B2AA' :
                '#3182CE'
              } 100%)`,
              color: 'white',
              border: 'none',
              borderRadius: '24px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '600',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              cursor: 'pointer',
              zIndex: 100,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              animation: 'fadeIn 0.3s ease',
            }}
          >
            <span style={{ fontSize: '16px' }}>↓</span> Latest Message
          </button>
        )}
          </div>
        </div>
      </div>
      )}
              
      {/* Suggested Questions Popup Trigger - Only show when no messages (hidden for OpsIQ/Business and PAI3) */}
      {messages.length === 0 && !isRecording && vertical !== 'opsiq' && vertical !== 'business' && vertical !== 'pai3' && (
        <button
          className="ov-suggestions-popup-trigger"
          onClick={() => setShowSuggestionsPopup(!showSuggestionsPopup)}
          aria-label="Show suggested questions"
        >
          <span className="ov-suggestions-icon">✨</span>
          <span className="ov-suggestions-label-text">Suggested Questions</span>
        </button>
      )}

      {/* Suggested Questions Popup (hidden for OpsIQ/Business and PAI3) */}
      {showSuggestionsPopup && vertical !== 'opsiq' && vertical !== 'business' && vertical !== 'pai3' && (
        <div className="ov-suggestions-popup-overlay" onClick={() => setShowSuggestionsPopup(false)}>
          <div className="ov-suggestions-popup" onClick={(e) => e.stopPropagation()}>
            <div className="ov-suggestions-popup-header">
              <h4>✨ Suggested Questions</h4>
              <button 
                className="ov-suggestions-popup-close"
                onClick={() => setShowSuggestionsPopup(false)}
                aria-label="Close suggestions"
              >
                ×
              </button>
            </div>
            <div className="ov-suggestions-popup-content">
              {generateDynamicSuggestions().map((suggestion, idx) => {
                const suggestionText = typeof suggestion === 'string' ? suggestion : (suggestion.title || suggestion.label || suggestion);
                return (
                <button 
                key={idx}
                    className="ov-suggestion-popup-item"
                onClick={() => handleQuickSuggestion(suggestion)}
                >
                    {suggestionText}
                </button>
                );
              })}
            </div>
              </div>
              </div>
      )}

      {/* Removed keyboard hints footer - cleaner mobile experience */}
      <div className="ov-chat-footer">
        {copyFeedback && <span className="ov-copy-feedback">{copyFeedback}</span>}
                  </div>
                </div>
  );

  // Save coach notes to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(`ov_notes_${companyIdSlug}`, JSON.stringify(coachNotes));
    } catch (e) {
    }
  }, [coachNotes, companyIdSlug]);

  // Note: Coach notes are loaded in handleTabChange before tab switch
  // This useEffect is disabled to prevent double-loading and re-render issues
  // useEffect(() => {
  //   if (activeTab === 'coach') {
  //     try {
  //       const saved = localStorage.getItem('ov_coach_notes');
  //       if (saved) {
  //         const parsed = JSON.parse(saved);
  //         setCoachNotes(prev => {
  //           const prevStr = JSON.stringify(prev);
  //           const newStr = JSON.stringify(parsed);
  //           if (prevStr !== newStr) {
  //             return parsed;
  //           }
  //           return prev;
  //         });
  //       }
  //     } catch (e) {

  //     }
  //   }
  // }, [activeTab]);

  const handleAddCoachNote = () => {
    if (!newCoachNote.content.trim()) return;
    
    const note = {
      id: `note-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type: newCoachNote.type,
      content: newCoachNote.content.trim(),
      timestamp: Date.now(),
      relatedMessage: null // Could link to a specific message if needed
    };
    
    setCoachNotes(prev => [note, ...prev]);
    setNewCoachNote({ type: 'question', content: '' });
    addToast('Note saved for your coach!', 'success');
  };

  const handleDeleteCoachNote = (noteId) => {
    setCoachNotes(prev => prev.filter(n => n.id !== noteId));
    addToast('Note removed', 'success');
  };

  const renderCoachTab = () => {
    // Vertical-specific configurations
    const verticalConfig = {
      mentormind: {
        title: 'Follow-up with Your Coach',
        subtitle: 'Save questions, unclear items, goals, and action items to discuss in your next session',
        emptyIcon: '👤',
        emptyTitle: 'No notes yet',
        emptySubtitle: 'Start adding questions, goals, or action items to discuss with your coach',
        noteTypeLabels: {
          question: '❓ Question',
          unclear: '❓ Unclear',
          goal: '🎯 Goal',
          action: '✅ Action Item',
          other: '📝 Note'
        },
        placeholders: {
          question: 'What would you like to ask your coach?',
          unclear: 'What was unclear from the AI response?',
          goal: 'What goal do you want to set or discuss?',
          action: 'What action item do you want to track?',
          other: 'Add a note for your coach...'
        }
      },
      hvac: {
        title: 'Service Call Notes',
        subtitle: 'Track important details, follow-ups, and action items for your jobs',
        emptyIcon: '🔧',
        emptyTitle: 'No notes saved',
        emptySubtitle: 'Start tracking customer requests, parts needed, and follow-up tasks',
        noteTypeLabels: {
          question: '❓ Customer Question',
          unclear: '⚠️ Issue',
          goal: '🎯 Parts Needed',
          action: '✅ Follow-up',
          other: '📝 Note'
        },
        placeholders: {
          question: 'What did the customer ask?',
          unclear: 'What issue needs attention?',
          goal: 'What parts do you need to order?',
          action: 'What follow-up is needed?',
          other: 'Add a job note...'
        }
      },
      business: {
        title: 'Quick Notes & Action Items',
        subtitle: 'Capture observations, issues, and follow-ups from your reviews',
        emptyIcon: '📋',
        emptyTitle: 'No notes yet',
        emptySubtitle: 'Start capturing questions, issues, action items, and important observations',
        noteTypeLabels: {
          question: '❓ Question',
          unclear: '⚠️ Issue',
          goal: '📅 Follow-Up',
          action: '✅ Action Item',
          other: '📝 Note'
        },
        placeholders: {
          question: 'What needs clarification?',
          unclear: 'What issue was found?',
          goal: 'What needs follow-up?',
          action: 'What needs to be done?',
          other: 'Add a quick note...'
        }
      }
    };

    const config = verticalConfig[vertical] || verticalConfig.business;

    const noteTypeColors = {
      question: '#6366F1',
      unclear: '#F59E0B',
      goal: '#10B981',
      action: '#3B82F6',
      other: '#6B7280'
    };

      return (
      <div className="ov-coach-tab">
        <div className="ov-coach-header">
          <div className="ov-coach-header-content">
            <h3 className="ov-coach-title">{config.title}</h3>
            <p className="ov-coach-subtitle">
              {config.subtitle}
            </p>
          </div>
        </div>

        {/* Add New Note Form */}
        <div className="ov-coach-note-form">
          <div className="ov-coach-note-type-selector">
            {Object.entries(config.noteTypeLabels).map(([type, label]) => (
            <button 
                key={type}
                className={`ov-note-type-btn ${newCoachNote.type === type ? 'active' : ''}`}
                onClick={() => setNewCoachNote(prev => ({ ...prev, type }))}
                style={{
                  borderColor: newCoachNote.type === type ? noteTypeColors[type] : 'transparent',
                  backgroundColor: newCoachNote.type === type ? `${noteTypeColors[type]}10` : 'transparent'
              }}
            >
                {label}
            </button>
            ))}
          </div>
          <textarea
            className="ov-coach-note-input"
            placeholder={config.placeholders[newCoachNote.type]}
            value={newCoachNote.content}
            onChange={(e) => setNewCoachNote(prev => ({ ...prev, content: e.target.value }))}
            rows={4}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                handleAddCoachNote();
              }
            }}
          />
          <div className="ov-coach-note-actions">
            <button
              className="ov-coach-add-btn"
              onClick={handleAddCoachNote}
              disabled={!newCoachNote.content.trim()}
            >
              <span className="ov-add-icon">+</span>
              Add Note
          </button>
            <span className="ov-coach-hint">Ctrl+Enter to save</span>
                    </div>
                    </div>
      
        {/* Notes List */}
        <div className="ov-coach-notes-list">
          {coachNotes.length === 0 ? (
            <div className="ov-empty-coach-notes">
              <div className="ov-empty-icon">{config.emptyIcon}</div>
              <h4>{config.emptyTitle}</h4>
              <p>{config.emptySubtitle}</p>
          </div>
        ) : (
            <>
              <div className="ov-coach-notes-header">
                <h4 className="ov-notes-title">
                  {coachNotes.length} Saved Note{coachNotes.length !== 1 ? 's' : ''}
                </h4>
                {coachNotes.length > 0 && (
                  <button
                    className="ov-clear-all-btn"
                    onClick={() => {
                      if (window.confirm('Clear all notes? This cannot be undone.')) {
                        setCoachNotes([]);
                        addToast('All notes cleared', 'success');
                      }
                    }}
                  >
                    Clear All
                  </button>
                  )}
                </div>
              {coachNotes.map((note) => (
                <div key={note.id} className="ov-coach-note-card">
                  <div className="ov-note-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      <div className="ov-note-type-badge" style={{ 
                        backgroundColor: `${noteTypeColors[note.type]}15`,
                        color: noteTypeColors[note.type],
                        borderColor: noteTypeColors[note.type]
                      }}>
                        {config.noteTypeLabels[note.type]}
                      </div>
                      {note.fromConversation && (
                        <span className="ov-note-source-badge" style={{
                          fontSize: '10px',
                          color: '#6B7280',
                          background: '#F3F4F6',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontWeight: 500
                        }}>
                          💬 From conversation
                        </span>
                      )}
                    </div>
                    <button 
                      className="ov-note-delete-btn"
                      onClick={() => {
                        handleDeleteCoachNote(note.id);
                        // Also remove from liked messages if it was saved from conversation
                        if (note.messageId) {
                          const newLiked = new Set(likedMessages);
                          newLiked.delete(note.messageId);
                          setLikedMessages(newLiked);
                          try {
                            localStorage.setItem(`ov_liked_messages_${companyIdSlug}`, JSON.stringify(Array.from(newLiked)));
                          } catch (e) {
                          }
                        }
                      }}
                      aria-label="Delete note"
                    >
                      ×
                    </button>
                  </div>
                  <div className="ov-note-content">{note.content}</div>
                  <div className="ov-note-meta">
                    {new Date(note.timestamp).toLocaleString()}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
    </div>
  );
  };



  const renderDocsTab = () => (
    <div className="ov-enterprise-docs">
      <div className="ov-docs-header">
        <h3 className="ov-section-title">Knowledge Base</h3>
        <div className="ov-docs-stats">
          <span className="ov-stat-pill">{docs.length} documents</span>
                          {trialState?.isTrialUser && (
                  <span className="ov-stat-pill trial-indicator">Trial: {trialState?.docsRemaining ?? 0}/3 uploads left</span>
          )}
                  </div>
                </div>
                
                
                  {trialState?.isTrialUser && (
        <div className="ov-upload-section">
          <div className="ov-upload-header">
            <h4>Upload Your Documents</h4>
            <p>Add up to 3 documents to get personalized insights (PDF, DOCX, TXT - 15MB max)</p>
          </div>
                        {(trialState?.docsRemaining ?? 0) > 0 ? (
            <div className="ov-upload-area" onClick={() => document.getElementById('file-upload')?.click()}>
              <input id="file-upload" type="file" multiple accept=".pdf,.docx,.txt" style={{ display: 'none' }} onChange={handleFileUpload} />
              <div className="ov-upload-placeholder">
                <span className="ov-upload-icon">📁</span>
                <div className="ov-upload-text">
                  <div>Click to upload documents</div>
                  <div className="ov-upload-hint">PDF, DOCX, TXT files up to 15MB</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="ov-upload-blocked">
              <span className="ov-blocked-icon">🔒</span>
              <div className="ov-blocked-text">
                <div>Upload limit reached</div>
                <div className="ov-blocked-hint">Upgrade to upload more documents</div>
              </div>
              <button className="ov-upgrade-btn" onClick={() => window.open('https://www.opsvoice.ai/pricing', '_blank')}>View Pricing</button>
            </div>
          )}
                      {trialState?.isLoading && (
            <div className="ov-upload-progress">
              <div className="ov-upload-spinner"></div>
              <div className="ov-upload-status">Uploading...</div>
            </div>
          )}
        </div>
      )}
                
      {docs.length === 0 ? (
        <div className="ov-empty-docs">
          <span className="ov-empty-icon">📚</span>
          <div className="ov-empty-text">
            <div>No documents uploaded yet</div>
            <div className="ov-empty-subtext">{trialState?.isTrialUser ? 'Upload your first document to start getting personalized insights' : 'Documents will appear here when uploaded to your knowledge base'}</div>
                        </div>
                        </div>
      ) : (
        <div className="ov-enterprise-docs-list">
          {docs.map((doc, idx) => {
            // Extract clean title from filename - remove path and company_id prefix
            let displayTitle = doc.title || doc.filename || '';
            if (displayTitle && !doc.title) {
              // Remove full path, keep just filename
              displayTitle = displayTitle.split('/').pop() || displayTitle;
              // Remove company_id and timestamp prefix (e.g., "coach-workin-1762400027_1762400046_")
              displayTitle = displayTitle.replace(/^[^_]+_\d+_/, '');
            }
            // Get filename for hash navigation (use the raw filename from backend)
            const docFilename = doc.filename || doc.title || '';
            const safeFilename = docFilename.split('/').pop() || docFilename; // Remove path if present
            
            // Construct view URL - prefer existing URLs, fallback to document ID endpoint
            const getViewUrl = () => {
              // First, use provided URLs from backend
              if (doc.view_url || doc.sop_file_url) {
                return doc.view_url || doc.sop_file_url;
              }
              // Try document ID endpoint - check if endpoint exists
              if (doc.id) {
                // Try /file endpoint first
                return `${API_BASE}/api/v1/documents/${encodeURIComponent(doc.id)}/file`;
              }
              // Fallback: try filename-based endpoint (may not exist yet)
              if (doc.filename || safeFilename) {
                const encodedFilename = encodeURIComponent(safeFilename);
                // Note: This endpoint may need to be implemented in backend
                return `${API_BASE}/api/v1/documents/file?filename=${encodedFilename}&company_id_slug=${encodeURIComponent(companyIdSlug)}`;
              }
              return null;
            };
            
            const viewUrl = getViewUrl();
            const isReady = (doc.status === 'embedded' || doc.status === 'ready' || !doc.status);
            
            // Only show View link if we have a URL and document is ready
            const finalViewUrl = viewUrl;
            
            return (
            <div 
              key={idx} 
              className="ov-enterprise-doc-item"
              data-doc-filename={safeFilename}
            >
              <div className="ov-doc-content">
                <div className="ov-doc-title">{displayTitle}</div>
                <div className="ov-doc-meta">
                  <span className="ov-doc-status">
                    <span className={`ov-status-dot ${isReady ? 'active' : 'inactive'}`}></span>
                    {isReady ? 'Ready' : (doc.status || 'Processing...')}
                  </span>
                  <span className="ov-doc-date">{doc.created_at ? new Date(doc.created_at).toLocaleDateString() : 'Recent'}</span>
                      </div>
                      </div>
              {isReady && finalViewUrl && (
                <a 
                  href={finalViewUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="ov-enterprise-doc-link"
                  title={doc.filename && doc.filename.toLowerCase().endsWith('.docx') ? 'Open in Microsoft Word or Office Online' : 'View document in browser'}
                  onClick={(e) => {
                    e.stopPropagation();
                    // For .docx files, let browser handle download (don't prevent default)
                    // User should open in Word/Office, not Notepad
                    if (doc.filename && doc.filename.toLowerCase().endsWith('.docx')) {
                      // Show helpful message via toast if available
                      if (typeof addToast === 'function') {
                        addToast('Downloading document - please open in Microsoft Word or Office Online (not Notepad)', 'info', 5000);
                      } else {
                      }
                      // Let the download happen - don't prevent default
                      return; // Don't prevent default - allow download
                    }
                    e.preventDefault();
                    // Ensure it opens in new tab
                    window.open(finalViewUrl, '_blank', 'noopener,noreferrer');
                  }}
                >
                  <svg className="ov-view-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>View</span>
                </a>
                    )}
                  </div>
            );
          })}
              </div>
            )}
    </div>
  );

  // Loading Skeleton Component
  const LoadingSkeleton = React.memo(() => (
    <div className="ov-skeleton-card">
      <div className="ov-skeleton-header">
        <div className="ov-loading-skeleton ov-skeleton-icon"></div>
        <div className="ov-skeleton-text">
          <div className="ov-loading-skeleton ov-skeleton-title"></div>
          <div className="ov-loading-skeleton ov-skeleton-desc"></div>
        </div>
      </div>
      <div className="ov-skeleton-metrics">
        <div className="ov-loading-skeleton ov-skeleton-metric"></div>
        <div className="ov-loading-skeleton ov-skeleton-metric"></div>
      </div>
      <div className="ov-loading-skeleton ov-skeleton-button"></div>
    </div>
  ));

  // Error State Component
  const ErrorState = React.memo(({ error, onRetry }) => (
    <div className="ov-error-state">
      <div className="ov-error-icon">⚠️</div>
      <div className="ov-error-title">Something went wrong</div>
      <div className="ov-error-message">{error || 'Failed to load insights'}</div>
      {onRetry && (
        <button className="ov-retry-button" onClick={onRetry}>
          Try Again
        </button>
      )}
    </div>
  ));

  // Empty State Component
  const EmptyState = React.memo(({ title, description, icon = "📊" }) => (
    <div className="ov-empty-state">
      <div className="ov-empty-icon">{icon}</div>
      <div className="ov-empty-title">{title}</div>
      <div className="ov-empty-desc">{description}</div>
    </div>
  ));

  // Smart Contextual Cards Component - replaces large intelligence panel
  // SmartContextCards is now imported from components

  // Legacy Conversation Intelligence Panel - kept for backward compatibility
  const ConversationIntelligencePanel = ({ context, vertical, coachingType }) => {
    if (!context || Object.keys(context).length === 0) return null;
    
    return (
      <div className="ov-intelligence-panel">
        <div className="ov-intel-header">
          <span className="ov-intel-icon">🧠</span>
          <span className="ov-intel-title">Conversation Intelligence</span>
          <span className="ov-intel-count">{Object.keys(context).length} data points</span>
                </div>
        <div className="ov-intel-grid">
          {Object.entries(context).slice(0, 6).map(([key, value]) => (
            <div key={key} className="ov-intel-item">
              <span className="ov-intel-label">{getFieldLabel(key)}</span>
              <span className="ov-intel-value">
                {Array.isArray(value) ? value.join(', ') : String(value)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

    // Memoized insight card component for performance
    const InsightCard = React.memo(({ card, conversationData, premiumIcons, onUseTool }) => {
      const result = card.compute(conversationData);
      const icon = premiumIcons[card.id] || card.icon;
      const isUnlocked = card.visibleWhen(conversationData);
      const [isAnimating, setIsAnimating] = useState(false);
      
      // Trigger unlock animation when card becomes available
      useEffect(() => {
        if (isUnlocked && !isAnimating) {
          setIsAnimating(true);
          setTimeout(() => setIsAnimating(false), 600);
        }
      }, [isUnlocked, isAnimating]);
      
      return (
        <div 
          key={card.id} 
          className={`ov-tool-card-ready ${isAnimating ? 'unlocking' : ''}`}
          onClick={() => {
            handleSelectTool(card.id);
          }}
          style={{ cursor: 'pointer' }}
        >
          <div className="ov-tool-header">
            <span className="ov-tool-icon-small">{icon}</span>
            <div className="ov-tool-info">
              <h4 className="ov-tool-name">{card.label}</h4>
              <p className="ov-tool-desc-short">{card.description}</p>
            </div>
            <div className="ov-tool-status-ready">✓</div>
          </div>
          
          {result?.metrics && (
            <div className="ov-metrics-horizontal">
              {Object.entries(result.metrics).map(([key, value]) => (
                <div key={key} className="ov-metric-pill">
                  <span className="ov-metric-value-small">{value}</span>
                  <span className="ov-metric-label-small">{key}</span>
                </div>
              ))}
            </div>
          )}
          
          <button 
            className="ov-use-tool-btn" 
            onClick={(e) => {
              e.stopPropagation();
              if (onUseTool) {
                onUseTool(card);
              } else {
                handleSelectTool(card.id);
              }
            }}
          >
            <span aria-hidden="true"><Zap size={16} strokeWidth={2} /></span>
                  <span>Apply This Framework</span>
          </button>
        </div>
      );
    });

    const renderInsightsTab = useCallback(() => {
    // Use the memoized values from component level
    
    // Merge preset tools (availableCards) with backend tools (from useToolsFeed)
    // Backend tools take precedence if they have the same ID, but we show all unique tools
    const allToolsMap = new Map();
    
    // For PAI3/OpsIQ/Business, use businessTools instead of availableCards
    const toolsToUse = businessTools || availableCards;
    
    // First add preset tools
    toolsToUse.forEach(tool => {
      allToolsMap.set(tool.id, {
        ...tool,
        label: tool.label || tool.name,
        source: 'preset'
      });
    });
    
    // Then add backend tools (they override preset tools with same ID)
    [...readyTools, ...lockedTools].forEach(tool => {
      // Convert backend tool format to match availableCards format
      const convertedTool = {
        id: tool.id || tool.name?.toLowerCase().replace(/\s+/g, '-'),
        label: tool.name || tool.label,
        description: tool.description || '',
        icon: tool.icon || '🧠',
        needs: tool.needs || tool.missing || [],
        visibleWhen: tool.ready ? () => true : () => false, // Backend already calculated readiness
        ready: tool.ready || false,
        sourceDocTitle: tool.sourceDocTitle,
        source: 'backend'
      };
      allToolsMap.set(convertedTool.id, convertedTool);
    });
    
    const allTools = Array.from(allToolsMap.values());
    
    return (
      <>
      <div className="ov-insights-clean">

        {/* Achievement Gallery - Trophy Cabinet for Completed Frameworks - ALWAYS SHOW IF COMPLETED */}
        {(() => {
          const completedFrameworks = JSON.parse(localStorage.getItem('completedFrameworks') || '[]');

          if (completedFrameworks.length === 0) return null;

          return (
            <div className="ov-achievement-gallery" data-vertical={vertical}>
              <div className="ov-achievement-header">
                <div className="ov-achievement-title-row">
                  <span className="ov-trophy-icon" aria-hidden="true">
                    <Trophy size={18} strokeWidth={2} />
                  </span>
                  <h3 className="ov-achievement-title">Your Achievements</h3>
                </div>
                <p className="ov-achievement-subtitle">
                  {completedFrameworks.length} framework{completedFrameworks.length !== 1 ? 's' : ''} completed
                </p>
              </div>

              <div className="ov-achievement-grid">
                {completedFrameworks.map((framework, index) => {
                  const completedDate = new Date(framework.completedAt);
                  const timeAgo = (() => {
                    const days = Math.floor((Date.now() - completedDate.getTime()) / (1000 * 60 * 60 * 24));
                    if (days === 0) return 'Today';
                    if (days === 1) return 'Yesterday';
                    if (days < 7) return `${days} days ago`;
                    if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) !== 1 ? 's' : ''} ago`;
                    return completedDate.toLocaleDateString();
                  })();

                  return (
                    <div
                      key={index}
                      className="ov-achievement-card"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className="ov-achievement-card-shimmer"></div>

                      {/* Delete Button - Top Right Corner */}
                      <button
                        className="ov-achievement-delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(`Delete "${framework.frameworkName}" from your achievements?\n\nThis cannot be undone.`)) {
                            // Remove from localStorage
                            const frameworks = JSON.parse(localStorage.getItem('completedFrameworks') || '[]');
                            frameworks.splice(index, 1);
                            localStorage.setItem('completedFrameworks', JSON.stringify(frameworks));

                            // Force re-render by triggering state update
                            setActiveTab(activeTab); // Trigger re-render

                            // Show success toast
                            addToast('🗑️ Framework removed from achievements', 'success');
                          }
                        }}
                        aria-label="Delete framework"
                      >
                        ×
                      </button>

                      <div className="ov-achievement-badge">
                        <span className="ov-badge-icon">✓</span>
                        <span className="ov-badge-text">Completed</span>
                      </div>
                      <div className="ov-achievement-content">
                        <h4 className="ov-achievement-name">{framework.frameworkName}</h4>
                        <p className="ov-achievement-date">{timeAgo}</p>
                      </div>
                      <button
                        className="ov-achievement-view-btn"
                        onClick={() => {
                          setViewingCompletedFramework(framework);
                          setShowFrameworkCompletionModal(true);
                        }}
                      >
                        <span className="ov-btn-icon" aria-hidden="true">
                          <BarChart3 size={16} strokeWidth={2} />
                        </span>
                        <span className="ov-btn-text">View Summary</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Simplified Hero Section */}
        <div className="ov-context-hero-v2">
          {contextIndicators.length > 0 ? (
            <>
              <div className="ov-hero-header">
                <div className="ov-hero-icon-pulse" aria-hidden="true">
                  <Target size={20} strokeWidth={2} />
                </div>
                <div className="ov-hero-text">
                  <h3 className="ov-hero-title-v2">Tools Unlocked</h3>
                  <p className="ov-hero-subtitle-v2">{contextIndicators.length} insights ready from your conversation</p>
                </div>
              </div>
              <div className="ov-context-indicators-v2">
                {contextIndicators.map((indicator, index) => (
                  <div key={index} className="ov-context-badge-v2" style={{animationDelay: `${index * 0.1}s`}}>
                    <span className="ov-badge-icon">{indicator.icon}</span>
                    <span className="ov-badge-text">{indicator.label}: {indicator.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="ov-empty-state-v2 ov-toolkit-preview">
              {/* Quick Reviews removed for OpsIQ/Business - keep (optional) for PAI3 only */}
              {vertical === 'pai3' && spreadsheetsEnabled ? (
                <div style={{ padding: '20px' }}>
                  <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                    <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: '0 0 8px 0' }}>
                      Quick Reviews
                    </h3>
                    <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                      Fast operational scans to stay ahead
                    </p>
                  </div>
                  
                  {/* Review Cards */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '20px',
                    maxWidth: '800px',
                    margin: '0 auto',
                  }}>
                    {/* Daily Ops Check */}
                    <div style={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '12px',
                      padding: '24px',
                      transition: 'all 0.2s',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#4ECDC4';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(78, 205, 196, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    onClick={() => {
                      // Switch to chat and automatically ask the question
                      setActiveTab('chat');
                      const question = "What items are overdue or due today?";
                      setTimeout(() => {
                        setInputText(question);
                        setTimeout(() => {
                          handleSendMessage();
                        }, 50);
                      }, 100);
                    }}
                    >
                      <div style={{ fontSize: '32px', marginBottom: '12px' }}>🗓️</div>
                      <h4 style={{ 
                        margin: '0 0 8px 0', 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#111827' 
                      }}>
                        Daily Ops Check
                      </h4>
                      <p style={{ 
                        margin: '0 0 16px 0', 
                        fontSize: '13px', 
                        color: '#6b7280',
                        lineHeight: '1.5',
                      }}>
                        Scan for urgent issues and actions
                      </p>
                      <div style={{
                        fontSize: '12px',
                        color: '#9ca3af',
                        borderTop: '1px solid #f3f4f6',
                        paddingTop: '12px',
                      }}>
                        <div style={{ marginBottom: '4px' }}>• Overdue items</div>
                        <div>• Due today</div>
                      </div>
                    </div>
                    
                    {/* Weekly Compliance Scan */}
                    <div style={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '12px',
                      padding: '24px',
                      transition: 'all 0.2s',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#4ECDC4';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(78, 205, 196, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    onClick={() => {
                      // Switch to chat and automatically ask the question
                      setActiveTab('chat');
                      const question = "What items are due in the next 7 days?";
                      setTimeout(() => {
                        setInputText(question);
                        setTimeout(() => {
                          handleSendMessage();
                        }, 50);
                      }, 100);
                    }}
                    >
                      <div style={{ fontSize: '32px', marginBottom: '12px' }}>📊</div>
                      <h4 style={{ 
                        margin: '0 0 8px 0', 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#111827' 
                      }}>
                        Weekly Compliance Scan
                      </h4>
                      <p style={{ 
                        margin: '0 0 16px 0', 
                        fontSize: '13px', 
                        color: '#6b7280',
                        lineHeight: '1.5',
                      }}>
                        Review upcoming risks and gaps
                      </p>
                      <div style={{
                        fontSize: '12px',
                        color: '#9ca3af',
                        borderTop: '1px solid #f3f4f6',
                        paddingTop: '12px',
                      }}>
                        <div style={{ marginBottom: '4px' }}>• Next 7 days obligations</div>
                        <div>• Grouped by owner</div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : lockedTools && lockedTools.length > 0 ? (
                <>
                  {/* Toolkit Preview Header */}
                  <div className="ov-toolkit-header">
                    <h3 className="ov-toolkit-title">
                      {(vertical === 'business' || vertical === 'opsiq' || vertical === 'pai3')
                        ? 'Your Business Tools'
                        : vertical === 'hvac'
                          ? 'Your Field Tools'
                          : 'Your Coaching Toolkit'}
                    </h3>
                    <p className="ov-toolkit-subtitle">
                      {(vertical === 'business' || vertical === 'opsiq' || vertical === 'pai3')
                        ? 'Smart tools unlock automatically based on your conversations and business context.'
                        : vertical === 'hvac'
                          ? 'Diagnostic and reference tools unlock as you share service call details.'
                          : 'These frameworks unlock automatically as we discuss your goals, challenges, and growth plans.'}
                    </p>
                  </div>

                  {/* Dynamic Tool Cards from Backend */}
                  <div className="ov-toolkit-grid">
                    {lockedTools.map((tool, index) => {
                      // Clean icon handling - ensure we always show a valid emoji
                      let displayIcon = '🧠'; // Default fallback
                      if (tool.icon && typeof tool.icon === 'string' && tool.icon.trim().length > 0) {
                        const trimmedIcon = tool.icon.trim();
                        // Stronger filter: reject ANY letter-like characters (including unicode letters with diacritics)
                        const hasTextChars = /[\p{L}\p{N}\p{M}]/u.test(trimmedIcon);
                        if (!hasTextChars && trimmedIcon.length <= 4) {
                          displayIcon = trimmedIcon;
                        }
                      }

                      // Helper function to format "needs" - convert snake_case to readable text
                      const formatNeed = (need) => {
                        return need
                          .split('_')
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                          .join(' ');
                      };

                      return (
                        <div key={tool.id || index} className="ov-tool-preview-card">
                          <div className="ov-tool-icon">{displayIcon}</div>
                          <div className="ov-tool-content">
                            <h4 className="ov-tool-name">{tool.name || tool.label}</h4>
                            <p className="ov-tool-desc">
                              {tool.description || 
                                ((vertical === 'business' || vertical === 'opsiq' || vertical === 'pai3') 
                                  ? 'Business tool' 
                                  : vertical === 'hvac' 
                                    ? 'Field service tool' 
                                    : 'Custom coaching framework')}
                            </p>
                          {tool.active_session && (
                            <div className="ov-tool-progress">
                              <span className="ov-progress-badge">
                                {tool.active_session.is_complete
                                  ? 'Complete'
                                  : `In progress: Step ${tool.active_session.current_step} / ${tool.active_session.total_steps} (${tool.active_session.completion_percentage || 0}%)`}
                              </span>
                            </div>
                          )}
                            {tool.needs && tool.needs.length > 0 && (
                              <span className="ov-tool-trigger">
                                <span aria-hidden="true" style={{ display: 'inline-flex', marginRight: '6px', verticalAlign: 'middle' }}>
                                  <MessageSquare size={14} strokeWidth={2} />
                                </span>
                                Share: {tool.needs.slice(0, 2).map(formatNeed).join(', ')}
                              </span>
                            )}
                          <div className="ov-tool-actions">
                            <button className="ov-start-convo-btn" onClick={() => resumeFramework(tool)}>
                              <span className="ov-cta-icon" aria-hidden="true">
                                <Play size={14} strokeWidth={2} />
                              </span>
                              <span>{tool.active_session ? 'Resume' : 'Start'}</span>
                            </button>
                            {tool.active_session && (
                              <button className="ov-reset-btn" onClick={() => resetFrameworkSession(tool)}>
                                <span className="ov-cta-icon" aria-hidden="true">
                                  <RotateCcw size={14} strokeWidth={2} />
                                </span>
                                <span>Restart</span>
                              </button>
                            )}
                          </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Quick Start CTA */}
                  <div className="ov-toolkit-cta">
                    <p className="ov-cta-label">Ready to begin?</p>
                    <button className="ov-start-convo-btn" onClick={() => setActiveTab('chat')}>
                      <span className="ov-cta-icon" aria-hidden="true">
                        <MessageSquare size={14} strokeWidth={2} />
                      </span>
                      <span>Start the conversation</span>
                      <span className="ov-cta-arrow">→</span>
                    </button>
                  </div>
                </>
              ) : (
                /* Clean Empty State - No Tools Yet - Vertical-specific messaging */
                <div className="ov-toolkit-empty">
                  <div className="ov-toolkit-empty-icon">
                    {(vertical === 'business' || vertical === 'opsiq' || vertical === 'pai3')
                      ? <Search size={28} strokeWidth={2} />
                      : vertical === 'hvac'
                        ? <Wrench size={28} strokeWidth={2} />
                        : <Target size={28} strokeWidth={2} />}
                  </div>
                  <h3 className="ov-toolkit-empty-title">
                    {(vertical === 'business' || vertical === 'opsiq' || vertical === 'pai3')
                      ? 'No actions available yet'
                      : vertical === 'hvac'
                        ? 'Field tools will appear here'
                        : 'Your frameworks will appear here'}
                  </h3>
                  <p className="ov-toolkit-empty-text">
                    {(vertical === 'business' || vertical === 'opsiq' || vertical === 'pai3')
                      ? 'Upload spreadsheets in the Knowledge tab to enable Quick Actions, or ask questions about your documents in the Conversation tab.'
                      : vertical === 'hvac'
                        ? 'Share details about your service call to access diagnostic tools, safety resources, and technical references.'
                        : 'Upload your coaching frameworks in the dashboard to unlock personalized tools for your clients.'}
                  </p>
                  <button className="ov-start-convo-btn" onClick={() => setActiveTab('chat')}>
                    <span className="ov-cta-icon" aria-hidden="true">
                      <MessageSquare size={14} strokeWidth={2} />
                    </span>
                    <span>{(vertical === 'business' || vertical === 'opsiq' || vertical === 'pai3') ? 'Start conversation' : 'Start chatting'}</span>
                    <span className="ov-cta-arrow">→</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Smart Insights Notification */}
        {insightNotification && (
          <div className="ov-compact-notification">
            <div className="ov-compact-content">
              <div className="ov-compact-icon">{insightNotification.icon}</div>
              <div className="ov-compact-text">
                <div className="ov-compact-title">{insightNotification.title}</div>
                <div className="ov-compact-desc">{insightNotification.description}</div>
              </div>
                <button 
                className="ov-compact-action"
                onClick={() => {
                  setSelectedTool(insightNotification.tool);
                  setShowInsightModal(true);
                  setInsightNotification(null);
                }}
              >
                Run
                </button>
            </div>
          </div>
        )}

        {/* Coaching Tools - Discovery Section (hidden for MentorMind - uses new toolkit preview instead) */}
        {vertical !== 'mentormind' && (
        <div className="ov-available-tools-section">
          <div className="ov-section-header-collapsible" onClick={() => setToolsSectionExpanded(!toolsSectionExpanded)}>
            <h4 className="ov-section-subtitle">
              {vertical === 'hvac'
                ? "HVAC Field Tools"
                : (vertical === 'business' || vertical === 'opsiq' || vertical === 'pai3')
                  ? "Quick Tools"
                  : isDanMartell
                    ? "Premium Coaching Tools"
                    : "Explore Tools"}
            </h4>
            <span className={`ov-collapse-arrow ${toolsSectionExpanded ? 'expanded' : 'collapsed'}`}>▼</span>
            </div>
          {toolsSectionExpanded && (
            <>
              <p className="ov-section-desc">
                {vertical === 'hvac'
                  ? "Diagnostic tools and safety resources for HVAC technicians. Share details about your service call to unlock relevant tools."
                  : (vertical === 'business' || vertical === 'opsiq' || vertical === 'pai3')
                    ? "Get instant help with common tasks, deadlines, and finding the right contacts. Share what you need help with to unlock tools."
                    : isDanMartell
                      ? "Discover the full potential of your business with these powerful tools. Share context in your conversations to unlock them."
                      : "Discover powerful tools and insights tailored to your industry. Share context in your conversations to unlock them."
                }
              </p>
              
              <div className="ov-available-tools-grid">
            {allTools.map((tool) => {
              // Backend tools already have readiness calculated, preset tools need visibleWhen check
              const isReady = tool.ready !== undefined ? tool.ready : (tool.visibleWhen ? tool.visibleWhen(conversationData) : false);
              const missingRequirements = tool.needs?.filter(need => !conversationData[need]) || [];
              
              // Create unlock message based on tool needs
              const getUnlockMessage = (tool) => {
                if (tool.id === 'buyback-roi') {
                  return "Share revenue and hours worked to unlock Time ROI Calculator";
                } else if (tool.id === 'replacement-ladder') {
                  return "Share team size and revenue to unlock Delegation Ladder";
                } else if (tool.id === '1-3-1-framework') {
                  return "Share business challenges to unlock Decision Framework 1-3-1";
                } else if (tool.id === 'bottleneck-analysis') {
                  return "Share business metrics to unlock Bottleneck Analysis";
                } else if (tool.id === '7-step-framework') {
                  return "Share business stage to unlock 7 Step Framework";
                } else {
                  // Generic message based on needs
                  const needs = tool.needs || [];
                  if (needs.length > 0) {
                    const needLabels = needs.map(need => getFieldLabel(need)).join(' and ');
                    return `Share ${needLabels.toLowerCase()} to unlock ${tool.label}`;
                  }
                  return `Share business context to unlock ${tool.label}`;
                }
              };
                
                return (
                <div key={tool.id} className={`ov-tool-discovery-card ${isReady ? 'ready' : 'locked'}`}>
                  <div className="ov-tool-discovery-header">
                    <span className="ov-tool-discovery-icon">{tool.icon}</span>
                    <div className="ov-tool-discovery-info">
                      <h5 className="ov-tool-discovery-title">{tool.label}</h5>
                      {tool.sourceDocTitle && (
                        <p className="ov-tool-discovery-doc-link">Linked to: {tool.sourceDocTitle}</p>
                      )}
                      <p className="ov-tool-discovery-desc">{tool.description}</p>
                      </div>
                    <div className="ov-tool-discovery-status">
                      {isReady ? (
                        <span className="ov-status-ready">
                          <span aria-hidden="true" style={{ display: 'inline-flex', marginRight: '6px', verticalAlign: 'middle' }}>
                            <Check size={14} strokeWidth={2} />
                          </span>
                          Ready
                        </span>
                      ) : (
                        <span className="ov-status-locked">
                          <span aria-hidden="true" style={{ display: 'inline-flex', marginRight: '6px', verticalAlign: 'middle' }}>
                            <Lock size={14} strokeWidth={2} />
                          </span>
                          Locked
                        </span>
                      )}
                      </div>
                    </div>
                    
                  <div className="ov-tool-requirements">
                    {/* Check if this is a framework tool */}
                    {(() => {
                      const isFramework = tool.is_framework || tool.isFramework || tool.tool_type === 'framework';
                      const isThisFrameworkActive = isFramework && frameworkExecutionActive && 
                        frameworkExecutionData?.framework_id?.includes(tool.id);
                      
                      // Check if completed
                      const completedFrameworks = JSON.parse(localStorage.getItem('completedFrameworks') || '[]');
                      const isCompleted = isFramework && completedFrameworks.some(f => 
                        f.frameworkName?.toLowerCase().includes(tool.label?.toLowerCase())
                      );
                      
                      if (isFramework) {
                        return (
                          <>
                            {isThisFrameworkActive && frameworkExecutionData && (
                              <div style={{marginBottom: '8px'}}>
                                <div style={{
                                  height: '6px',
                                  background: '#d1fae5',
                                  borderRadius: '3px',
                                  overflow: 'hidden'
                                }}>
                                  <div style={{
                                    width: `${(frameworkExecutionData.current_stage / frameworkExecutionData.total_stages) * 100}%`,
                                    height: '100%',
                                    background: 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)',
                                    transition: 'width 0.3s ease'
                                  }} />
                                </div>
                                <p style={{fontSize: '11px', color: '#15803d', marginTop: '4px'}}>
                                  Stage {frameworkExecutionData.current_stage} of {frameworkExecutionData.total_stages}
                                  {frameworkExecutionData.current_stage_title && `: ${frameworkExecutionData.current_stage_title}`}
                                </p>
                              </div>
                            )}
                            {isCompleted && !isThisFrameworkActive && (
                              <span style={{
                                display: 'inline-block',
                                background: '#dcfce7',
                                color: '#166534',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 600,
                                marginBottom: '8px'
                              }}>
                                ✓ Previously Completed
                              </span>
                            )}
                            <button 
                              className="ov-tool-action-btn"
                              style={isThisFrameworkActive ? {
                                background: '#22c55e',
                                animation: 'pulse 2s infinite'
                              } : isCompleted ? {
                                background: '#f0fdf4',
                                color: '#166534',
                                border: '1px solid #86efac'
                              } : {
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                              }}
                              onClick={() => {
                                // Switch to chat tab and send the trigger message
                                setActiveTab('chat');
                                setInputText(`Coach me through ${tool.label || tool.name}`);
                                setTimeout(() => handleSendMessage(), 200);
                              }}
                            >
                              {isThisFrameworkActive ? '🔄 Continue Framework' : isCompleted ? '🔄 Restart Framework' : '▶️ Start Framework'}
                            </button>
                          </>
                        );
                      }
                      
                      // Regular tool handling
                      return (
                        <>
                          <p className="ov-requirements-title">
                            {isReady ? "✅ Ready to use!" : getUnlockMessage(tool)}
                          </p>
                          {!isReady && missingRequirements.length > 0 && (
                            <div className="ov-requirements-list">
                              {missingRequirements.map((req, idx) => (
                                <span key={idx} className="ov-requirement-chip">
                                  {getFieldLabel(req)}
                                </span>
                              ))}
                            </div>
                          )}
                          {isReady && (
                            <button 
                              className="ov-tool-action-btn"
                              onClick={() => {
                                
                                // Handle new business tools for PAI3/OpsIQ
                                if (tool.id === 'document-summary') {
                                  // Switch to documents tab to select document
                                  addToast('📄 Select a document from the Knowledge tab to summarize', 'info');
                                  setActiveTab('documents');
                                  return;
                                }
                                
                                if (tool.id === 'action-items') {
                                  // Extract action items from conversation
                                  setActiveTab('chat');
                                  setInputText('Extract all action items, tasks, and todos from our conversation. Format as a checklist with owners and due dates if mentioned.');
                                  setTimeout(() => handleSendMessage(), 100);
                                  return;
                                }
                                
                                if (tool.id === 'business-insights') {
                                  // Generate business insights dashboard
                                  setActiveTab('chat');
                                  const metrics = [];
                                  if (conversationContext.revenue) metrics.push('revenue');
                                  if (conversationContext.team_size) metrics.push('team size');
                                  if (conversationContext.hours_per_week) metrics.push('hours worked');
                                  setInputText(`Create a business insights dashboard based on the ${metrics.join(', ')} we discussed. Include trends and recommendations.`);
                                  setTimeout(() => handleSendMessage(), 100);
                                  return;
                                }
                                
                                if (tool.id === 'decision-helper') {
                                  // Analyze decisions
                                  setActiveTab('chat');
                                  setInputText('Analyze the decision we discussed. Create a structured pros/cons analysis with a recommendation based on my situation.');
                                  setTimeout(() => handleSendMessage(), 100);
                                  return;
                                }
                                
                                // Default tool handling
                                setSelectedTool(tool);
                                setShowInsightModal(true);
                                addToast(`✅ ${tool.label} tool activated!`, 'success');
                              }}
                            >
                              Use Tool
                            </button>
                          )}
                        </>
                      );
                    })()}
                    </div>
                  </div>
                );
              })}
            </div>
            </>
        )}
        </div>
        )}

        {/* Tool Results */}
        {activeToolResult && (() => {
          // Get the tool result using the generateToolResults function
          const toolResult = generateToolResults(activeToolResult, conversationData, coachingType, coachFrameworks);
          
          // Find the tool definition for additional info (check both preset and backend tools)
          const allToolsForLookup = [...availableCards, ...readyTools, ...lockedTools];
          const tool = allToolsForLookup.find(card => card.id === activeToolResult);
          
          return (
            <div className="ov-tool-result">
              <div className="ov-tool-result-header">
                <h3>{tool?.icon || '🔧'} {toolResult.title || tool?.label || activeToolResult.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</h3>
                <button className="ov-tool-result-close" onClick={() => setActiveToolResult(null)}>✕</button>
              </div>
              
              <div className="ov-tool-result-content">
                <div className="ov-tool-result-summary">
                  <p>{toolResult.summary}</p>
                </div>
                
                {toolResult.metrics && Object.keys(toolResult.metrics).length > 0 && (
                  <div className="ov-tool-result-metrics">
                    <h4>Key Metrics</h4>
                    <div className="ov-metrics-grid">
                      {Object.entries(toolResult.metrics).map(([key, value]) => (
                        <div key={key} className="ov-metric-item">
                          <span className="ov-metric-label">{key}</span>
                          <span className="ov-metric-value">{value}</span>
                        </div>
                      ))}
                    </div>
            </div>
          )}

                {toolResult.recommendations && toolResult.recommendations.length > 0 && (
                  <div className="ov-tool-result-recommendations">
                    <h4>Recommended Actions</h4>
                    <ul className="ov-recommendations-list">
                      {toolResult.recommendations.map((rec, idx) => (
                        <li key={idx} className="ov-recommendation-item">{rec}</li>
                      ))}
                    </ul>
            </div>
          )}

                {toolResult.followUp && (
                  <div className="ov-tool-result-followup">
                    <h4>Next Steps</h4>
                    <p>{toolResult.followUp}</p>
            </div>
          )}
              </div>
            </div>
          );
        })()}
        </div>
      </>
    );
  }, [contextIndicators, insightNotification, visibleCards, availableCards, businessTools, readyTools, lockedTools, conversationData, premiumIcons, activeToolResult, toolsSectionExpanded, setShowInsightModal, setSelectedTool, addToast, getFieldLabel, vertical, isDanMartell, coachFrameworks, coachingType]);

  // File Upload Handler
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    if (trialState?.isTrialUser && files.length > (trialState?.docsRemaining ?? 0)) {
              addToast(`You can only upload ${trialState?.docsRemaining ?? 0} more documents in your trial`, 'warning');
      return;
    }

    setTrialState(prev => ({ ...prev, isLoading: true }));

    for (const file of files.slice(0, Math.max(1, trialState?.docsRemaining ?? 0))) {
      try {
        if (!validateFile(file)) continue;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('company_id_slug', getBackendCompanyId(companyIdSlug));
        formData.append('terms_accepted', 'true');

        const response = await fetch(`${API_BASE}/api/v1/upload-sop`, { method: 'POST', body: formData });
        const result = await response.json().catch(() => ({}));

        if (response.ok) {
          addToast(`${file.name} uploaded successfully`, 'success');
          setTrialState(prev => ({
            ...prev,
            docsUploaded: prev.docsUploaded + 1,
            docsRemaining: Math.max(0, prev.docsRemaining - 1),
            hasUploadedDocs: true
          }));
          loadDocs();
        } else {
          addToast(`Failed to upload ${file.name}: ${result.error || 'Error'}`, 'error');
        }
      } catch (err) {
        console.error('Upload error:', err);
        addToast(`Upload failed: ${err?.message || String(err)}`, 'error');
      }
    }

    setTrialState(prev => ({ ...prev, isLoading: false }));
    if (event.target) event.target.value = '';
  };

  const validateFile = (file) => {
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];
    const maxSize = 15 * 1024 * 1024;
    if (!allowedTypes.includes(file.type)) {
      addToast(`${file.name}: Only PDF, DOCX, and TXT files are allowed`, 'error');
      return false;
    }
    if (file.size > maxSize) {
      addToast(`${file.name}: File size must be under 15MB`, 'error');
      return false;
    }
    return true;
  };

  // Trial Banner Component - defined before main render
  const TrialBanner = () => {
    if (!trialState || !trialState.isTrialUser) return null;
    return (
      <div className="ov-trial-banner">
        <div className="ov-trial-content">
          <span className="ov-trial-icon">🚀</span>
          <div className="ov-trial-text">
            <div className="ov-trial-title">Free Trial Active</div>
            <div className="ov-trial-subtitle">
                          {(trialState?.docsRemaining ?? 0) > 0
              ? `${trialState?.docsRemaining ?? 0} document uploads remaining`
                : 'Upload limit reached - upgrade to continue'}
            </div>
          </div>
          {(trialState?.docsRemaining ?? 1) === 0 && (
            <button
              className="ov-trial-upgrade-btn"
              onClick={() => window.open('https://www.opsvoice.ai/pricing', '_blank')}
            >
              Upgrade Now
            </button>
          )}
        </div>
      </div>
    );
  };

  // Analyze recent conversation to suggest relevant tools dynamically
  const analyzeConversationForInsights = (msgs, type) => {
    try {
      const lastUserTexts = (msgs || [])
        .filter(m => (m?.role || '') === 'user')
        .slice(-6)
        .map(m => String(m.content || ''));
      const combined = lastUserTexts.join(' ').trim();
      if (!combined) { setContextualSuggestions([]); return; }

      const slots = extractSlots(combined, type);
      const cfg = getCoachingConfig(type, getVerticalOverrides(vertical, companyIdSlug));
      const tools = cfg.tools || [];
      const byId = (id) => tools.find(t => t.id === id);
      const out = [];
      if (slots.hoursPerWeek && slots.revenue && byId('roi')) out.push({ id: 'roi', name: byId('roi').name, reason: 'High hours + revenue' });
      if (slots.hoursPerWeek && byId('replacement')) out.push({ id: 'replacement', name: byId('replacement').name, reason: 'Working many hours in ops' });
      if (slots.process && byId('bottleneck')) out.push({ id: 'bottleneck', name: byId('bottleneck').name, reason: 'Process/efficiency issue' });
      if (slots.sales && byId('pipeline')) out.push({ id: 'pipeline', name: byId('pipeline').name, reason: 'Sales/pipeline issue' });
      if (slots.debt && byId('debt')) out.push({ id: 'debt', name: byId('debt').name, reason: 'Debt details detected' });
      if (slots.currentWeight && byId('program')) out.push({ id: 'program', name: byId('program').name, reason: 'Weight goal detected' });
      if (slots.hvacIssue && byId('safety')) out.push({ id: 'safety', name: byId('safety').name, reason: 'HVAC issue → Safety first' });
      setContextualSuggestions(out);
    } catch (_) {
      setContextualSuggestions([]);
    }
  };

  // Real-time Business Health Thermometer Component
  const BusinessHealthThermometer = ({ score, areas, isUpdating = false, compact = false }) => {
    const [isAnimating, setIsAnimating] = useState(false);
    
    // Trigger animation when score changes
    useEffect(() => {
      if (score !== undefined) {
        setIsAnimating(true);
        const timer = setTimeout(() => setIsAnimating(false), 2000);
        return () => clearTimeout(timer);
      }
    }, [score]);
    
    const getColor = (score) => {
      if (score <= 40) return '#ef4444'; // Red
      if (score <= 70) return '#f59e0b'; // Yellow
      return '#10b981'; // Green
    };
    
    const getStatus = (score) => {
      if (score <= 40) return 'Critical';
      if (score <= 70) return 'Warning';
      return 'Healthy';
    };
    
    const getRecommendation = (score, areas) => {
      if (score <= 40) return 'Immediate action required';
      if (score <= 70) return 'Address key areas soon';
      return 'Maintain momentum';
    };
    
    const color = getColor(score);
    const status = getStatus(score);
    const recommendation = getRecommendation(score, areas);
    
    if (compact) {
      return (
        <div className={`ov-health-thermometer ov-health-compact ${isAnimating ? 'animating' : ''}`}>
          <div className="ov-thermometer-header">
            <h3 className="ov-thermometer-title">Business Health</h3>
            <div className="ov-thermometer-status" style={{ color }}>
              {status}
            </div>
          </div>
          
          <div className="ov-thermometer-display">
            <div className="ov-thermometer-circle" style={{ '--score': score, '--color': color }}>
              <svg viewBox="0 0 50 50" className="ov-thermometer-svg">
                <circle
                  cx="25"
                  cy="25"
                  r="22"
                  fill="none"
                  stroke="rgba(255,255,255,0.2)"
                  strokeWidth="4"
                />
                <circle
                  cx="25"
                  cy="25"
                  r="22"
                  fill="none"
                  stroke={color}
                  strokeWidth="4"
                  strokeDasharray={`${(score / 100) * 138.23} 138.23`}
                  strokeDashoffset="0"
                  className={`ov-thermometer-progress ${isAnimating ? 'updating' : ''}`}
                  style={{
                    transform: 'rotate(-90deg)',
                    transformOrigin: '25px 25px'
                  }}
                />
              </svg>
              <div className="ov-thermometer-score">
                <span className="ov-score-number">{score}</span>
                <span className="ov-score-max">/100</span>
              </div>
            </div>
            
            <div className="ov-thermometer-details">
              <div className="ov-thermometer-recommendation">
                {recommendation}
              </div>
              
              {areas && areas.length > 0 && (
                <div className="ov-thermometer-areas">
                  <div className="ov-areas-title">Focus:</div>
                  {areas.slice(0, 2).map((area, idx) => (
                    <div key={idx} className="ov-area-item">
                      <span className="ov-area-icon">🎯</span>
                      <span className="ov-area-name">{area}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className={`ov-health-thermometer ${isAnimating ? 'animating' : ''}`}>
        <div className="ov-thermometer-header">
          <h3 className="ov-thermometer-title">Business Health Score</h3>
          <div className="ov-thermometer-status" style={{ color }}>
            {status}
          </div>
        </div>
        
        <div className="ov-thermometer-display">
          <div className="ov-thermometer-circle" style={{ '--score': score, '--color': color }}>
            <svg viewBox="0 0 120 120" className="ov-thermometer-svg">
              <circle
                cx="60"
                cy="60"
                r="54"
                fill="none"
                stroke="rgba(255,255,255,0.2)"
                strokeWidth="8"
              />
              <circle
                cx="60"
                cy="60"
                r="54"
                fill="none"
                stroke={color}
                strokeWidth="8"
                strokeDasharray={`${(score / 100) * 339.292} 339.292`}
                strokeDashoffset="0"
                className={`ov-thermometer-progress ${isAnimating ? 'updating' : ''}`}
                style={{
                  transform: 'rotate(-90deg)',
                  transformOrigin: '60px 60px'
                }}
              />
            </svg>
            <div className="ov-thermometer-score">
              <span className="ov-score-number">{score}</span>
              <span className="ov-score-max">/100</span>
            </div>
          </div>
          
          <div className="ov-thermometer-details">
            <div className="ov-thermometer-recommendation">
              {recommendation}
            </div>
            
            {areas && areas.length > 0 && (
              <div className="ov-thermometer-areas">
                <div className="ov-areas-title">Focus Areas:</div>
                {areas.map((area, idx) => (
                  <div key={idx} className="ov-area-item">
                    <span className="ov-area-icon">🎯</span>
                    <span className="ov-area-name">{area}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {isAnimating && (
          <div className="ov-thermometer-updating">
            <div className="ov-updating-indicator"></div>
            <span>Analyzing conversation...</span>
          </div>
        )}
      </div>
    );
  };

  // Main Render
  return (
    <ErrorBoundary>
      <LoadingOverlay show={isLoading} message={loadingMessage} />
      <div
        ref={(el) => {
          keyboardRef.current = el;
          swipeGestures.ref.current = el;
        }}
        className={`ov-enterprise-root ${isMentorMind ? 'mm-premium-container' : ''} ${isMobile ? 'ov-mobile' : ''} ${isMobileFullscreen ? 'ov-mobile-fullscreen' : ''} ${mobilePerformance.shouldReduceAnimations ? 'ov-reduced-motion' : ''} ${mobileKeyboard.isKeyboardOpen ? 'ov-keyboard-open' : ''}`}
        data-tenant={config.theme}
        data-vertical={vertical}
        data-mobile={isMobile}
        data-fullscreen={isMobileFullscreen}
        data-orientation={viewport.orientation}
        data-device-type={viewport.isMobile ? 'mobile' : viewport.isTablet ? 'tablet' : 'desktop'}
        style={{
          '--ov-keyboard-height': `${mobileKeyboard.keyboardHeight}px`
        }}
      >
        <div className="ov-enterprise-container">
          {/* Sticky Header with Navigation */}
          {/* Mobile Header for PAI3/OpsIQ - Clean & Simple */}
          {isOpsMobileVertical && isMobile ? (
            <div className="ov-mobile-header">
              <button 
                className="ov-header-menu-btn"
                onClick={() => setIsMenuDrawerOpen(true)}
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setIsMenuDrawerOpen(true); }}
                onTouchStart={(e) => { e.stopPropagation(); setIsMenuDrawerOpen(true); }}
                aria-label="Open menu"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="3" y1="6" x2="21" y2="6"/>
                  <line x1="3" y1="12" x2="21" y2="12"/>
                  <line x1="3" y1="18" x2="21" y2="18"/>
                </svg>
              </button>
              
              <h1 className="ov-header-title">
                {displayTitle || config.title}
              </h1>
              
              <button 
                className="ov-header-more-btn"
                ref={(el) => {
                  if (el && isMoreMenuOpen) {
                    setSettingsMenuAnchor(el);
                  }
                }}
                onClick={(e) => {
                  const target = e.currentTarget;
                  if (isMoreMenuOpen) {
                    setIsMoreMenuOpen(false);
                    setSettingsMenuAnchor(null);
                  } else {
                    setIsMoreMenuOpen(true);
                    setSettingsMenuAnchor(target);
                  }
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const target = e.currentTarget;
                  if (isMoreMenuOpen) {
                    setIsMoreMenuOpen(false);
                    setSettingsMenuAnchor(null);
                  } else {
                    setIsMoreMenuOpen(true);
                    setSettingsMenuAnchor(target);
                  }
                }}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  const target = e.currentTarget;
                  if (isMoreMenuOpen) {
                    setIsMoreMenuOpen(false);
                    setSettingsMenuAnchor(null);
                  } else {
                    setIsMoreMenuOpen(true);
                    setSettingsMenuAnchor(target);
                  }
                }}
                aria-label="More options"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="5" r="1.5" fill="currentColor"/>
                  <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
                  <circle cx="12" cy="19" r="1.5" fill="currentColor"/>
                </svg>
              </button>
            </div>
          ) : (
          <div className="ov-enterprise-header">
            <div className="ov-enterprise-header-content">
              {/* Compact Single-Line Browser Header for OpsIQ/Business */}
              {!isMobile && (vertical === 'opsiq' || vertical === 'business') ? (
                <div className="ov-compact-header-row">
                  {/* Left: Hamburger Menu */}
                  <button 
                    className="ov-browser-header-menu-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsMenuDrawerOpen(true);
                    }}
                    onMouseDown={(e) => { 
                      e.preventDefault(); 
                      e.stopPropagation(); 
                      setIsMenuDrawerOpen(true); 
                    }}
                    onTouchStart={(e) => { 
                      e.preventDefault();
                      e.stopPropagation(); 
                      setIsMenuDrawerOpen(true); 
                    }}
                    aria-label="Open menu"
                    type="button"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="3" y1="6" x2="21" y2="6"/>
                      <line x1="3" y1="12" x2="21" y2="12"/>
                      <line x1="3" y1="18" x2="21" y2="18"/>
                    </svg>
                  </button>
                  
                  {/* Left Section: Company Name (centered within its space) */}
                  <div className="ov-compact-header-title-section">
                    <h1 className="ov-enterprise-title">{displayTitle || config.title}</h1>
                  </div>
                  
                  {/* Center: Tab Navigation */}
                  <div className="ov-compact-header-tabs">
                    <button
                      className={`ov-browser-tab ${activeTab === 'chat' ? 'ov-browser-tab-active' : ''}`}
                      onClick={() => setActiveTab('chat')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                      </svg>
                      <span>Conversation</span>
                    </button>
                    <button
                      className={`ov-browser-tab ${activeTab === 'coach' ? 'ov-browser-tab-active' : ''}`}
                      onClick={() => setActiveTab('coach')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 20h9"/>
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                      </svg>
                      <span>Quick Notes</span>
                      {coachNotes && coachNotes.length > 0 && (
                        <span className="ov-browser-tab-badge">{coachNotes.length}</span>
                      )}
                    </button>
                    <button
                      className={`ov-browser-tab ${activeTab === 'documents' ? 'ov-browser-tab-active' : ''}`}
                      onClick={() => setActiveTab('documents')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                      </svg>
                      <span>Knowledge</span>
                      {docs && docs.length > 0 && (
                        <span className="ov-browser-tab-badge">{docs.length}</span>
                      )}
                    </button>
                    <button
                      className={`ov-browser-tab ${activeTab === 'insights' ? 'ov-browser-tab-active' : ''}`}
                      onClick={() => setActiveTab('insights')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 3v18M3 12h18"/>
                        <circle cx="12" cy="12" r="2"/>
                        <path d="M7 7l2 2M7 17l2-2M17 7l-2 2M17 17l-2-2"/>
                      </svg>
                      <span>Actions</span>
                    </button>
                  </div>
                  
                  {/* Right: Three-Dots Menu */}
                  <button
                    className="ov-browser-menu-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const target = e.currentTarget;
                      if (isMoreMenuOpen) {
                        setIsMoreMenuOpen(false);
                        setSettingsMenuAnchor(null);
                      } else {
                        setIsMoreMenuOpen(true);
                        setSettingsMenuAnchor(target);
                      }
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const target = e.currentTarget;
                      if (isMoreMenuOpen) {
                        setIsMoreMenuOpen(false);
                        setSettingsMenuAnchor(null);
                      } else {
                        setIsMoreMenuOpen(true);
                        setSettingsMenuAnchor(target);
                      }
                    }}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const target = e.currentTarget;
                      if (isMoreMenuOpen) {
                        setIsMoreMenuOpen(false);
                        setSettingsMenuAnchor(null);
                      } else {
                        setIsMoreMenuOpen(true);
                        setSettingsMenuAnchor(target);
                      }
                    }}
                    aria-label="More options"
                    type="button"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="5" r="1.5" fill="currentColor"/>
                      <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
                      <circle cx="12" cy="19" r="1.5" fill="currentColor"/>
                    </svg>
                  </button>
                </div>
              ) : (
                /* Standard Header for Other Verticals */
                <div className="ov-enterprise-logo-container">
                  {(companyConfig?.ui?.logo_url || companyConfig?.logo || config.logo) && (
                    <img 
                      src={companyConfig?.ui?.logo_url || companyConfig?.logo || config.logo} 
                      alt={`${displayTitle || config.title} Logo`} 
                      className="ov-enterprise-logo"
                      onError={(e) => {
                        const container = e.target.closest('.ov-enterprise-logo-container');
                        if (container) {
                          const logoImg = container.querySelector('.ov-enterprise-logo');
                          if (logoImg) logoImg.style.display = 'none';
                        }
                      }}
                    />
                  )}
                  {!(companyConfig?.ui?.logo_url || companyConfig?.logo || config.logo) && (companyConfig?.ui?.logo_emoji || config.logoEmoji) && (
                    <span className="ov-enterprise-logo-emoji" style={{ fontSize: '32px' }}>
                      {companyConfig?.ui?.logo_emoji || config.logoEmoji}
                    </span>
                  )}
                  <div>
                    <h1 className="ov-enterprise-title">{displayTitle || config.title}</h1>
                    {/* Hide subtitle for Business/OpsIQ - company name only in header */}
                    {displaySubtitle && !(isOpsMobileVertical && isMobile) && vertical !== 'business' && vertical !== 'opsiq' ? (
                      <p className="ov-enterprise-subtitle">{displaySubtitle}</p>
                    ) : null}
                  </div>
                </div>
              )}
              {/* Mobile fullscreen toggle button */}
              {isMobile && (
                <button
                  onClick={toggleMobileFullscreen}
                  className="ov-mobile-fullscreen-toggle"
                  aria-label={isMobileFullscreen ? "Minimize" : "Expand fullscreen"}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '8px',
                    cursor: 'pointer',
                    fontSize: '24px',
                    color: 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {isMobileFullscreen ? '✕' : '⛶'}
                </button>
              )}
            </div>
            {/* Hide EnterpriseNavigation for OpsIQ/Business/MentorMind */}
            {/* OpsIQ/Business use browser tab nav in header; MentorMind uses its own 4-tab system */}
            {vertical !== 'opsiq' && vertical !== 'business' && !isMentorMind && (
              <EnterpriseNavigation />
            )}
            {/* MentorMind desktop navigation - matches mobile tab system */}
            {isMentorMind && !isMobile && (
              <nav className="ov-enterprise-nav">
                {[
                  { id: 'home', label: 'Home', icon: '🏠' },
                  { id: 'sessions', label: 'Sessions', icon: '💬' },
                  { id: 'progress', label: 'Progress', icon: '📈' },
                  { id: 'insights', label: 'Insights', icon: '💡' }
                ].map(tab => (
                  <button
                    key={tab.id}
                    className={`ov-nav-btn ${activeTab === tab.id ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                    aria-label={tab.label}
                  >
                    <span className="ov-nav-icon">{tab.icon}</span>
                    <span className="ov-nav-label">{tab.label}</span>
                  </button>
                ))}
              </nav>
            )}
            {/* Browser Tab Navigation for OpsIQ/Business - NOW INTEGRATED INTO HEADER ABOVE */}
          </div>
          )}

          {/* Network Status Banner - Shows when offline */}
          <NetworkStatus
            isOnline={offlineManager.isOnline}
            isReconnecting={offlineManager.isReconnecting}
            queuedCount={offlineManager.queuedCount}
          />

          {/* Docs/Spreadsheets Data Source Toggle - Only show when on Documents tab */}
          {spreadsheetsEnabled && activeTab === 'documents' && (
            <div style={{
              display: 'flex',
              gap: '8px',
              padding: '12px 16px',
              borderBottom: '1px solid #eee',
              backgroundColor: '#f9f9f9',
            }}>
              <button
                onClick={() => setDataSource('docs')}
                style={{
                  flex: 1,
                  padding: '8px',
                  backgroundColor: dataSource === 'docs' ? '#4ECDC4' : 'transparent',
                  color: dataSource === 'docs' ? 'white' : '#666',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                📄 Docs
              </button>
              <button
                onClick={() => setDataSource('spreadsheets')}
                style={{
                  flex: 1,
                  padding: '8px',
                  backgroundColor: dataSource === 'spreadsheets' ? '#4ECDC4' : 'transparent',
                  color: dataSource === 'spreadsheets' ? 'white' : '#666',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                📊 Spreadsheets
              </button>
            </div>
          )}

          {/* Coach Credentials */}
          {config.coachName && (
            <div className="coach-credentials">
              <div className="coach-photo">
                <img 
                  src={config.coachPhoto || '/api/placeholder/80/80'} 
                  alt={config.coachName} 
                  className="coach-photo-img"
                />
              </div>
              <div className="coach-bio">
                <h3 className="coach-name">{config.coachName}</h3>
                <p className="coach-credentials-text">{config.coachCredentials || 'Certified Business Coach & Entrepreneur'}</p>
                <p className="coach-achievements">{config.coachAchievements || 'Helped 1000+ entrepreneurs scale their businesses'}</p>
              </div>
            </div>
          )}

          {/* TrialBanner - Can be shown conditionally */}
          {trialState?.isTrialUser && <TrialBanner />}

          {/* Conversation Intelligence Panel - Removed to reduce clutter */}

          {/* Contextual Suggestions */}
          {Array.isArray(contextualSuggestions) && contextualSuggestions.length > 0 && (
            <div className="ov-context-suggestions">
              <div className="ov-context-title">Suggestions for you</div>
              <div className="ov-context-cards">
                {contextualSuggestions.map(cs => (
                  <button key={cs.id} className="ov-suggestion-chip" onClick={() => { setActiveTab('insights'); }}>
                    ➡ Run {cs.name || cs.id}
                  </button>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="ov-enterprise-alert ov-alert-error">
              <span className="ov-alert-icon">⚠️</span>
              <span>{error}</span>
              <button className="ov-alert-action" onClick={() => setError(null)}>Dismiss</button>
              </div>
            )}

          {sessionMetrics.timeSaved >= 10 && (
            <div className="ov-enterprise-alert ov-alert-success">
              <span className="ov-alert-icon">⚡</span>
              <span>Excellent! You've saved {sessionMetrics.timeSaved} minutes this session</span>
          </div>
        )}
        
          <div className={isMentorMind ? "mm-view-wrapper" : "ov-enterprise-card"}>
            {/* Main content area - Toggle removed, now in header */}
            {spreadsheetsEnabled && dataSource === 'spreadsheets' ? (
              <SpreadsheetPanel
                companyIdSlug={companyIdSlug}
                sessionId={sessionId}
              />
            ) : (
              <>
                {/* New MentorMind Views - Premium Redesign */}
                {isMentorMind && activeTab === 'home' && (
                  <HomeViewPremium
                    companyIdSlug={companyIdSlug}
                    vertical={vertical}
                    companyConfig={companyConfig}
                    onStartVoiceSession={handleStartVoiceSession}
                    onStartTextSession={handleStartTextSession}
                    onStartWithFramework={handleStartWithFramework}
                    onResumeSession={handleResumeSession}
                  />
                )}
                {isMentorMind && activeTab === 'sessions' && (
                  activeSessionId ? (
                    renderChatTab()
                  ) : (
                    <SessionsViewPremium
                      companyIdSlug={companyIdSlug}
                      activeSessionId={activeSessionId}
                      onResumeSession={handleResumeSession}
                      onStartNewSession={() => setActiveTab('home')}
                    />
                  )
                )}
                {isMentorMind && activeTab === 'progress' && (
                  <ProgressViewPremium
                    companyIdSlug={companyIdSlug}
                    conversationContext={conversationContext}
                    capturedInsights={capturedInsights}
                    savedNotes={coachNotes}
                  />
                )}
                {isMentorMind && activeTab === 'insights' && (
                  <InsightsView
                    companyIdSlug={companyIdSlug}
                    capturedInsights={capturedInsights}
                    savedNotes={coachNotes}
                  />
                )}
                
                {/* Legacy views for non-MentorMind verticals */}
                {!isMentorMind && (activeTab === 'voice' || activeTab === 'chat') && renderChatTab()}
                {!isMentorMind && activeTab === 'coach' && renderCoachTab()}
                {!isMentorMind && activeTab === 'documents' && renderDocsTab()}
                {!isMentorMind && activeTab === 'insights' && (
                  <ErrorBoundary fallback={<div className="ov-error-fallback">Tools temporarily unavailable. Please refresh the page.</div>}>
                    {renderInsightsTab()}
                  </ErrorBoundary>
                )}
                {!isMentorMind && activeTab === 'calculators' && vertical === 'hvac' && (
                  <div className="ov-calculators-container">
                    <HVACCalculators
                      onCalculation={(calcData) => {
                        // Could track calculator usage here
                      }}
                    />
                  </div>
                )}
                
                {/* Fallback for invalid tabs - redirect to appropriate default */}
                {((isMentorMind && !['home', 'sessions', 'progress', 'insights'].includes(activeTab)) ||
                  (!isMentorMind && !['chat', 'voice', 'coach', 'documents', 'insights', 'calculators'].includes(activeTab))) && (
                  <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <p>Loading...</p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Framework Picker Modal */}
          {isMentorMind && (
            <FrameworkPickerModal
              isOpen={showFrameworkPicker}
              onClose={() => setShowFrameworkPicker(false)}
              companyIdSlug={companyIdSlug}
              onFrameworkSelect={handleFrameworkSelect}
            />
          )}

          {/* Sticky Input Form at Bottom - Show only on chat/voice tab (or MentorMind sessions tab) */}
          {((!isMentorMind && (activeTab === 'chat' || activeTab === 'voice')) || (isMentorMind && activeTab === 'sessions' && activeSessionId)) && (
            <div className="ov-enterprise-input-form">
              {/* OpsIQ: Floating Action Pills - Action Items & Decision Analysis (primary actions) */}
              {(vertical === 'opsiq' || vertical === 'business') && messages.length > 0 && (
                <div className="ov-action-pills" style={{ display: 'flex', gap: '0.5rem', padding: '0 1rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="ov-action-pill"
                    onClick={() => {
                      setActiveTab('chat');
                      setInputText('Extract all action items, tasks, and todos from our conversation. Format as a checklist with owners and due dates if mentioned.');
                      setTimeout(() => {
                        inputRef.current?.focus();
                        handleSendMessage();
                      }, 100);
                    }}
                    style={{
                      background: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '1rem',
                      padding: '0.5rem 0.875rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: '#111827',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.375rem',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#3B82F6';
                      e.currentTarget.style.color = 'white';
                      e.currentTarget.style.borderColor = '#3B82F6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#F9FAFB';
                      e.currentTarget.style.color = '#111827';
                      e.currentTarget.style.borderColor = '#E5E7EB';
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Action Items
                  </button>
                  <button
                    type="button"
                    className="ov-action-pill"
                    onClick={() => {
                      setActiveTab('chat');
                      setInputText('Analyze the decision we discussed. Create a structured pros/cons analysis with a recommendation based on my situation.');
                      setTimeout(() => {
                        inputRef.current?.focus();
                        handleSendMessage();
                      }, 100);
                    }}
                    style={{
                      background: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '1rem',
                      padding: '0.5rem 0.875rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: '#111827',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.375rem',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#8B5CF6';
                      e.currentTarget.style.color = 'white';
                      e.currentTarget.style.borderColor = '#8B5CF6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#F9FAFB';
                      e.currentTarget.style.color = '#111827';
                      e.currentTarget.style.borderColor = '#E5E7EB';
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                      <path d="M12 3v18M3 12h18"/>
                      <circle cx="12" cy="12" r="2"/>
                      <path d="M7 7l2 2M7 17l2-2M17 7l-2 2M17 17l-2-2"/>
                    </svg>
                    Decision Analysis
                  </button>
                </div>
              )}
              
              {/* OpsIQ + PAI3: Quick action chips row (contextual, when messages exist) */}
              {isOpsMobileVertical && isMobile && (messages?.length || 0) > 0 && (
                <div className="ov-composer-chips" role="region" aria-label="Shortcuts">
                  <button
                    type="button"
                    className="ov-chip"
                    title="Summarize the latest assistant answer"
                    onClick={() => {
                      setInputText('Summarize the most important points from our latest conversation.');
                      setTimeout(() => inputRef.current?.focus(), 0);
                    }}
                  >
                    Summary
                  </button>
                  <button
                    type="button"
                    className="ov-chip"
                    title="Turn the latest answer into a checklist"
                    onClick={() => {
                      setInputText('Turn the last answer into an operational checklist with owners and due dates if possible.');
                      setTimeout(() => inputRef.current?.focus(), 0);
                    }}
                  >
                    To Checklist
                  </button>
                  <button
                    type="button"
                    className="ov-chip"
                    title="Search your SOP/Knowledge base"
                    onClick={() => {
                      setInputText('Find the relevant SOP for: ');
                      setTimeout(() => inputRef.current?.focus(), 0);
                    }}
                  >
                    Find SOP
                  </button>
                </div>
              )}
              <div className="ov-enterprise-input-wrapper">
                {/* Voice-first: Mic button always visible */}
                <button
                  className={`ov-enterprise-mic-btn ${isRecording ? 'recording' : ''}`}
                  onClick={handleStartRecording}
                  disabled={isLoading || isRecording}
                  aria-label="Start voice recording"
                  title="Tap to speak"
                >
                  {isRecording ? (
                    <div className="ov-mic-pulse-ring"></div>
                  ) : null}
                  <svg className="ov-mic-icon-small" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C10.9 2 10 2.9 10 4V12C10 13.1 10.9 14 12 14C13.1 14 14 13.1 14 12V4C14 2.9 13.1 2 12 2Z" fill="currentColor"/>
                    <path d="M19 10V12C19 15.9 15.9 19 12 19C8.1 19 5 15.9 5 12V10H3V12C3 16.4 6.6 20 11 20V22H13V20C17.4 20 21 16.4 21 12V10H19Z" fill="currentColor"/>
                  </svg>
                </button>
                
                <input 
                  ref={inputRef}
                  type="text" 
                  className="ov-enterprise-input prominent"
                  placeholder={getPlaceholderText()}
                  value={inputText}
                  onChange={(e) => {
                    setInputText(e.target.value);
                    debouncedExtract(e.target.value);
                  }}
                  disabled={isLoading}
                  aria-label="Type your message"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (inputText.trim() && !isLoading) {
                        handleSendMessage();
                      }
                    }
                  }}
                />
                
                <button
                  className="ov-enterprise-send-btn large"
                  onClick={handleSendMessage}
                  disabled={!inputText.trim() || isLoading}
                  aria-label="Send message"
                >
                  {isLoading ? (
                    <div className="ov-send-spinner"></div>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="ov-send-icon">
                      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="currentColor"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Tool Execution Modal */}
          {showInsightModal && selectedTool && (
            <div className="ov-modal-overlay" onClick={() => setShowInsightModal(false)}>
              <div className="ov-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="ov-modal-header">
                  <h3 className="ov-modal-title">
                    {selectedTool.icon} {selectedTool.label}
                  </h3>
                  <button 
                    className="ov-modal-close"
                    onClick={() => setShowInsightModal(false)}
                  >
                    ×
                  </button>
                </div>
                <div className="ov-modal-body">
                  <div className="ov-modal-description">
                    {selectedTool.description || selectedTool.name}
                  </div>
                  {toolExecuting ? (
                    <div className="ov-tool-loading">
                      <div className="ov-spinner"></div>
                      <p>Loading tool...</p>
                              </div>
                  ) : toolExecutionResult ? (
                    <ToolResultRenderer
                      tool={selectedTool}
                      toolResult={toolExecutionResult}
                      conversationData={conversationData}
                      onFieldUpdate={(fieldName, value) => {
                        // Re-execute tool when field updates
                        executeToolAsync(selectedTool, { ...conversationData, [fieldName]: value });
                      }}
                    />
                  ) : (
                    <div className="ov-tool-placeholder">Tool ready to use</div>
                        )}
                </div>
                <div className="ov-modal-footer">
                  <button 
                    className="ov-modal-btn-secondary"
                    onClick={() => setShowInsightModal(false)}
                  >
                    Close
                  </button>
                  <button 
                    className="ov-modal-btn-primary"
                    onClick={() => {
                      setActiveTab('insights');
                      setShowInsightModal(false);
                    }}
                  >
                    View All Tools
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Footer - OpsIQ shows "Powered by OpsIQ", others show disclaimer */}
          {(vertical === 'opsiq' || vertical === 'business') ? (
            <div className="ov-enterprise-footer" style={{ textAlign: 'center', padding: '0.75rem 1rem', fontSize: '0.75rem', color: '#6B7280', borderTop: '1px solid #F3F4F6', flexShrink: 0 }}>
              Powered by <span className="ov-footer-logo" style={{ display: 'inline-block', marginLeft: '0.25rem', fontWeight: 600, color: '#14B8A6' }}>OpsIQ</span>
            </div>
          ) : (
            <div className="ov-enterprise-disclaimer">
              {config.disclaimer}
            </div>
          )}
        </div>
        
        {/* Mobile Navigation */}
        {isMobile && (
          <MobileNavigation
            tabs={mobileTabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            compact={true}
            getScrollElement={() => {
              // Use the actual scroll container (messages) so auto-hide works like ChatGPT.
              try {
                return document.querySelector('.ov-enterprise-messages');
              } catch (_) {
                return null;
              }
            }}
          />
        )}
        
        {/* Privacy and disclaimer components removed to clean up UI */}
        
        {/* Framework Completion Modal - Beautiful celebration when framework is complete */}
        <FrameworkCompletionModal
          isOpen={showFrameworkCompletionModal}
          onClose={() => {
            setShowFrameworkCompletionModal(false);
            setFrameworkCompletionData(null);
            setViewingCompletedFramework(null);
            setFrameworkExecutionActive(false);
            setFrameworkExecutionData(null);
          }}
          frameworkName={
            viewingCompletedFramework?.frameworkName ||
            frameworkCompletionData?.frameworkName ||
            'Framework'
          }
          completionData={
            viewingCompletedFramework
              ? (viewingCompletedFramework.completionData || viewingCompletedFramework.summary || {})
              : {
                  ...frameworkCompletionData?.completionSummary,
                  collected: frameworkCompletionData?.collected || {},
                  context: frameworkCompletionData?.context || {}
                }
          }
          conversationContext={
            viewingCompletedFramework?.context || {
              ...conversationContext,
              ...(frameworkCompletionData?.collected || {}),
              ...(frameworkCompletionData?.context || {})
            }
          }
          isViewingMode={!!viewingCompletedFramework}
          onSave={() => {
            // Save to localStorage for persistence
            const savedFrameworks = JSON.parse(localStorage.getItem('completedFrameworks') || '[]');

            // Save the complete frameworkCompletionData object so we can restore it exactly
            const completionRecord = {
              frameworkName: frameworkCompletionData?.frameworkName,
              completedAt: new Date().toISOString(),
              // Save the entire completion data (includes isV2, summaryCard, or completionSummary/collected)
              completionData: frameworkCompletionData,
              // For v1 frameworks, save the collected context (not raw conversationContext)
              context: frameworkCompletionData?.collected ||
                       frameworkCompletionData?.context ||
                       conversationContext
            };
            savedFrameworks.push(completionRecord);
            localStorage.setItem('completedFrameworks', JSON.stringify(savedFrameworks));
            
            // Also add to Coach Notes so it's visible in the Notes tab!
            const frameworkNote = {
              id: `framework-${Date.now()}`,
              type: 'goal', // Use 'goal' type for completed frameworks
              content: `✅ Completed: ${frameworkCompletionData?.frameworkName}\n\n` +
                `📊 Key Insights:\n` +
                `• Customer: ${conversationContext?.customer_type || conversationContext?.target_customer || 'Business coaches'}\n` +
                `• Price: ${conversationContext?.price_point || conversationContext?.deal_size || '$200/mo'}\n` +
                `• Pain Point: ${conversationContext?.pain_point || 'Scaling challenges'}\n\n` +
                `📅 Completed: ${new Date().toLocaleDateString()}`,
              timestamp: Date.now(),
              relatedMessage: null
            };
            setCoachNotes(prev => [frameworkNote, ...prev]);
            
            // Show success toast
            addToast('🎉 Framework saved to your Notes!', 'success');
            
            // Close modal
            setShowFrameworkCompletionModal(false);
            setFrameworkCompletionData(null);
            
            // Switch to Notes tab so they can see it
            setTimeout(() => {
              setActiveTab('coach');
            }, 500);
          }}
          onExport={() => {
            // Future: Generate PDF export
          }}
        />

        {/* Quick Actions FAB - Temporarily Hidden (Pending proper vertical-specific config) */}
        {false && isMobile && (
          <QuickActions
            vertical={vertical}
            isMobile={isMobile}
            onActionSelect={(action) => {
              // Switch to chat tab and fill prompt
              setActiveTab('chat');
              setUserMessage(action.prompt);
              if (!action.requiresInput) {
                // Auto-send if doesn't require input
                setTimeout(() => {
                  handleSendMessage();
                }, 300);
              }
            }}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};

const playCelebrationTone = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    const now = ctx.currentTime;

    oscillator.type = 'triangle';
    oscillator.frequency.setValueAtTime(880, now);
    oscillator.frequency.linearRampToValueAtTime(1320, now + 0.2);

    gain.gain.setValueAtTime(0.18, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

    oscillator.connect(gain);
    gain.connect(ctx.destination);

    oscillator.start(now);
    oscillator.stop(now + 0.25);
  } catch (error) {
    console.debug('[CelebrationTone] Unable to play tone:', error);
  }
};

const launchConfetti = () => {
  if (typeof document === 'undefined') return;
  const container = document.createElement('div');
  container.className = 'ov-confetti-container';
  for (let i = 0; i < 24; i += 1) {
    const piece = document.createElement('span');
    piece.className = 'ov-confetti-piece';
    piece.style.setProperty('--ov-confetti-left', `${Math.random() * 100}%`);
    piece.style.setProperty('--ov-confetti-delay', `${Math.random()}s`);
    piece.style.setProperty('--ov-confetti-hue', `${Math.floor(Math.random() * 360)}`);
    container.appendChild(piece);
  }
  document.body.appendChild(container);
  window.setTimeout(() => {
    container.remove();
  }, 1500);
};

export default OpsVoiceWidget;
